/**
 * ONE TAP — arena mesh construction.
 *
 * `buildArenaMesh(arena)` turns the shared collision data into the place the match
 * happens in. The brushes are the skeleton; everything else — roof decks, parapets,
 * signage, landmarks, the city — is generated here from `props.ts` and `decals.ts`.
 *
 * ## THE ARENA IS A ROOFTOP (DESIGN.md 4)
 *
 * This is a premise, not a grade. The previous build was an enclosed multi-deck interior
 * with a grey ceiling, and it failed the brief while *measuring* correct on every lighting
 * pass — because the building was wrong, and no amount of colour grading fixes a building.
 * Everything below follows from the arena being a sunlit roof in a white city:
 *
 *  - **`openToSky` is the pivot.** Every deck is asked, from the geometry itself, whether
 *    anything is above it. A deck with sky over it becomes a ROOF: roofing membrane or
 *    gravel ballast, a parapet with a steel handrail, rooflights let into the deck, vent
 *    curbs, drains, a galvanised service walkway and a painted roof number. A deck with a
 *    slab over it stays an interior floor. Derived rather than authored, so this survives
 *    the arena being rebuilt underneath it.
 *  - **The city is built, not implied.** `buildSkyline` places 214 unlit boxes — a swept
 *    ring of 44 neighbouring roofs and 170 hazed towers behind it — in three
 *    tiers of aerial perspective, and seats the near tier *below* the play space so a
 *    player looking out over a parapet looks DOWN onto other people's roofs — parapets,
 *    plant rooms, water tanks. That is the cheapest and most direct statement of altitude
 *    available, and it is most of the feeling.
 *  - **Everything on a roof is ankle height or see-through.** A roof is a low landscape
 *    anyway, which is lucky, because this file only skins collision brushes: a solid
 *    metre-high parapet would be chest-high cover a bullet passes through. So the parapet
 *    is a 30 cm upstand (under `STEP_HEIGHT`) with a skeletal rail on top of it, and the
 *    tall rooftop objects — tanks, plant rooms — live on the surrounding city where they
 *    cost nothing. See `P.PARAPET_HEIGHT`.
 *
 * ## The colour system (DESIGN.md 4)
 *
 * The city is white. Roughly six surfaces in seven are a tone of `base`/`base2`/`shadow`/
 * `ink`, and they are told apart by **value** — bright coping, white wall, grey membrane,
 * pale ballast, slate reveal — not by hue. That value ladder is what makes a space read as
 * a space instead of as fog, and it is free: it never competes with anything the player
 * has to react to.
 *
 * ### Two saturated hues, and both of them are a team.
 *
 * `PALETTE.teamA` (azure) and `PALETTE.teamB` (signal red), and nothing else. Everything
 * in the world a team does not own is clamped to `WORLD_MAX_SATURATION` — 8% — by
 * `zoneTone`. Saturated amber does not appear in the arena *at all*: it is the colour of a
 * chambered gun and of nothing else. The one exception is `warn`, a matte safety band on
 * the grapple davits, rationed to a few square metres in the whole arena and documented
 * where it is made.
 *
 * The team hues are spent in a very particular way, and it is a **gameplay** decision
 * rather than a compositional one. Team *walls* are pale tinted plaster in the wall value
 * band; the full-chroma colour is reserved for a 30 cm skirting band at the wall/deck
 * junction, for banner head and foot bands, for the low parapet round the back of a spawn
 * and for a pennant at the gun pad. A hundred square metres of signal red is a wall a red
 * player cannot be seen against — see `TEAM_WALL_SATURATION`, which carries the
 * measurements.
 *
 * **And none of it is on the floor.** The previous build's most saturated element by a
 * wide margin was a pair of enormous glowing blue arcs painted on the deck at each spawn,
 * with neutral grey architecture around them. DESIGN.md 4 names that exact failure. The
 * arcs are gone; a spawn is now a painted outline, a low parapet in the team's colour and
 * a banner on a mast. See `buildTeamZone`.
 *
 * ### Storeys are named by value, not by hue
 *
 *  - **Regions are storeys.** The deck levels are found from the geometry itself and named
 *    bottom-to-top. In an arena whose premise is verticality, altitude is the thing worth
 *    coding, and a player who can see a plane knows what floor they are looking at.
 *  - **`zoneA` is a dark plane, `zoneB` is a bright one.** Both are chroma-capped tints of
 *    the old accent hues, so the deck still has an identity, but it lands as a step on the
 *    value ladder rather than as a competing colour. That is a double win: it is the same
 *    channel the rest of the look already reads in, and a five-storey atrium separated by
 *    value has *more* form than one separated by hue, not less.
 *  - **Paint arrives as architecture, never as trim, and it is *counted*.** A plane lands
 *    only on a brush with a genuinely monumental exposed face, and only on the biggest few:
 *    `chooseAccentBrushes` ranks candidates and stops at four masses per zone and two per
 *    team. Painted piping, coloured ledge rims, tinted ramp cheeks, hued wall-run chevrons
 *    and glowing lip strips are what turned this arena into a rainbow; all of them are gone.
 *  - **NOTHING IN THE ARENA IS EMISSIVE.** Not one material. The last two were the spawn
 *    and gun-pad rings, and outdoors they were both invisible *and* the most saturated
 *    thing in the frame — the worst possible trade. The spawn fill light, the gun pad's
 *    light shaft and the anchors' jade glow went with them. A chambered gun is now the only
 *    thing in the world that burns, which is what DESIGN.md 4 calls the single most
 *    important readability rule in the project.
 *
 * ## The signage kit (critique 4)
 *
 * `decals.ts` bakes eight marks and no more, and this file paints every non-team one of
 * them in `PALETTE.ink` at `P.SIGNAGE_STRENGTH` — **one weight, one value**. Marks are
 * sized from `SIGN_TIERS`, three discrete widths chosen by how big the plane is, so a
 * decal is never at an arbitrary scale; and every one is centred on its face or spaced at
 * a fixed metre pitch, so it is aligned to the architecture rather than dropped at a
 * random point on it. A face carries at most one mark. If a mark does not answer a
 * wayfinding question it is not in the kit.
 *
 * ## Three rules this file will not break
 *
 * 1. **Visual honesty.** Nothing occludes a sightline that collision does not also
 *    occlude, and nothing that looks like cover is empty air. Skins fill collision
 *    brushes exactly; surface relief is capped at centimetres; airborne clutter is
 *    only placed where a heightfield check proves it is far above every walkable
 *    surface. Brushes flagged `decorative` — the ones collision *skips* — are drawn
 *    opaque only when `plateMayBeOpaque` can prove they hide nothing: either the plate is
 *    a kerb too low to conceal a crouched head or a resting gun, or its whole footprint
 *    is backed by collision geometry. Everything else becomes open pipework, grating,
 *    glass or a framed translucent scrim. On this arena that leaves exactly nine opaque
 *    decorative volumes, eight of them 14 cm deck kerbs — every hung banner, every 120 m
 *    service run and every roof truss is see-through, because none of them is cover.
 *
 * 2. **Draw calls.** Every material is one merged mesh for the whole arena. Hundreds
 *    of brushes cost ~25 opaque draws, ~11 outline draws, 2 decal draws and a handful
 *    of specials — comfortably under the 150 budget with room for players and VFX.
 *
 *    **The 2.05 m body.** The player got 14% taller (`PLAYER_HEIGHT` 1.8 -> 2.05), so
 *    everything in here keyed to human scale was re-checked against it rather than
 *    assumed. Guard rails are 1.25 m (`RAILING_HEIGHT`, hip height on the new body,
 *    shoulder height on a crouched one); wall-run chevrons are at 1.9 m, still eye level
 *    (`PLAYER_EYE_HEIGHT` 1.85); planting beds are 0.22 m and roof parapets 0.30 m, both
 *    below `STEP_HEIGHT` (0.45 m); vent curbs top out at `P.ROOF_CURB_MAX` (0.40 m) and
 *    walkway grating sits 0.08 m proud. Every one of those was checked against the 2.05 m
 *    body rather than inherited: waist-high has to actually be waist-high, and ankle-high
 *    has to be low enough that the simulation would not notice it.
 *    **No cover height changed**, and none can: cover is collision geometry, this file
 *    only skins it, and every piece of applied detail here is capped at `SURFACE_RELIEF`
 *    (5 cm) or is see-through. Chest-high cover that forces a body shot instead of a
 *    headshot is a collision-brush primitive and it is exactly as tall as it was.
 *
 * 3. **Determinism.** All variation comes from `makeRng(hashString(arena.name))`.
 *    Never `Math.random()`: every client must build the same world, because callouts
 *    like "graffiti wall" have to mean the same thing to both teams.
 *
 * Client-only. `src/shared` never imports this.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

import type { ArenaData, Brush, Ramp } from '@shared/types';
import { SurfaceKind } from '@shared/types';
import type { Vec3 } from '@shared/math';
import { DEG2RAD, clamp, hashString, makeRng } from '@shared/math';
import { FOV_BASE, OUTLINE_THICKNESS, PALETTE, TEAM_COLORS, TEAM_NAMES, TOON_BANDS } from '@shared/constants';

import { makeToonMaterial, setToonSpecular } from '@client/render/toon';
import {
  DecalBuilder,
  buildDecalAtlas,
  makeDecalGlowMaterial,
  makeDecalMaterial,
  type DecalAtlas,
  type DecalKind,
  type DecalPlacement,
} from './decals';
import * as P from './props';
import type { BuildCtx, MatKey, SkinOpts } from './props';

// ---------------------------------------------------------------------------
// Art tuning
// ---------------------------------------------------------------------------

/**
 * Ink weight, derived the same way toon.ts derives it: OUTLINE_THICKNESS is a hull
 * offset in metres at OUTLINE_REF_DISTANCE, converted to CSS pixels against a 1080p /
 * FOV_BASE reference. Deriving it rather than guessing keeps arena ink and character ink
 * the same weight.
 */
const OUTLINE_REF_DISTANCE = 5.0;
const OUTLINE_PIXELS =
  (OUTLINE_THICKNESS / OUTLINE_REF_DISTANCE) * (1 / Math.tan(FOV_BASE * 0.5 * DEG2RAD)) * (1080 * 0.5);

/** Coarse height/occupancy grid resolution, metres. */
const GRID_CELL = 1.25;
/** A surface has to sit this far above everything under it before airborne clutter is allowed. */
const OVERHEAD_CLEARANCE = 3.2;
/** A ledge with at least this much drop beyond it gets hazard striping. */
const LEDGE_DROP = 2.6;
/** Minimum top-face area before a brush is treated as a platform worth trimming. */
const PLATFORM_MIN_AREA = 1.2;
/** Minimum top-face area before a platform gets a laid floor rather than a bare shell. */
const FLOOR_MIN_AREA = 3.5;
/** Distance a spawn marking searches for the floor beneath it. */
const SPAWN_FLOOR_PROBE = 4.0;

// -- Deck detection ---------------------------------------------------------

/** Bin width when histogramming brush tops to find the deck levels. */
const DECK_BIN = 1.5;
/** Two decks closer than this are the same deck with a step in it. */
const DECK_MIN_GAP = 6.0;
/** A top face smaller than this is furniture, not a storey. */
const DECK_MIN_AREA = 6.0;
/** Never name more storeys than there are names for. */
const DECK_MAX = 5;
/** A brush belongs to the highest deck at or below its base, allowing for a kerb. */
const DECK_SNAP_UP = 1.2;

// -- Colour budget ----------------------------------------------------------

/**
 * Hard ceiling on saturation for every surface in the world that a team does not own.
 *
 * 8% is low enough that a plane reads as a *tinted neutral* — it sits on the value ladder
 * with the concrete and the terrazzo instead of arguing with it — while still being
 * distinguishable side by side, which is all a storey plane has to do. Above this the
 * plane starts competing with the two team colours and with the gun's amber, and the frame
 * loses its hierarchy: the previous build ran six hues at full chroma and, as a direct
 * consequence, the most important object in the game did not read.
 */
const WORLD_MAX_SATURATION = 0.08;
/**
 * The two storey planes, as lightness targets.
 *
 * They are told apart by **value**, and both sit *below* the architecture, because the
 * architecture is white and nothing can be brighter than it. `zoneA` is a dark cool plane
 * and `zoneB` a mid warm one, so the full ladder runs
 *
 *   base .98 → base2 .92 → floor .88 → floorDark .83 → **zoneB .68** → **zoneA .40** → ink .21
 *
 * resolving to #b4b1a7 and #5e6e69 — a warm putty and a dark slate green, both at 8%
 * saturation where the hues they came from were at 100% and 82%. Each zone lands in a gap
 * nothing else occupies, which is the point. A plane the same value as the wall
 * behind it names nothing at all — that is complaint #1 restated at the scale of one
 * surface, and it is why the obvious "make the bright storey bright" choice is wrong here.
 */
const ZONE_A_LIGHTNESS = 0.4;
const ZONE_B_LIGHTNESS = 0.68;
/** Curtain-wall glazing, chroma-capped from the sky tone. Bright, because it is glass. */
const GLASS_LIGHTNESS = 0.86;

/**
 * TEAM WALLS ARE PALE TINTED PLASTER. This is a gameplay fix, not a taste change.
 *
 * Measured on the previous build: a signal-red team wall came back at L 81.8 with the deck
 * in front of it at L 79.2 — two and a half points apart. Two consequences, both bad:
 *
 *  1. In greyscale the wall and the floor are the same mass, so the corner between them
 *     disappears and the room loses its form exactly where a player is trying to read it.
 *  2. **A player standing against that wall is nearly invisible.** Both team silhouettes
 *     are painted in the team colours, so a red player in front of a red wall at the same
 *     value has neither a hue edge nor a value edge to be found by. That is not an art
 *     problem, it is a "you got shot by someone you could not see" problem.
 *
 * So the paint moves to where paint on a real building lives: chroma down to
 * `TEAM_WALL_SATURATION`, value up into the wall band at `TEAM_WALL_LIGHTNESS`. A pale
 * azure wall and a pale red wall still name the two ends of the deck instantly — that is
 * all a wall has to do — while sitting a long way above the floor in value and a long way
 * below a full-chroma player silhouette in chroma.
 *
 * The full-chroma team colour is not lost, it is *concentrated*: a 30 cm skirting band
 * where the wall meets the deck, and the spawn pad. A narrow band of saturated colour at
 * the foot of a pale wall reads as a stronger team claim than the whole wall did, because
 * it is the only saturated thing near it.
 *
 * Delivered, measured the same way the review measured the old build (CIE L*, C*):
 *
 *     teamA wall  #dbe3eb   L* 89.9   C* 5.0
 *     teamB wall  #ebdbdb   L* 88.7   C* 5.8
 *     deck        #e4e6eb   L* 70.9   (toon.ts sits every upward face a rung lower)
 *
 * — against 81.8 / 79.2 before. Nineteen points of separation instead of two and a half,
 * and both walls sit inside the band asked for while carrying the most chroma that band
 * allows. The saturation ceiling is the binding constraint here, not the lightness: at
 * L* ~89 there is simply not much room left in the gamut, which is precisely why the
 * saturated statement has to be made by a band and not by the wall.
 */
const TEAM_WALL_SATURATION = 0.3;
const TEAM_WALL_LIGHTNESS = 0.89;
/** Height of the full-chroma skirting band at the foot of a team wall. */
const TEAM_BAND_HEIGHT = 0.3;

/**
 * Pale wood: handrail caps and nothing else.
 *
 * Wood is on DESIGN.md's material list and it is the one warm surface in an otherwise cool
 * building, which is worth a great deal — an all-white arena under a blue sky grades cold,
 * and a warm note at hand height is what makes it read as inhabited. Derived from the amber
 * hue but at a quarter of its chroma and matte, so it can never be confused with the one
 * thing amber is allowed to mean: a rail cap is a 6 cm matte stick that does not glow, and
 * a chambered gun is an emissive that blooms.
 */
const WOOD_SATURATION = 0.22;
const WOOD_LIGHTNESS = 0.72;

/**
 * Planting: a real green, in two values.
 *
 * The only soft silhouette in a building made of straight lines, which is most of what it is
 * here for. It used to be authored at 26% saturation and 30% lightness — a mass so dark and
 * so grey that it read as a shadow with a leaf shape, which is exactly the "muted" the
 * playtest saw. Greenery on a bright day is *bright*: a clipped hedge in full sun is one of
 * the more saturated things in a real roofscape, and it is a hue no team owns, so it costs
 * the gameplay budget nothing to let it be one.
 *
 * Two values because a toon ramp cannot supply the second one. A clipped box's top face and
 * its sides band identically under a hard three-step ramp, so the sunlit top has to be
 * painted in as its own material (`foliageLight`) or the whole bed is one flat slab. Every
 * planter, bed and tree in the kit lays both.
 */
const FOLIAGE_HUE = 0.26;
const FOLIAGE_SATURATION = 0.42;
const FOLIAGE_LIGHTNESS = 0.38;
/** The sunlit half: tree crowns and the tops of clipped beds. Yellower and much lighter. */
const FOLIAGE_LIGHT_HUE = 0.235;
const FOLIAGE_LIGHT_SATURATION = 0.52;
const FOLIAGE_LIGHT_LIGHTNESS = 0.52;

/**
 * THE LANDMARK PAINTS — the splashes.
 *
 * Playtest: *"the map is looking better. I think it still feels a little muted, like there
 * could be a little bit more splashes of colour."* Everything above this line is a system
 * for spending colour carefully, and it worked: the arena arrived at 8%-saturation storey
 * planes, pale tinted team walls and nothing else, which is a beautifully disciplined
 * greyscale drawing of a building. It is also, correctly observed, muted.
 *
 * The fix is not to raise the ceiling on everything — that is how a rainbow happens and the
 * comments above record exactly what it cost last time. It is to spend a *separate*,
 * hard-capped budget at genuinely architectural scale: `LANDMARK_MASSES` volumes in the
 * whole arena, chosen as the largest exposed masses in the building, painted at real chroma
 * and nothing else in the world touching these hues. Few, large, confident. A handful of big
 * blocks reads as design; thirty small painted trims read as noise, and the cap is what
 * makes the difference, not the chroma.
 *
 * ### Why jade and terracotta, and why not red, blue or yellow
 *
 *  - **Not azure, not signal red.** Those are the teams. A saturated wall in a team's hue is
 *    a wall that team's players cannot be seen against — the measured failure documented at
 *    `TEAM_WALL_SATURATION`, and it applies to any surface, not just a team's own.
 *  - **Not sun yellow.** At full chroma it lands at L* 84 / C* 69 against the chambered
 *    gun's 79 / 83. Yellow is therefore rationed to *machinery* — davit bands, hoists,
 *    windsocks and the city's tower cranes — where it is unmistakably equipment and never a
 *    plane of architecture. See `accentSun` in props.ts.
 *  - **These two clear both team colours by 15-19 L\***: jade lands at L* 75 and terracotta
 *    at L* 72, against teamA 56 and teamB 57. So a player of *either* team standing in front
 *    of one keeps a value edge as well as a hue edge, which no same-value plane would give.
 *  - **One warm, one cool.** A white building under a blue sky grades cold, and no lighting
 *    change fixes that from inside this file. A terracotta mass does: it is the single
 *    strongest "hot bright day" signal available in albedo alone.
 */
const LANDMARK_JADE_SATURATION = 0.52;
const LANDMARK_JADE_LIGHTNESS = 0.58;
const LANDMARK_CLAY_SATURATION = 0.58;
const LANDMARK_CLAY_LIGHTNESS = 0.66;
/** Machinery yellow. Cranes, hoists and windsocks — never a building. */
const LANDMARK_SUN_SATURATION = 0.9;
const LANDMARK_SUN_LIGHTNESS = 0.62;

/**
 * A WARM BIAS ON THE WHITE ARCHITECTURE.
 *
 * Playtest: *"I think it could feel a little more sunny. Like it's a beautiful day, you
 * know, like insanely pristine."* Lighting is not this file's to change, but albedo is, and
 * the dominant surface in the game being a *neutral* white is a real part of why the frame
 * grades cool: a white wall in direct sun is not neutral, it is the colour of the sun
 * falling on it. Pulling `panel` and `concrete` a third of the way toward `PALETTE.sun`
 * moves them from #fafaf8 to #fcf9f1 and #e9ebee to #eeedeb — a change of two or three
 * points, invisible on any one surface and worth a great deal across four hundred of them,
 * because it is the difference between white paper and white paper in sunlight.
 *
 * Deliberately small. The architecture must stay unmistakably white; this is a bias, not a
 * cream, and anything stronger would start arguing with the warm/cool grade the renderer
 * already applies.
 */
const SUNLIT_WHITE_BIAS = 0.3;
const SUNLIT_CONCRETE_BIAS = 0.22;

/**
 * A plane has to be *architecture* to earn paint. Anything with less exposed face than
 * this is trim, and trim is neutral — this threshold is the whole difference between "a
 * red stairwell" and "red piping everywhere".
 */
const ACCENT_MIN_FACE_AREA = 40.0;
/**
 * Hard cap on painted masses per paint, for the entire building.
 *
 * This used to be a 22% dice roll over every brush that cleared the area test, and on this
 * arena that is 205 candidates — about forty-five painted blocks scattered through a
 * five-storey atrium. Forty-five landmarks is no landmarks: it is a colour *texture*, which
 * is precisely the "neon crept back" failure, just matte.
 *
 * A cap is the honest way to spend a budget. The candidates are ranked by exposed face area
 * and the biggest few win, so the paint always lands on the plane that reads furthest, and
 * the count is knowable rather than emergent: at most four masses per storey plane, two per
 * team, plus one landmark band per storey. Twelve painted blocks out of three hundred and
 * forty-one — and ten of the twelve are chroma-capped, so the *saturated* fraction of the
 * frame is the two team ends and nothing else. Comfortably inside the 15% DESIGN.md 4 asks
 * for, which is the point: 85% of the screen has to be neutral for colour to mean anything.
 */
const ACCENT_PLANES_PER_PAINT = 4;
/**
 * Team paint gets a tighter budget than a storey plane: two walls per end, the two biggest.
 *
 * A storey's plane is allowed to be a *place* you are inside of. A team's colour is a
 * label on the far end of one deck, and two enormous walls say that completely. A third
 * starts marching the colour up the deck toward the middle, where it stops meaning "their
 * end" and starts meaning nothing.
 */
const ACCENT_PLANES_PER_TEAM = 2;
/**
 * Painted team banners per spawn.
 *
 * These are the biggest saturated fields in the game, so the count is the whole budget for
 * team colour on top of the two painted masses. Two boards name the end; a third is the
 * colour marching toward the middle of the deck, where it stops meaning "their end".
 */
const TEAM_BANNERS_PER_END = 2;
/**
 * Two masses of the same paint closer together than this are the same landmark said twice,
 * so the second one is skipped and the budget goes somewhere it adds information.
 */
const ACCENT_MIN_SPACING = 22.0;
/**
 * How far from a spawn centroid a brush still counts as that team's end of the map.
 *
 * Generous, because on this arena the spawn plateau itself is open deck and low cover —
 * the nearest wall worth painting is nearly thirty metres out. The radius is not what
 * bounds team colour; `ACCENT_PLANES_PER_TEAM` is, together with the fighting-deck test in
 * `paintFor`. Widening the search only changes *which* two walls win, never how many.
 */
const TEAM_END_RADIUS = 32.0;

/**
 * THE LANDMARK BUDGET. Three painted volumes in the whole arena, and that is the number.
 *
 * "Few, large and confident" is a count before it is anything else. Ranking picks the three
 * biggest mirror-classes of mass in the building and paints them; every other brush on the
 * map stays white. On SKYLINE NINE that resolves to the core tower's shoulder, the helipad
 * pedestal and the pair of flank stacks — three of the four things a player navigates by,
 * which is exactly where a landmark colour belongs.
 *
 * **Mirror-classes, not brushes.** The arena is mirrored across Z and a competitive map has
 * to stay symmetric: painting one of a mirrored pair and not the other would hand one team a
 * landmark the other does not have. Brushes are therefore grouped by their mirrored position
 * and a whole class wins or loses together, so a pair counts as one landmark and gets one
 * hue.
 */
const LANDMARK_MASSES = 3;
/** A mass has to be at least this big on its biggest face to be a landmark at all. */
const LANDMARK_MIN_FACE_AREA = 120.0;
/**
 * And no bigger than this, which is the less obvious half of the rule.
 *
 * Past a certain size a mass stops being a landmark and becomes *the room you are standing
 * in*, and paint on it is not an accent any more, it is the wall. The bound is set from the
 * measured spend rather than guessed: with the whole core tower and both flank stacks
 * painted, saturated accent comes out at about four per cent of the arena's outward surface
 * — a quarter of the 15% DESIGN.md 4 allows. That is the right side of the line for a
 * playtest that asked for *more* colour, and it is why the cap sits where it does rather
 * than lower.
 */
const LANDMARK_MAX_FACE_AREA = 800.0;
/** And at least this tall. A wide flat slab is a deck, not a mass you navigate by. */
const LANDMARK_MIN_HEIGHT = 6.0;
/** How far a landmark must stay clear of a spawn centroid, so it never muddies team colour. */
const LANDMARK_TEAM_CLEARANCE = 34.0;
/**
 * The hues, in the order the ranked classes take them — and the order is a readability
 * decision, not an aesthetic one.
 *
 * **Jade goes on the biggest mass**, because the biggest mass is the one players fight in
 * front of most, and jade is the hue that separates best from *both* team silhouettes: it
 * is complementary to signal red and it clears both team colours by 18 L\*. Terracotta is
 * the weaker of the two against red (a 15 L\* value edge and a narrower hue edge), so it
 * takes the smaller, more peripheral masses where a duel is less likely to be resolved
 * against it.
 */
const LANDMARK_PAINTS: readonly MatKey[] = ['accentJade', 'accentClay', 'accentJade'];
/**
 * Height above a landmark's own base at which its white relief band runs.
 *
 * A four-thousand-square-metre flat field of any colour is a swatch. One white band across
 * it at two thirds height, with the storey's own kerb detail above and below, turns it into
 * a building — and it drops the *saturated* fraction of that mass by about a fifth, which is
 * where the screen-area budget gets paid back.
 */
const LANDMARK_BAND_FRACTION = 0.66;
const LANDMARK_BAND_HEIGHT = 1.1;

// -- The roof garden --------------------------------------------------------

/**
 * Life on the deck: trees, water and shade.
 *
 * DESIGN.md 4 asks for planted greenery, still water and glass; the playtest asked for a
 * place that feels like a beautiful day. All three of these are rationed the same way the
 * rest of the kit is — only on a deck big enough to be a *terrace* rather than a ledge, at
 * most one water feature and one sail per deck, and everything either flat, see-through or
 * narrower than a player. See `P.TREE_MAX_WIDTH` for the honest-width rule they all obey.
 */
const GARDEN_MIN_DECK_AREA = 300.0;
/** Trees per qualifying deck, and the spacing along the walkway they line up on. */
const TREES_PER_DECK = 3;
const TREE_SPACING = 7.0;
/** Odds a qualifying deck gets a reflecting pool, and how big it is relative to the deck. */
const POOL_CHANCE = 0.5;
const POOL_MAX_SIZE = 7.0;
/** Odds a qualifying deck gets a shade sail. */
const SAIL_CHANCE = 0.45;
const SAIL_MAX_SIZE = 8.0;
/**
 * Fraction of roof edges that get a glass balustrade instead of a painted steel railing.
 *
 * Both, alternating, because a hundred edges detailed one way is one extruded detail said a
 * hundred times. Glass is the showpiece answer DESIGN.md 4 asks for ("spotless glass") and
 * steel is the service-roof answer; a building that has both is a building somebody designed.
 */
const GLASS_BALUSTRADE_SHARE = 0.42;
/**
 * How far behind a spawn pad the banner mast stands.
 *
 * Set clear of the shade canopy's far edge (`spawnCanopy`), because the mast is 6.4 m tall
 * and the canvas is at 3.6 m — put them on the same footprint and the mast spears straight
 * up through the middle of the sail.
 */
const SPAWN_MAST_RADIUS = 7.2;

// -- Signage ----------------------------------------------------------------

/** Region name stencils per region. More than this and the name stops being a landmark. */
const NAME_DECALS_PER_REGION = 3;
/** A wall needs at least this much clear face before it can carry a region name. */
const NAME_MIN_FACE_AREA = 30.0;
/**
 * The three widths a mark is allowed to be, in metres — small, medium, monumental.
 *
 * Scale was the other half of why the old marks read as accidents. A wall carried a 0.4 m
 * barcode, a 0.55 m notice and a 0.9 m tick all at once, each at its own randomly-rolled
 * size, so nothing looked set out. A tier picked from the plane's own area gives the
 * opposite read: a mark's size tells you how big the surface is, which is exactly what a
 * distance cue should do, and only three sizes ever appear anywhere in the building.
 */
const SIGN_TIERS: readonly number[] = [0.9, 2.2, 5.0];
/** Face area at which a plane steps up to the next tier. */
const SIGN_TIER_AREA: readonly number[] = [8.0, 45.0];
/**
 * A face smaller than this carries no mark at all. Small planes are architecture.
 *
 * Roughly a 4x3 m wall. Below it a stencil is bigger news than the surface it is on, and
 * on a map with three hundred brushes "every wall over 6 m²" is still hundreds of marks —
 * which is how a signage system turns back into wallpaper.
 */
const SIGN_MIN_FACE_AREA = 12.0;
/**
 * Largest fraction of a plane's width or height one mark may span.
 *
 * A stencil is a mark *on* a wall, not a skin for it — past about a third the wall stops
 * reading as a wall. This also bounds the tallest numeral, since the numeral art is 1.4
 * times as tall as it is wide.
 */
const SIGN_MAX_FACE_FRACTION = 0.34;
/**
 * Height above the foot of a wall that wall-run chevrons are stencilled at.
 *
 * Eye level for the 2.05 m body: `PLAYER_EYE_HEIGHT` is 1.85 m and a wall-run puts the
 * camera above that, so 1.9 m lands the mark where a player running the wall is already
 * looking. It was set for the old 1.8 m body and happens to still be right — checked
 * rather than assumed, because everything else keyed to human height had to move.
 */
const SIGN_EYE_HEIGHT = 1.9;
/**
 * Smallest deck that gets painted circulation lanes.
 *
 * Lane paint means "this is a route people walk". On a 14 m² ledge that is a lie and the
 * dashes read as loose marks on a small slab; at 60 m² and up it is a concourse and the
 * lines give the eye something to measure a long floor against. The threshold is the
 * difference between circulation paint and litter.
 */
const LANE_MIN_DECK_AREA = 60.0;
/** Painted floor lane dashes, metres per repeat. */
const LANE_PITCH = 3.2;
/** Width of a painted floor lane line. */
const LANE_WIDTH = 0.22;
/** Half-distance between the pair of painted lane lines on a wide deck. */
const LANE_SPACING = 2.4;
/**
 * Half-thickness above which a decorative plate stops being credible as a mounted board.
 * Beyond this it becomes see-through, because an opaque volume that collision ignores is a
 * lie about cover, and lies about cover lose rounds.
 */
const SIGN_MAX_THICKNESS = 0.5;
/**
 * How far behind a decorative plate to probe for solid geometry.
 *
 * A plate bolted flat to a wall occludes nothing the wall does not already occlude, so it
 * can be opaque. A plate hanging in open air occludes something collision does not, so it
 * cannot be. This distance is the whole test, and it is generous enough to survive the
 * gap a real fixing bracket leaves.
 */
const SIGN_MOUNT_PROBE = 0.9;
/**
 * Fraction of a plate's footprint that must come back solid before it may be opaque.
 *
 * Not 100%: a plate flush against a wall lands exactly on the wall's boundary planes at
 * its corners, and float equality there is a coin toss. Not 60% either — on a 28 m board
 * that would leave five metres of unbacked opaque material hanging off each end. 92% of a
 * 5x5 grid means at most a corner sample's worth of overhang.
 */
const SIGN_MOUNT_COVERAGE = 0.92;
/**
 * A plate lying flat and shorter than this may be opaque wherever it is, backed or not.
 *
 * A deck lip kerb is 14 cm tall and cantilevers a few centimetres over the void. Nothing
 * that low can conceal anything the game cares about: a crouched head sits at 0.86 m and a
 * gun at rest is 0.44 m across. Without this the kerbs on two of the four atrium lips come
 * back "unbacked" — true, and completely irrelevant — and the same painted edge ends up
 * rendered two different ways on the same storey.
 */
const SIGN_FLAT_SAFE_HEIGHT = 0.3;

// -- What a signage plate is allowed to be ----------------------------------

/**
 * Smallest dimension a signage plate's face may have and still take a region plane.
 *
 * Below this it is not a board, it is a *strip*: a deck lip rim, a pylon band, a piece of
 * edge trim. Painting those was where most of the arena's stray colour came from — a
 * painted line four hundred metres long tells you nothing a painted *wall* does not tell
 * you better. Strips get the neutral kerb-and-reveal treatment.
 */
const SIGN_MIN_BOARD_DIM = 1.2;
/** A face longer than this many times its own height is edge trim, whatever its size. */
const SIGN_MAX_BOARD_ASPECT = 8.0;
/** Face area above which a signage plate carries the storey name rather than a short notice. */
const SIGN_NAME_MIN_AREA = 12.0;

// -- Floor markings ---------------------------------------------------------

/**
 * There is no wear layer any more.
 *
 * `footScuff` laid up to three random-angle ellipse clusters on every deck in the arena at
 * a metre and a half across. On a polished slab seen from standing height they did not
 * read as scuffing; they read as short black diagonal slashes with no source object and no
 * perspective logic — leftover debug art scattered across the one surface the player looks
 * at most. Value texture on a floor is the *floor material's* job (see `floorPlate`, which
 * scores the slab into bays), not a decal's.
 */

/** An edge shorter than this gets striping only — a two-post rail reads as a prop. */
const RAILING_MIN_EDGE = 3.2;
/** How far inboard of the lip the rail line stands. Clear of the hazard band at the edge. */
const RAILING_INSET = 0.52;
/** Tighter, for a rail standing on the back of a roof parapet rather than free on a deck. */
const ROOF_RAILING_INSET = 0.2;

/** A ledge needs this much clear width before a painted keep-clear box fits inboard of it. */
const KEEP_CLEAR_MIN_WIDTH = 6.0;
/** Size of that box, metres. */
const KEEP_CLEAR_SIZE = 2.6;

// -- Markers ----------------------------------------------------------------

/**
 * Wall-run chevrons: metres of wall per mark, and the hard cap per face.
 *
 * A mark every eight metres, at most three to a wall, at one fixed width. The old rule was
 * one every 3.2 m up to six, which put ten or more chevrons across a single soffit band and
 * turned an instruction ("you can run this") into wallpaper. A chevron has to be rare
 * enough that seeing one means something.
 */
const CHEVRON_PITCH = 8.0;
const CHEVRON_MAX_PER_FACE = 3;
const CHEVRON_WIDTH = 1.3;
/** A wall has to be at least this big before it is worth announcing as runnable. */
const CHEVRON_MIN_FACE_W = 6.0;
const CHEVRON_MIN_FACE_H = 3.0;
/** Metres of ramp per downhill chevron. Ramps are shorter than walls, so a tighter pitch. */
const RAMP_CHEVRON_PITCH = 4.0;
/**
 * There is no team spawn fill light any more.
 *
 * A coloured point light over each spawn made sense in a dark interior. Outdoors at midday
 * it is nonsense twice over: it is invisible against a sun-lit white deck, and where it is
 * visible at all it tints the one surface whose neutrality the whole value ladder depends
 * on. Which end a player is standing in is now said by a parapet and a banner — objects,
 * lit by the same sun as everything else.
 */

// -- Ceilings, roofs and plant ----------------------------------------------

/**
 * Coffer bay size on a soffit, metres.
 *
 * Four metres matches the floor's own expansion-joint bay, so a deck and the ceiling over
 * it are laid out on the same grid — which is what a real building does and what makes a
 * space feel measured rather than assembled.
 */
const SOFFIT_BAY = 4.0;
/** How far a coffer rib hangs below the soffit plate. */
const SOFFIT_RIB_DEPTH = 0.34;
/** Smallest soffit that can take a rooflight. Below this it is a beam, not a roof. */
const SKYLIGHT_MIN_SPAN = 6.0;
/** Metres of roof per rooflight, and the hard cap per axis. */
const SKYLIGHT_PITCH = 9.0;
const SKYLIGHT_MAX_PER_AXIS = 4;
/** Largest a single rooflight may be. Bigger than this and the ceiling is a light box. */
const SKYLIGHT_MAX_SIZE = 4.2;
/**
 * A hanging duct needs this much air between the soffit and whatever is under it.
 *
 * Generous: the duct drops most of a metre, the player is 2.05 m, and a jumping or
 * grappling player goes a long way above that. Nothing hung from a ceiling is allowed to
 * become something you can crouch behind.
 */
const DUCT_MIN_CLEARANCE = 7.0;
/** Metres of soffit width per duct run. */
const DUCT_PITCH = 7.0;

/** Chance a qualifying wall gets a planting bed at its foot. Punctuation, not texture. */
const PLANTING_CHANCE = 0.34;
/** Longest single run of planting. */
const PLANTING_MAX_RUN = 9.0;
/** How far a wall brush may be sunk into the slab it stands on and still take a bed at its foot. */
const PLANTING_MAX_SINK = 4.0;
/** A wall has to rise this far above its deck before a bed at its foot makes sense. */
const PLANTING_MIN_RISE = 2.0;
/** Fraction of interior ceilings that carry hung ductwork. Plant is punctuation too. */
const DUCT_CHANCE = 0.42;

// -- Grapple anchors --------------------------------------------------------

/**
 * DAVIT DIMENSIONS, sized against a 2.05 m player and a 60 m `GRAPPLE_RANGE`.
 *
 * The fixture is a window-washing davit — a mast, a jib arm cantilevering out over the
 * drop, a diagonal stay, a sheave block and a hook. It is bigger than the old sci-fi gimbal
 * on purpose: at the far end of the grapple's reach a 4.6 m jib still subtends about four
 * degrees, which is eighty pixels at 1080p and 92 degrees of FOV, and a playtester could
 * not find the previous fixture *at all*.
 *
 * The old one glowed neon jade and read as a powerup. This one has to be found against
 * BRIGHT SKY, which is a harder problem than finding something against a dark ceiling and
 * cannot be solved with emission — a glow on white is invisible. It is solved three ways
 * instead, and none of them spends a hue on architecture:
 *
 *  1. **Silhouette.** An L: a vertical mast with a horizontal arm and a diagonal stay
 *     across the corner. Nothing else in a roofscape of horizontal decks and vertical
 *     cores has that outline, so recognition never depends on resolving detail.
 *  2. **Both values, hard against each other.** A graphite frame carrying a white-painted
 *     counterweight. Against sky the graphite reads; against the white city the paint
 *     reads; there is no background in this arena that swallows both.
 *  3. **A high-frequency warm band on the jib nose** (`warn`). A stripe survives to a
 *     distance where a flat tone has washed out, and it is matte, so it can never be
 *     mistaken for the one emissive amber in the game.
 */
const ANCHOR_MAST_HEIGHT = 5.0;
const ANCHOR_JIB_REACH = 4.6;
/** How far the hook hangs below the jib nose. The anchor point itself sits at the sheave. */
const ANCHOR_HOOK_DROP = 1.5;
/**
 * Radius of the billboarded halo, in metres at the anchor's distance.
 *
 * Halved, and its intensity cut hard. Against an open sky an additive halo is nearly
 * invisible anyway — you cannot add light to something already near white — so it does
 * almost nothing where the silhouette now does everything. Where it still earns its keep is
 * the other half of the frame: an anchor seen against the shaded flank of a tower or against
 * the city far below, where a faint warm ring is the difference between spotting a fixture
 * and sweeping for it. Small, warm and quiet, so it can never be read as a pickup.
 */
const ANCHOR_HALO_RADIUS = 1.5;

// -- The roof ---------------------------------------------------------------

/**
 * Fraction of open-to-sky decks laid in gravel ballast rather than membrane.
 *
 * Two roof surfaces, not one. Real adjacent roofs differ by what is laid on them, and here
 * that difference is doing wayfinding work for free: a ballasted deck is a full value step
 * brighter than a membrane one, so "the pale roof" and "the grey roof" are callouts that
 * survive greyscale, distance and colour blindness — and neither of them costs a hue.
 */
const GRAVEL_ROOF_CHANCE = 0.38;
/** Metres of roof per vent curb, and the hard cap per deck. Plant is punctuation. */
const CURB_PITCH = 13.0;
const CURB_MAX_PER_DECK = 5;
/** Smallest deck that gets a service walkway painted and gratinged across it. */
const WALKWAY_MIN_AREA = 120.0;
/** Chance a deck big enough for a walkway also gets planting beside it. Punctuation. */
const ROOF_PLANTER_CHANCE = 0.5;
/** Longest single run of roof planting. */
const PLANTER_MAX_RUN = 9.0;
/** Smallest roof deck that can take a rooflight. */
const DECK_SKYLIGHT_MIN_SPAN = 9.0;
/** Metres of roof per rooflight on a deck's TOP face, and the cap per axis. */
const DECK_SKYLIGHT_PITCH = 14.0;
const DECK_SKYLIGHT_MAX_PER_AXIS = 3;
/** Largest a rooflight let into a deck may be. */
const DECK_SKYLIGHT_SIZE = 3.4;
/** A deck counts as open to sky when nothing solid rises this far above it. */
const SKY_CLEARANCE = 1.0;
/**
 * Fraction of a deck's footprint that must be uncovered before it is treated as a roof.
 *
 * Two-thirds, not all of it. A real roof carries a stair core, a lift overrun and a plant
 * enclosure and is still unambiguously a roof; demanding a completely clear column would
 * classify every interesting deck in the arena as an interior and hand it a polished
 * terrazzo floor under an open sky.
 */
const SKY_OPEN_FRACTION = 0.66;

// -- Skyline ----------------------------------------------------------------

/**
 * Buildings in the surrounding city.
 *
 * Nearly doubled from 96, and it is the cheapest visual spend in the file: these are flat
 * unlit boxes outside the play space, they never cast or receive, and they are the entire
 * difference between "an arena" and "a rooftop in a city". DESIGN.md 4 asks for a real
 * horizon and a real city; you cannot get one out of a ring of ninety-six.
 */
const SKYLINE_BLOCKS = 170;
/** How many times the placement angle wraps, so the ring reads as depth rather than a fence. */
const SKYLINE_RINGS = 5;
/**
 * Where the two hazed tiers break, as a fraction of the reach past the near ring.
 *
 * The near ring is no longer one of these — it is placed separately, see
 * `SKYLINE_NEAR_BLOCKS`. Everything in this pass simply hazes toward the sky with distance.
 */
const SKYLINE_MID_FRACTION = 0.42;
/**
 * THE NEAR RING — the roofs next door, and the one tier that is placed rather than sampled.
 *
 * It used to be drawn from the same random spiral as the rest of the city, taking whichever
 * 16% of 170 blocks happened to roll a short distance. Measured on this arena that came out
 * at **26 blocks with ten empty five-degree sectors and holes up to twenty degrees wide**:
 * stand at the wrong parapet, look out, and the ring of neighbouring roofs the whole altitude
 * read depends on simply is not there — just hazed towers four hundred metres off and the
 * void underneath. A backdrop that is present from three sides of the map and missing from
 * the fourth is worse than one that is thin everywhere, because the player finds the seam.
 *
 * So the near ring is now swept at a fixed angular pitch with bounded jitter, which makes
 * full coverage a property of the construction instead of a property of the seed. At this
 * count the pitch is ~8 degrees and a near block subtends ~11, so neighbours overlap even at
 * worst-case jitter. They alternate between an inner and an outer sub-band so the ring reads
 * as a *field* of roofs receding, not as a fence standing at one radius.
 */
const SKYLINE_NEAR_BLOCKS = 44;
/** Depth of the near ring's band, as a fraction of the arena span. */
const SKYLINE_NEAR_BAND = 0.62;
/** Largest angular wobble off the fixed pitch, as a fraction of that pitch. */
const SKYLINE_NEAR_JITTER = 0.34;
/**
 * How far below the arena's own decks the near city sits.
 *
 * The whole point of a rooftop is the drop, and a drop is only legible against something to
 * be above. Seating the near ring well below the play space means the horizon line of the
 * city cuts across the *bottom* of the frame when a player looks out, which is what "high
 * up" actually looks like.
 */
const SKYLINE_NEAR_DROP = 26.0;

/**
 * A named region of the building.
 *
 * Regions are **storeys**, not map halves. In an arena whose entire premise is verticality
 * the most useful thing a plane can tell you is *what altitude you are at*, so each deck is
 * one region with one plane, and a callout resolves to a height.
 *
 * The planes are separated by value, not hue: one storey is dark, one is bright, the rest
 * are plain slate, and the two team paints sit at the two ends of the fighting deck. That
 * is the entire colour scheme of the building.
 */
interface Region {
  key: string;
  name: string;
  /** Storey number, counting up from the lowest. Painted next to the name. */
  numeral: number;
  /** Deck height this region's floor sits at. */
  y: number;
  /** Material for a monumental painted plane. Neutral regions use slate. */
  paint: MatKey;
  /** True when `paint` is a distinct storey plane rather than the default slate. */
  painted: boolean;
  crown: 'ring' | 'spires' | 'pylon' | 'dish' | 'lattice';
}

/** What a brush is entitled to be painted, and how a stencil on it must be tinted. */
interface Paint {
  paint: MatKey;
  /** True when this is one of the two team paints — the only saturated hues in the world. */
  team: boolean;
  /** Painted at all, i.e. not the default slate. */
  painted: boolean;
}

/**
 * Storey names, bottom to top, for each possible storey count.
 *
 * These are ROOF names now, not atrium names. `SUMP`, `UNDER` and `GALLERY` described a
 * drowned five-deck interior, and a callout is a promise about the place you are standing
 * in: telling a player they are on the `GALLERY` when they are on an open roof deck under
 * open sky teaches them the names are decoration. Every word here is something a real
 * roofscape has, and each one says roughly how high it is without needing a number.
 */
const DECK_NAMES: readonly (readonly string[])[] = [
  [],
  ['ROOF'],
  ['ROOF', 'CROWN'],
  ['APRON', 'ROOF', 'CROWN'],
  ['APRON', 'DECK', 'ROOF', 'CROWN'],
  ['APRON', 'STACK', 'DECK', 'ROOF', 'CROWN'],
];
const CROWN_SHAPES: readonly Region['crown'][] = ['pylon', 'dish', 'ring', 'lattice', 'spires'];

/** The two saturated hues, by paint key. Nothing else in the world has an entry here. */
const TEAM_PAINT_COLORS: Readonly<Record<string, number>> = {
  teamA: PALETTE.teamA,
  teamB: PALETTE.teamB,
};

/**
 * The full-chroma accent that belongs to each team paint. Skirting bands and spawn pads
 * only — see TEAM_WALL_SATURATION.
 */
const TEAM_BAND_MATS: Readonly<Record<string, MatKey>> = {
  teamA: 'teamBandA',
  teamB: 'teamBandB',
};

/**
 * Paints a white stencil reads against. Everything else takes an ink stencil.
 *
 * The team paints used to be in here, because they used to be full-chroma walls. They are
 * pale plaster now, so a white stencil on one would be invisible — they take ink, or, when
 * the mark is naming the team, the team's own full-chroma colour.
 */
const DARK_PAINTS: ReadonlySet<MatKey> = new Set<MatKey>(['trim', 'zoneA']);
/** Paints that are a deliberate plane rather than the building's default slate. */
const PAINTED: ReadonlySet<MatKey> = new Set<MatKey>(['zoneA', 'zoneB', 'teamA', 'teamB']);

/**
 * Every string the building is allowed to paint on itself, on top of the storey names and
 * the two team names.
 *
 * Empty, and that is the end of a long shortening. It once held `NO ENTRY`, `KEEP CLEAR`
 * and `EXIT`, which are *instructions* — and none of them was true, because there is
 * nowhere in this arena a player may not go. Signage that lies is worse than a blank
 * board: it teaches a player to stop reading signs, and the storey names are signs they
 * need. The last survivor was `CORE`, naming the middle of the atrium; the middle of the
 * roof is a helipad now, and a bordered H says what it is far better than a word does.
 */
const EXTRA_LABELS: readonly string[] = [];

// ---------------------------------------------------------------------------
// Solid geometry oracle — the thing that keeps decoration honest
// ---------------------------------------------------------------------------

interface Solid {
  /** World → local, so containment is a box test. */
  inv: THREE.Matrix4;
  half: THREE.Vector3;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

/**
 * Answers "is this point inside something solid?" and "how high is the world here?".
 * Both questions gate every piece of decoration this file emits.
 */
class SolidOracle {
  private readonly solids: Solid[] = [];
  private readonly grid = new Map<number, number[]>();
  private readonly height: Float32Array;
  private readonly gw: number;
  private readonly gd: number;
  private readonly ox: number;
  private readonly oz: number;
  readonly floorY: number;

  constructor(arena: ArenaData) {
    this.ox = arena.bounds.min.x;
    this.oz = arena.bounds.min.z;
    this.floorY = arena.bounds.min.y;
    this.gw = Math.max(1, Math.ceil((arena.bounds.max.x - arena.bounds.min.x) / GRID_CELL) + 1);
    this.gd = Math.max(1, Math.ceil((arena.bounds.max.z - arena.bounds.min.z) / GRID_CELL) + 1);
    this.height = new Float32Array(this.gw * this.gd).fill(this.floorY);

    for (const b of arena.brushes) {
      if (b.decorative) continue; // collision skips it, so it must not count as solid here either
      this.push(brushMatrix(b), vec(b.half));
    }
    for (const r of arena.ramps) {
      this.push(rampMatrix(r), vec(r.half));
    }
  }

  private push(xf: THREE.Matrix4, half: THREE.Vector3): void {
    const inv = xf.clone().invert();
    // World AABB of the oriented box.
    const e = new THREE.Vector3();
    const m = xf.elements;
    e.set(
      Math.abs(m[0]) * half.x + Math.abs(m[4]) * half.y + Math.abs(m[8]) * half.z,
      Math.abs(m[1]) * half.x + Math.abs(m[5]) * half.y + Math.abs(m[9]) * half.z,
      Math.abs(m[2]) * half.x + Math.abs(m[6]) * half.y + Math.abs(m[10]) * half.z,
    );
    const c = new THREE.Vector3(m[12], m[13], m[14]);
    const s: Solid = {
      inv,
      half: half.clone(),
      minX: c.x - e.x,
      maxX: c.x + e.x,
      minY: c.y - e.y,
      maxY: c.y + e.y,
      minZ: c.z - e.z,
      maxZ: c.z + e.z,
    };
    const index = this.solids.length;
    this.solids.push(s);

    const i0 = this.cellX(s.minX);
    const i1 = this.cellX(s.maxX);
    const k0 = this.cellZ(s.minZ);
    const k1 = this.cellZ(s.maxZ);
    for (let i = i0; i <= i1; i++) {
      for (let k = k0; k <= k1; k++) {
        const key = i * 8192 + k;
        let list = this.grid.get(key);
        if (!list) {
          list = [];
          this.grid.set(key, list);
        }
        list.push(index);
        const h = this.height[i * this.gd + k];
        if (s.maxY > h) this.height[i * this.gd + k] = s.maxY;
      }
    }
  }

  private cellX = (x: number): number => clamp(Math.floor((x - this.ox) / GRID_CELL), 0, this.gw - 1);
  private cellZ = (z: number): number => clamp(Math.floor((z - this.oz) / GRID_CELL), 0, this.gd - 1);

  /** Is the world point inside any solid brush or ramp? */
  contains(p: THREE.Vector3): boolean {
    const list = this.grid.get(this.cellX(p.x) * 8192 + this.cellZ(p.z));
    if (!list) return false;
    const local = _p0;
    for (const i of list) {
      const s = this.solids[i];
      if (p.x < s.minX || p.x > s.maxX || p.y < s.minY || p.y > s.maxY || p.z < s.minZ || p.z > s.maxZ) continue;
      local.copy(p).applyMatrix4(s.inv);
      if (
        Math.abs(local.x) <= s.half.x + 1e-4 &&
        Math.abs(local.y) <= s.half.y + 1e-4 &&
        Math.abs(local.z) <= s.half.z + 1e-4
      ) {
        return true;
      }
    }
    return false;
  }

  /** Highest solid surface over this column (conservative — over-estimates on ramps). */
  heightAt(x: number, z: number): number {
    return this.height[this.cellX(x) * this.gd + this.cellZ(z)];
  }

  /** Highest solid surface anywhere in the XZ rectangle. */
  maxHeightIn(x0: number, z0: number, x1: number, z1: number): number {
    const i0 = this.cellX(Math.min(x0, x1));
    const i1 = this.cellX(Math.max(x0, x1));
    const k0 = this.cellZ(Math.min(z0, z1));
    const k1 = this.cellZ(Math.max(z0, z1));
    let best = this.floorY;
    for (let i = i0; i <= i1; i++) {
      for (let k = k0; k <= k1; k++) {
        const h = this.height[i * this.gd + k];
        if (h > best) best = h;
      }
    }
    return best;
  }

  /**
   * Highest solid surface at (x, z) that is at or below `y`. -Infinity when there is none.
   *
   * `heightAt` cannot answer "what am I standing on", because it reports the tallest solid
   * over the whole *column* — under the helipad mast that is the mast, forty metres up. Any
   * mark painted on a deck has to be placed against the deck, so it asks this instead.
   */
  surfaceUnder(x: number, z: number, y: number): number {
    const list = this.grid.get(this.cellX(x) * 8192 + this.cellZ(z));
    if (!list) return -Infinity;
    let best = -Infinity;
    for (const i of list) {
      const s = this.solids[i];
      if (s.maxY > y || s.maxY <= best) continue;
      if (x < s.minX || x > s.maxX || z < s.minZ || z > s.maxZ) continue;
      _p0.set(x, s.maxY - 1e-3, z).applyMatrix4(s.inv);
      if (
        Math.abs(_p0.x) <= s.half.x + 1e-3 &&
        Math.abs(_p0.y) <= s.half.y + 1e-3 &&
        Math.abs(_p0.z) <= s.half.z + 1e-3
      ) {
        best = s.maxY;
      }
    }
    return best;
  }

  /** Is there a solid *overhanging* (x, z) — one whose underside is above `y`? */
  private overheadAt(x: number, z: number, y: number): boolean {
    const list = this.grid.get(this.cellX(x) * 8192 + this.cellZ(z));
    if (!list) return false;
    for (const i of list) {
      const s = this.solids[i];
      // Must both *start* above the plane and rise above it: that is what a soffit is. A
      // mass whose base is at or below the deck is a building standing ON the roof.
      if (s.minY < y || s.maxY <= y) continue;
      if (x < s.minX || x > s.maxX || z < s.minZ || z > s.maxZ) continue;
      _p0.set(x, s.minY + 1e-3, z).applyMatrix4(s.inv);
      if (
        Math.abs(_p0.x) <= s.half.x + 1e-3 &&
        Math.abs(_p0.y) <= s.half.y + 1e-3 &&
        Math.abs(_p0.z) <= s.half.z + 1e-3
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Fraction of an XZ rectangle with nothing built OVER it at height `y`.
   *
   * This is the "is this deck a roof" test, and the distinction it draws is the whole point.
   * Asking whether anything is *taller* than the deck is the obvious version and it is
   * wrong twice over: the height grid reports the tallest solid over each column, so a
   * single stair core or parapet condemns the whole roof — and even with that fixed, a core
   * tower rising out of a deck is not a roof over it. On this arena the apron ring, one of
   * the two most fought-over surfaces in the level, failed at 51% open purely because the
   * building it belongs to continues upward through the middle of it, and it was being
   * handed a polished interior floor under open sky.
   *
   * So a column is covered only when some solid's *underside* sits above the deck. A lift
   * overrun standing on a roof is not a ceiling; the plant-room slab and the glass-link roof
   * are, and those are the only two things in this arena that should be.
   *
   * And columns where the deck itself is *buried* are not counted at all. The apron ring is
   * one 66 m brush whose middle forty metres are underneath the core tower; those columns
   * are not roof, they are not floor, they are inside a building, and letting them vote
   * dragged the one surface they say nothing about down to 58% and lost it its membrane.
   */
  openFractionAbove(x0: number, z0: number, x1: number, z1: number, y: number): number {
    const ax = Math.min(x0, x1);
    const bx = Math.max(x0, x1);
    const az = Math.min(z0, z1);
    const bz = Math.max(z0, z1);
    const nx = clamp(Math.ceil((bx - ax) / GRID_CELL), 1, 24);
    const nz = clamp(Math.ceil((bz - az) / GRID_CELL), 1, 24);
    // Just above the deck: if a player could not stand here, this column has no opinion.
    const stand = y - SKY_CLEARANCE + 0.5;
    let open = 0;
    let total = 0;
    for (let i = 0; i < nx; i++) {
      const x = ax + ((bx - ax) * (i + 0.5)) / nx;
      for (let k = 0; k < nz; k++) {
        const z = az + ((bz - az) * (k + 0.5)) / nz;
        if (this.contains(_p1.set(x, stand, z))) continue;
        total++;
        if (!this.overheadAt(x, z, y)) open++;
      }
    }
    // A deck almost entirely built over is genuinely an interior, so fall back rather than
    // dividing by nothing.
    if (total < nx * nz * 0.25) return open / (nx * nz);
    return open / total;
  }

  /** True when a straight span from a→b stays `clear` metres above everything under it. */
  spanIsClear(a: THREE.Vector3, b: THREE.Vector3, lowestY: number, clear: number): boolean {
    const steps = Math.max(2, Math.ceil(a.distanceTo(b) / GRID_CELL));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = a.x + (b.x - a.x) * t;
      const z = a.z + (b.z - a.z) * t;
      if (lowestY - this.heightAt(x, z) < clear) return false;
    }
    return true;
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const _p0 = new THREE.Vector3();
const _p1 = new THREE.Vector3();
const _bufferSize = new THREE.Vector2();

const vec = (v: Vec3): THREE.Vector3 => new THREE.Vector3(v.x, v.y, v.z);

const _mixA = new THREE.Color();
const _mixB = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
/** Blend two palette entries. Keeps every derived tone anchored to PALETTE. */
function mix(a: number, b: number, t: number): number {
  _mixA.setHex(a, THREE.SRGBColorSpace);
  _mixB.setHex(b, THREE.SRGBColorSpace);
  return _mixA.lerp(_mixB, t).getHex(THREE.SRGBColorSpace);
}

/**
 * Take a palette hue's *identity* — its position on the colour wheel — and throw away its
 * chroma and its value, re-landing it at `WORLD_MAX_SATURATION` and a chosen lightness.
 *
 * This is the single function that keeps the world at two saturated hues. Jade stays
 * recognisably the cool storey and sun yellow the warm one, so the arena has not lost its
 * identity; they simply stop shouting, and they land on the value ladder where the eye is
 * already reading form. Derived from PALETTE rather than hard-coded, so an art-direction
 * change upstream still repaints the building.
 */
function zoneTone(hue: number, lightness: number): number {
  return recolor(hue, WORLD_MAX_SATURATION, lightness);
}

/** Re-land a palette hue at a chosen chroma ceiling and lightness. */
function recolor(hue: number, maxSaturation: number, lightness: number): number {
  _mixA.setHex(hue, THREE.SRGBColorSpace);
  _mixA.getHSL(_hsl, THREE.SRGBColorSpace);
  _mixA.setHSL(_hsl.h, Math.min(_hsl.s, maxSaturation), lightness, THREE.SRGBColorSpace);
  return _mixA.getHex(THREE.SRGBColorSpace);
}

/**
 * A team's colour as **wall paint**: same hue, a quarter of the chroma, and lifted into the
 * band the white architecture occupies. See TEAM_WALL_SATURATION for why this is a
 * gameplay requirement rather than a preference.
 */
function teamWallTone(hue: number): number {
  return recolor(hue, TEAM_WALL_SATURATION, TEAM_WALL_LIGHTNESS);
}

/**
 * The colour a paint key actually renders as.
 *
 * Published on the arena group so the HUD can draw a callout swatch that matches the
 * building exactly. Derived from the same `zoneTone` call the material uses, so the two
 * can never drift apart — a minimap chip in full-chroma jade over a deck painted in
 * 8%-saturation jade would be the rainbow leaking back in through the UI.
 */
function paintColor(paint: MatKey): number {
  switch (paint) {
    case 'zoneA':
      return zoneTone(PALETTE.accentA, ZONE_A_LIGHTNESS);
    case 'zoneB':
      return zoneTone(PALETTE.accentB, ZONE_B_LIGHTNESS);
    case 'teamA':
      return PALETTE.teamA;
    case 'teamB':
      return PALETTE.teamB;
    default:
      return PALETTE.ink;
  }
}

/**
 * The tint a stencil must take to read against the plane it is painted on.
 *
 * A stencil at the same value as its board is not a sign, it is a texture — so this is a
 * value decision, never a taste one, and it is the only place in the file that chooses a
 * decal colour.
 */
function stencilOn(paint: MatKey): number {
  return DARK_PAINTS.has(paint) ? PALETTE.base : PALETTE.ink;
}

/**
 * Brush transform. `yaw` matches the shared convention (yaw 0 faces -Z, +yaw turns
 * left), which is exactly `Object3D.rotation.y`.
 */
function brushMatrix(b: Brush): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    vec(b.pos),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(0, b.yaw, 0, 'YXZ')),
    new THREE.Vector3(1, 1, 1),
  );
}

/**
 * Ramp transform: yaw first, then pitch about the ramp's own X axis so the slab
 * rises along its local Z. Modelled as an oriented slab (the same shape an OBB
 * collider built from {pos, half, yaw, pitch} produces).
 */
function rampMatrix(r: Ramp): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    vec(r.pos),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(r.pitch, r.yaw, 0, 'YXZ')),
    new THREE.Vector3(1, 1, 1),
  );
}

const surfaceIsSeeThrough = (s: SurfaceKind): boolean => s === SurfaceKind.Grate || s === SurfaceKind.Glass;

/**
 * A synthetic upward-facing `Face` at a world point, for the handful of props that want a
 * deck frame but are placed from a spawn or a centre point rather than from a brush.
 *
 * Matches `boxFaces` index 2 exactly — normal +Y, u along +X, v along -Z — so anything
 * placed through it lands in the same convention (and at the same `FLOOR_TOP` offset) as
 * everything placed on a real deck. The extents are nominal; `facePoint` never reads them.
 */
function flatFaceAt(p: THREE.Vector3): P.Face {
  return {
    index: 2,
    n: new THREE.Vector3(0, 1, 0),
    u: new THREE.Vector3(1, 0, 0),
    v: new THREE.Vector3(0, 0, -1),
    c: p.clone(),
    w: 1,
    h: 1,
  };
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

class ArenaBuilder implements BuildCtx {
  private readonly buckets = new Map<MatKey, THREE.BufferGeometry[]>();
  private readonly materials = new Map<MatKey, THREE.Material>();
  private readonly textures: THREE.Texture[] = [];
  private readonly disposables: { dispose(): void }[] = [];
  private readonly regions: Region[] = [];
  /**
   * The storey the spawns stand on. Team paint is confined to it, because "which end of
   * the deck is that" is a question about the deck — a red slab three storeys down says
   * nothing true about where anybody spawns.
   */
  private fightingDeckKey = '';
  private teamEnds: { centre: THREE.Vector3; paint: MatKey }[] = [];
  /** The brushes that won a slot in the colour budget, and the hue each one gets. */
  private readonly accentBrushes = new Map<Brush, MatKey>();
  /**
   * The `LANDMARK_MASSES` volumes that carry the arena's saturated architectural colour.
   *
   * Chosen before anything else is skinned, so `chooseAccentBrushes` can stand off them and
   * the chroma-capped storey planes never land on a brush that is already a landmark.
   */
  private readonly landmarkBrushes = new Map<Brush, MatKey>();
  /** Faces big enough to carry a region name, gathered during the brush pass. */
  private readonly nameSites = new Map<string, { area: number; place: () => void }[]>();
  private readonly oracle: SolidOracle;
  private readonly atlas: DecalAtlas;
  private readonly decals: DecalBuilder;
  private readonly gradient: THREE.DataTexture;
  private readonly random: () => number;
  private readonly group = new THREE.Group();

  private readonly boundsMin: THREE.Vector3;
  private readonly boundsMax: THREE.Vector3;
  private readonly arenaSpan: number;
  /** Decks above this get the paler floor: more sky reaches them. */
  private readonly midHeight: number;

  private anchorMaterial: THREE.ShaderMaterial | null = null;
  private haloMaterial: THREE.ShaderMaterial | null = null;
  private outlineMaterial: THREE.ShaderMaterial | null = null;

  constructor(private readonly arena: ArenaData) {
    this.random = makeRng(hashString(arena.name) ^ 0x0e75ab1e);
    this.oracle = new SolidOracle(arena);
    this.boundsMin = vec(arena.bounds.min);
    this.boundsMax = vec(arena.bounds.max);
    this.arenaSpan = Math.max(this.boundsMax.x - this.boundsMin.x, this.boundsMax.z - this.boundsMin.z);
    this.midHeight = this.boundsMin.y + (this.boundsMax.y - this.boundsMin.y) * 0.4;

    this.computeRegions();
    // Landmarks first: they are the arena's biggest colour statement, so the chroma-capped
    // storey planes have to be chosen around them rather than the other way round.
    this.chooseLandmarkMasses();
    this.chooseAccentBrushes();
    const labels = [...this.regions.map((r) => r.name), ...TEAM_NAMES, ...EXTRA_LABELS];
    this.atlas = buildDecalAtlas(this.random, labels);
    this.decals = new DecalBuilder(this.atlas);
    this.gradient = P.makeToonGradient(TOON_BANDS);
    this.textures.push(this.gradient);
  }

  // -- BuildCtx ------------------------------------------------------------

  rng(): number {
    return this.random();
  }

  add(mat: MatKey, geo: THREE.BufferGeometry, xf: THREE.Matrix4): void {
    const g = geo.index ? geo.toNonIndexed() : geo.clone();
    g.applyMatrix4(xf);
    this.pushGeometry(mat, g);
  }

  addRaw(mat: MatKey, geo: THREE.BufferGeometry): void {
    this.pushGeometry(mat, geo.index ? geo.toNonIndexed() : geo);
  }

  decal(p: DecalPlacement): void {
    this.decals.place(p);
  }

  decalKind(kind: DecalKind, p: Omit<DecalPlacement, 'cell'> & { variant?: number }): void {
    this.decals.placeKind(kind, this.random, p);
  }

  /** Normalise attributes so every geometry in a bucket merges cleanly. */
  private pushGeometry(mat: MatKey, g: THREE.BufferGeometry): void {
    for (const name of Object.keys(g.attributes)) {
      if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
    }
    if (!g.getAttribute('normal')) g.computeVertexNormals();
    if (!g.getAttribute('uv')) {
      const n = g.getAttribute('position').count;
      g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
    }
    g.clearGroups();
    g.morphAttributes = {};
    let list = this.buckets.get(mat);
    if (!list) {
      list = [];
      this.buckets.set(mat, list);
    }
    list.push(g);
  }

  // -- Regions -------------------------------------------------------------

  /**
   * Find the storeys by asking the geometry where its floors are: histogram every
   * substantial top face by height, weighted by area, and keep the peaks that are far
   * enough apart to be separate decks. Derived rather than authored, so this survives an
   * arena being retuned or a second arena being added.
   */
  private computeRegions(): void {
    const lo = this.boundsMin.y;
    const bins = new Map<number, number>();
    /** Per bin: the exact slab heights inside it, by how much area each one carries. */
    const binTops = new Map<number, Map<number, number>>();
    for (const b of this.arena.brushes) {
      if (b.decorative) continue;
      const area = b.half.x * b.half.z * 4;
      if (area < DECK_MIN_AREA) continue;
      const top = Math.round((b.pos.y + b.half.y) * 1000) / 1000;
      const key = Math.round((top - lo) / DECK_BIN);
      bins.set(key, (bins.get(key) ?? 0) + area);
      let tops = binTops.get(key);
      if (!tops) {
        tops = new Map();
        binTops.set(key, tops);
      }
      tops.set(top, (tops.get(top) ?? 0) + area);
    }

    /**
     * A bin is 1.5 m wide, so its centre is up to 75 cm away from the slab it describes,
     * and every downstream test that compares a brush base against a storey height is
     * quietly wrong by that much. It is not cosmetic: `fightingDeckKey` resolves a spawn to
     * a storey by an inequality, and on this arena a bin centre of 26.5 against a real roof
     * at 26.0 put the team colours on the wrong deck entirely. So a level is snapped to the
     * exact top face that carries the most area inside its bin — the real slab, not the
     * histogram's opinion of it.
     */
    const snap = (key: number): number => {
      const tops = binTops.get(key);
      if (!tops) return lo + key * DECK_BIN;
      let bestTop = lo + key * DECK_BIN;
      let bestArea = -1;
      for (const [top, area] of tops) {
        // Ties go to the HIGHER face, and that is not arbitrary: a roof slab and the shaft
        // it caps have identical footprints, so they tie exactly, and the one you walk on is
        // the top of the slab. Picking the lower one put a whole storey 1.2 m underground.
        if (area > bestArea || (area === bestArea && top > bestTop)) {
          bestArea = area;
          bestTop = top;
        }
      }
      return bestTop;
    };

    const ranked = [...bins.entries()].sort((p, q) => q[1] - p[1] || p[0] - q[0]);
    const levels: number[] = [];
    for (const [key] of ranked) {
      if (levels.length >= DECK_MAX) break;
      const y = snap(key);
      if (levels.some((l) => Math.abs(l - y) < DECK_MIN_GAP)) continue;
      levels.push(y);
    }
    if (levels.length === 0) levels.push(lo);
    levels.sort((p, q) => p - q);

    const names = DECK_NAMES[levels.length];
    for (let i = 0; i < levels.length; i++) {
      this.regions.push({
        key: `d${i}`,
        name: names[i],
        numeral: i + 1,
        y: levels[i],
        paint: 'trim',
        painted: false,
        crown: CROWN_SHAPES[i % CROWN_SHAPES.length],
      });
    }

    this.teamEnds = [
      { centre: centroid(this.arena.spawnsA, this.arena.centerSpawn), paint: 'teamA' },
      { centre: centroid(this.arena.spawnsB, this.arena.centerSpawn), paint: 'teamB' },
    ];
    // The deck the spawns stand on. Spawn Y is an eye/feet height just above the slab, so
    // regionAt resolves it to the storey a player actually starts the round on.
    const spawn = this.arena.spawnsA[0] ?? this.arena.centerSpawn;
    const fighting = this.regionAt(spawn.y - DECK_SNAP_UP);
    this.fightingDeckKey = fighting.key;

    /**
     * Two painted storeys, and never the one the round is played on.
     *
     * The old rule was a fixed five-slot pattern indexed by storey number, which put a plane
     * on whichever deck happened to land in slot 1 and 3 — including, on this arena, the
     * home roofs, where it argued with the team colours that own that deck. Derive it from
     * the fighting deck instead: the lowest storey that is not the fighting deck takes the
     * dark plane, the highest takes the bright one, and everything between stays white.
     *
     * That reads correctly on a rooftop for a reason beyond the colour budget: the bottom of
     * the complex is the shaded end and the top is the sunlit one, so the two planes agree
     * with the light instead of fighting it, and a callout naming a plane also names an
     * altitude. The fighting deck keeps its white, so the only saturated things on it are
     * the two team ends and a chambered gun.
     */
    const others = this.regions.filter((r) => r.key !== fighting.key);
    if (others.length >= 1) {
      const dark = others[0];
      dark.paint = 'zoneA';
      dark.painted = true;
    }
    if (others.length >= 2) {
      const bright = others[others.length - 1];
      bright.paint = 'zoneB';
      bright.painted = true;
    }
  }

  /**
   * SPEND THE SPLASHES. Three masses, chosen once, before anything is skinned.
   *
   * Playtest: *"it still feels a little muted, like there could be a little bit more
   * splashes of colour."* This is the whole of the answer, and the shape of it matters more
   * than the hues do — see the `LANDMARK_*` block for why the colour has to arrive as a
   * *count* rather than as a raised ceiling.
   *
   * Ranking, not rolling, and by **mirrored column** rather than by brush. Two things fall
   * out of that, and both are requirements rather than conveniences:
   *
   *  - The arena is mirrored across Z, and a competitive map has to stay symmetric. Painting
   *    one of a mirrored pair and not the other hands one team a landmark the other does not
   *    have, which is a real advantage and not a cosmetic one. A whole class wins or loses
   *    together.
   *  - Masses stacked in the same column are **one tower**, and a tower is one landmark. A
   *    clay base under a jade crown is two colours on one object, which is exactly the busy,
   *    accidental read this budget exists to prevent.
   *
   * The exclusions are all fairness or budget, never taste:
   *
   *  - **Nothing near a spawn** (`LANDMARK_TEAM_CLEARANCE`). Team colour has to be the only
   *    saturated thing at a team's end of the map, or "whose end is that" stops being
   *    answerable at a glance.
   *  - **Nothing thin and nothing short.** A landmark is a mass you navigate by. A tall thin
   *    slab is a billboard and a wide flat one is a deck; painting either produces a stripe
   *    rather than a block, which is the noise this budget exists to prevent.
   *  - **Nothing that does not stand on a deck.** See the base test below — ranked on area
   *    alone the winners are the building *shafts*, which are almost entirely under the
   *    player's feet.
   *  - **Nothing bigger than `LANDMARK_MAX_FACE_AREA`.** Past that a mass is the room you
   *    are in rather than a thing in it.
   *  - **Nothing see-through.** Glass and grating are honest about sightlines by being
   *    transparent, and paint would take that away.
   *
   * Deterministic without touching the rng, so both clients paint the same landmarks and a
   * callout like "the clay core" means the same thing to everybody.
   */
  private chooseLandmarkMasses(): void {
    interface Klass {
      key: string;
      area: number;
      brushes: Brush[];
      x: number;
      z: number;
    }
    const classes = new Map<string, Klass>();

    for (const b of this.arena.brushes) {
      if (b.decorative) continue;
      if (b.surface === SurfaceKind.Grate || b.surface === SurfaceKind.Glass) continue;
      if (b.half.y * 2 < LANDMARK_MIN_HEIGHT) continue;
      // Much wider than it is tall in *both* plan axes is a deck slab, not a mass.
      if (b.half.y < Math.min(b.half.x, b.half.z) * 0.28) continue;
      const area = Math.max(b.half.y * b.half.x, b.half.y * b.half.z) * 4;
      if (area < LANDMARK_MIN_FACE_AREA || area > LANDMARK_MAX_FACE_AREA) continue;
      /**
       * **It has to stand on a deck**, and this is the test that decides whether the
       * landmarks are landmarks at all.
       *
       * Ranked on area alone the winners are the building *shafts* — the fifty-metre tower
       * flanks that run from the kill plane up to the lowest deck. They are several times
       * the area of anything else on the map and they are almost entirely below the player:
       * painting them spends the whole splash budget on surfaces you only ever see by
       * leaning over a parapet, and leaves every mass you actually navigate by white. A
       * landmark is a thing you orient off while running, so it has to sit in the play
       * space rather than hang under it.
       */
      if (b.pos.y - b.half.y < this.lowestDeckY - DECK_SNAP_UP) continue;

      let nearSpawn = false;
      for (const end of this.teamEnds) {
        const dx = b.pos.x - end.centre.x;
        const dz = b.pos.z - end.centre.z;
        if (dx * dx + dz * dz < LANDMARK_TEAM_CLEARANCE * LANDMARK_TEAM_CLEARANCE) nearSpawn = true;
      }
      if (nearSpawn) continue;

      // Mirrored-column key: absolute plan position, rounded, so a brush, its mirror and
      // anything stacked above it all land in the same bucket on every client regardless of
      // the order the arena author declared them in.
      const r = (v: number): string => (Math.round(v * 10) / 10).toFixed(1);
      const key = `${r(Math.abs(b.pos.x))}:${r(Math.abs(b.pos.z))}`;
      const found = classes.get(key);
      if (found) {
        found.brushes.push(b);
        if (area > found.area) found.area = area;
      } else {
        classes.set(key, { key, area, brushes: [b], x: Math.abs(b.pos.x), z: Math.abs(b.pos.z) });
      }
    }

    const ranked = [...classes.values()].sort(
      (p, q) => q.area - p.area || p.key.localeCompare(q.key),
    );
    const taken: Klass[] = [];
    for (const k of ranked) {
      if (taken.length >= LANDMARK_MASSES) break;
      // Two landmarks stacked on the same footprint are one landmark said twice.
      if (taken.some((t) => Math.hypot(t.x - k.x, t.z - k.z) < ACCENT_MIN_SPACING * 0.5)) continue;
      const paint = LANDMARK_PAINTS[taken.length % LANDMARK_PAINTS.length];
      for (const b of k.brushes) this.landmarkBrushes.set(b, paint);
      taken.push(k);
    }
  }

  /**
   * Spend the colour budget, once, before a single brush is skinned.
   *
   * Ranking beats rolling. Every brush that could take paint is scored by its largest
   * exposed-facing plane, the biggest win, and each paint stops at `ACCENT_PLANES_PER_PAINT`
   * with `ACCENT_MIN_SPACING` between winners so a plane is never said twice in one glance.
   * The result is a fixed, knowable number of painted masses in the entire building —
   * "the dark wall" is a place, not a texture — and it is deterministic without needing
   * the rng at all, so both clients paint the same landmarks.
   */
  private chooseAccentBrushes(): void {
    const pools = new Map<MatKey, { b: Brush; area: number }[]>();
    for (const b of this.arena.brushes) {
      if (b.decorative) continue;
      if (b.surface === SurfaceKind.Grate || b.surface === SurfaceKind.Glass) continue;
      // A landmark has already spent real chroma on this mass. Laying an 8%-saturation
      // storey plane over it would waste the storey's one plane on a wall nobody would read
      // it on, and the landmark would win the pixel anyway.
      if (this.landmarkBrushes.has(b)) continue;
      const paint = this.paintFor(b, this.regionAt(b.pos.y - b.half.y));
      if (!paint.painted) continue;
      const area = Math.max(b.half.y * b.half.x, b.half.y * b.half.z) * 4;
      if (area < ACCENT_MIN_FACE_AREA) continue;
      let pool = pools.get(paint.paint);
      if (!pool) {
        pool = [];
        pools.set(paint.paint, pool);
      }
      pool.push({ b, area });
    }

    for (const [paint, pool] of pools) {
      const cap = paint === 'teamA' || paint === 'teamB' ? ACCENT_PLANES_PER_TEAM : ACCENT_PLANES_PER_PAINT;
      // Ties broken by position so the ordering cannot depend on brush insertion order.
      pool.sort((p, q) => q.area - p.area || p.b.pos.x - q.b.pos.x || p.b.pos.z - q.b.pos.z);
      const taken: Brush[] = [];
      for (const { b } of pool) {
        if (taken.length >= cap) break;
        const clash = taken.some((t) => {
          const dx = t.pos.x - b.pos.x;
          const dy = t.pos.y - b.pos.y;
          const dz = t.pos.z - b.pos.z;
          return dx * dx + dy * dy + dz * dz < ACCENT_MIN_SPACING * ACCENT_MIN_SPACING;
        });
        if (clash) continue;
        taken.push(b);
        this.accentBrushes.set(b, paint);
      }
    }
  }

  /** Deck level of the topmost storey. A slab at this height is the building's roof. */
  private get topDeckY(): number {
    const top = this.regions[this.regions.length - 1];
    return top ? top.y : this.boundsMax.y;
  }

  /** Deck level of the lowest storey — the floor of the play space, not of the bounding box. */
  private get lowestDeckY(): number {
    const low = this.regions[0];
    return low ? low.y : this.boundsMin.y;
  }

  /**
   * Deck level of the highest storey strictly below `y`, or the arena floor.
   *
   * The honest way to ask "how much air is under this ceiling". The height grid cannot
   * answer it — it stores the highest solid over each column, which over a deck is that
   * deck — so anything that needs a *downward* clearance asks the storey ladder instead.
   */
  private deckBelow(y: number): number {
    let best = this.oracle.floorY;
    for (const r of this.regions) {
      if (r.y < y - DECK_SNAP_UP && r.y > best) best = r.y;
    }
    return best;
  }

  /**
   * Is this brush's top face open to the sky?
   *
   * The single most important question in the file now, because it is what decides whether
   * a deck is a **roof** — membrane, ballast, parapets, rooflights, plant, sky — or an
   * interior floor. DESIGN.md 4 makes open sky the premise of the arena rather than a mood,
   * and interiors the rare exception, so the treatment has to be driven off the geometry
   * itself. That way this file adapts to whatever the arena author builds instead of
   * hard-coding a storey number, and a roof stays a roof if the level is retuned.
   *
   * Answered by `openFractionAbove`, which asks what fraction of the footprint has nothing
   * *built over* it — see there for why the obvious "is anything taller than this" test is
   * wrong. A roof with a lift overrun, a core tower or a parapet on it is still a roof; a
   * floor with a slab over most of it is not.
   */
  private openToSky(b: Brush, half: THREE.Vector3): boolean {
    const topY = b.pos.y + half.y;
    if (topY >= this.topDeckY - DECK_SNAP_UP) return true;
    return (
      this.oracle.openFractionAbove(
        b.pos.x - half.x,
        b.pos.z - half.z,
        b.pos.x + half.x,
        b.pos.z + half.z,
        topY + SKY_CLEARANCE,
      ) >= SKY_OPEN_FRACTION
    );
  }

  /** The storey a brush standing with its base at `y` belongs to. */
  private regionAt(y: number): Region {
    let best = this.regions[0];
    for (const r of this.regions) {
      if (r.y <= y + DECK_SNAP_UP) best = r;
    }
    return best;
  }

  /**
   * The paint a brush is entitled to: its storey's plane normally, its team's colour when it
   * stands at one end of the map. Team ends override the storey because "which end am I
   * looking at" is the more urgent question, and it is the one the team colours already answer
   * on the HUD.
   */
  private paintFor(b: Brush, region: Region): Paint {
    // Only on the fighting deck: the team colours mark the two *ends* of the storey the
    // round is played on. Bleeding them up into the gallery and down into the sump put
    // four hues into a single sightline and left the storeys unable to name themselves.
    if (region.key === this.fightingDeckKey) {
      for (const end of this.teamEnds) {
        const dx = b.pos.x - end.centre.x;
        const dz = b.pos.z - end.centre.z;
        if (dx * dx + dz * dz <= TEAM_END_RADIUS * TEAM_END_RADIUS) {
          return { paint: end.paint, team: true, painted: true };
        }
      }
    }
    return { paint: region.paint, team: false, painted: region.painted };
  }

  /** The width tier a mark on a plane of this area is allowed to take. */
  private signTier(area: number): number {
    for (let i = 0; i < SIGN_TIER_AREA.length; i++) {
      if (area < SIGN_TIER_AREA[i]) return SIGN_TIERS[i];
    }
    return SIGN_TIERS[SIGN_TIERS.length - 1];
  }

  // -- Materials -----------------------------------------------------------

  private material(key: MatKey): THREE.Material {
    const found = this.materials.get(key);
    if (found) return found;
    const m = this.createMaterial(key);
    m.name = `arena-${key}`;
    this.materials.set(key, m);
    return m;
  }

  private createMaterial(key: MatKey): THREE.Material {
    switch (key) {
      // -- Neutral structure. Read down this list and it is a value ramp, not a palette:
      //    soffit (brightest) -> wall -> floor -> dark floor -> machined metal -> slate.
      //    That ramp is what stops a white room reading as fog. Every tone is derived from
      //    PALETTE, so an art-direction change upstream repaints the whole arena.
      case 'panel':
        // Soffits and applied plates. The brightest surface in the building, and warmed a
        // third of the way toward sunlight — see SUNLIT_WHITE_BIAS.
        return makeToonMaterial({ color: mix(PALETTE.base, PALETTE.sun, SUNLIT_WHITE_BIAS), rim: 0.32 });
      case 'concrete':
        return makeToonMaterial({ color: mix(PALETTE.base2, PALETTE.sun, SUNLIT_CONCRETE_BIAS), rim: 0.24 });
      case 'concreteDark':
        return makeToonMaterial({ color: mix(PALETTE.base2, PALETTE.shadow, 0.26), rim: 0.2 });
      case 'floor': {
        // Polished terrazzo. toon.ts already sits every upward face a full rung below the
        // walls (floors 30-38% display grey against walls at 54-83%), so the albedo does
        // not have to do that job a second time — and when it tried, at 0.22 toward
        // shadow, the result was the grey asphalt deck the playtest saw instead of the
        // pale sunlit terrazzo the art direction asks for. Barely tinted, and *polished*:
        // the flat specular below is what makes a floor read as a hard shiny plane rather
        // than as a matte one.
        const m = makeToonMaterial({ color: mix(PALETTE.base2, PALETTE.shadow, 0.1), rim: 0.5 });
        setToonSpecular(m, 0.22);
        return m;
      }
      case 'floorDark': {
        // The lower storeys, where less sky reaches the deck.
        const m = makeToonMaterial({ color: mix(PALETTE.base2, PALETTE.shadow, 0.3), rim: 0.42 });
        setToonSpecular(m, 0.14);
        return m;
      }
      case 'metal': {
        // Brushed aluminium. DESIGN.md 4 asks for a crisp specular *shape* on metal, and
        // this is the material every rail, mullion, duct and skylight frame is made of.
        const m = makeToonMaterial({ color: mix(PALETTE.base2, PALETTE.shadow, 0.26), rim: 0.45 });
        setToonSpecular(m, 0.4);
        return m;
      }
      case 'metalDark':
        return makeToonMaterial({ color: PALETTE.shadow, rim: 0.3 });
      case 'trim':
        return makeToonMaterial({ color: PALETTE.ink, rim: 0.22 });
      case 'rubber':
        return makeToonMaterial({ color: mix(PALETTE.ink, PALETTE.shadow, 0.25), rim: 0.12 });

      // -- THE ROOF. Two surfaces, one value apart, and a painted steel for everything
      //    bolted to them. This is the material the player spends the entire match looking
      //    at, so it is where the "bright and clean, but a real place" line gets drawn.
      case 'membrane': {
        const tex = P.makeMembraneTexture();
        this.textures.push(tex);
        // Matte: a roof sheet is the one large surface in the building with no specular
        // shape at all, and that is what separates it from glass, metal and terrazzo.
        return new THREE.MeshToonMaterial({ map: tex, gradientMap: this.gradient });
      }
      case 'gravel': {
        const tex = P.makeGravelTexture();
        this.textures.push(tex);
        return new THREE.MeshToonMaterial({ map: tex, gradientMap: this.gradient });
      }
      case 'rail': {
        /**
         * Painted steel: handrails, walkway kerbs, davit frames, banner masts.
         *
         * Graphite, and darker than any architectural surface, which is the whole point.
         * Every one of these objects now lives with open sky behind it, and a rail the
         * value of the wall it stands on is a rail nobody sees. A hard dark line against
         * a bright sky is the most legible thing this arena can draw, and it costs no hue.
         */
        const m = makeToonMaterial({ color: mix(PALETTE.ink, PALETTE.shadow, 0.42), rim: 0.4 });
        setToonSpecular(m, 0.3);
        return m;
      }

      // -- The soft materials. Small in area, large in what they say about the place.
      case 'wood':
        return makeToonMaterial({ color: recolor(PALETTE.loaded, WOOD_SATURATION, WOOD_LIGHTNESS), rim: 0.2 });
      case 'foliage': {
        _mixA.setHSL(FOLIAGE_HUE, FOLIAGE_SATURATION, FOLIAGE_LIGHTNESS, THREE.SRGBColorSpace);
        return makeToonMaterial({ color: _mixA.getHex(THREE.SRGBColorSpace), rim: 0.5 });
      }
      case 'foliageLight': {
        // The sunlit face of every hedge, bed and tree in the arena. See FOLIAGE_* — a toon
        // ramp bands a clipped box's top and its sides identically, so the light has to be
        // painted in or planting reads as one flat dark mass whatever the sun is doing.
        _mixA.setHSL(
          FOLIAGE_LIGHT_HUE,
          FOLIAGE_LIGHT_SATURATION,
          FOLIAGE_LIGHT_LIGHTNESS,
          THREE.SRGBColorSpace,
        );
        return makeToonMaterial({ color: _mixA.getHex(THREE.SRGBColorSpace), rim: 0.55 });
      }

      // -- THE LANDMARK PAINTS. Three masses in the arena carry these and nothing else in
      //    the world does. Real chroma, on architecture, at monumental scale — see the
      //    LANDMARK_* block above for why these two hues and not red, blue or yellow.
      case 'accentJade':
        return makeToonMaterial({
          color: recolor(PALETTE.accentA, LANDMARK_JADE_SATURATION, LANDMARK_JADE_LIGHTNESS),
          rim: 0.34,
        });
      case 'accentClay':
        return makeToonMaterial({
          // Terracotta, derived halfway between signal red and the gun's amber and then
          // re-landed at a value both of them clear. Anchored to PALETTE rather than typed
          // in, so an art-direction change upstream still repaints it.
          color: recolor(
            mix(PALETTE.teamB, PALETTE.loaded, 0.5),
            LANDMARK_CLAY_SATURATION,
            LANDMARK_CLAY_LIGHTNESS,
          ),
          rim: 0.32,
        });
      case 'accentSun':
        // Machinery only: cranes, hoists, windsocks. Never a plane of architecture.
        return makeToonMaterial({
          color: recolor(PALETTE.accentB, LANDMARK_SUN_SATURATION, LANDMARK_SUN_LIGHTNESS),
          rim: 0.36,
        });

      // -- Fabric and water. Neither is a colour event; both are what makes the roof read
      //    as a place somebody spends a nice afternoon.
      case 'awning': {
        const tex = P.makeAwningTexture(
          recolor(mix(PALETTE.teamB, PALETTE.loaded, 0.5), LANDMARK_CLAY_SATURATION, LANDMARK_CLAY_LIGHTNESS),
        );
        this.textures.push(tex);
        return new THREE.MeshToonMaterial({
          map: tex,
          gradientMap: this.gradient,
          // Seen from below as often as from above, and it is a single sheet.
          side: THREE.DoubleSide,
        });
      }
      case 'canvas':
        /**
         * Shade-sail canvas. Translucent, and that is a fairness requirement rather than a
         * look: collision has nothing up there, so an opaque sheet stretched over open deck
         * would hide a player the simulation says is in the open. Bright and warm, so it
         * reads as sunlight coming through fabric.
         */
        return new THREE.MeshBasicMaterial({
          color: mix(PALETTE.sun, PALETTE.base, 0.25),
          transparent: true,
          opacity: 0.34,
          depthWrite: false,
          side: THREE.DoubleSide,
          toneMapped: false,
        });
      case 'water': {
        // Still water: pale, barely tinted, and hard-specular. Its interest is the sky
        // highlight sliding across it, not its hue — which is why it is one of the few
        // things that can be added to a strict colour budget for free.
        // The hottest specular in the arena, and by some way: glass sits at 0, brushed
        // aluminium at 0.4 and polished terrazzo at 0.22. Water earns it — a still pool is
        // a mirror, and the sun's reflection sliding across it is the entire reason it is
        // worth building. Toon materials write alpha 1, so this never reaches the bloom
        // pyramid; the chambered gun stays the only thing in the world that burns.
        const m = makeToonMaterial({ color: mix(PALETTE.sky, PALETTE.base, 0.42), rim: 0.6 });
        setToonSpecular(m, 0.7);
        return m;
      }

      // -- The two storey planes. Matte, never emissive, and chroma-capped: they carry
      //    the accent hue's *identity* at 8% saturation, and do their real work by
      //    sitting a long way off the neutral group in value. `zoneA` reads as the dark
      //    deck, `zoneB` as the bright one, from any distance and in greyscale.
      case 'zoneA':
        return makeToonMaterial({ color: zoneTone(PALETTE.accentA, ZONE_A_LIGHTNESS), rim: 0.3 });
      case 'zoneB':
        return makeToonMaterial({ color: zoneTone(PALETTE.accentB, ZONE_B_LIGHTNESS), rim: 0.35 });

      // -- Team WALLS: pale tinted plaster, in the wall band. A player standing in front of
      //    one has to keep their silhouette. See TEAM_WALL_SATURATION.
      case 'teamA':
        return makeToonMaterial({ color: teamWallTone(PALETTE.teamA), rim: 0.32 });
      case 'teamB':
        return makeToonMaterial({ color: teamWallTone(PALETTE.teamB), rim: 0.32 });

      // -- Team ACCENT: the full-chroma hue, on the skirting band and the spawn pad only.
      //    Perhaps two square metres of it per team, in an arena of thousands.
      case 'teamBandA':
        return makeToonMaterial({ color: PALETTE.teamA, rim: 0.3 });
      case 'teamBandB':
        return makeToonMaterial({ color: PALETTE.teamB, rim: 0.3 });

      // -- There is no emissive case here any more, and that is the point. See the MatKey
      //    union in props.ts: nothing in this arena glows, so a chambered gun is the only
      //    thing in the world that does.

      // -- textured; these need a map, which the shared factory does not expose
      case 'hazard': {
        const tex = P.makeHazardTexture();
        this.textures.push(tex);
        return new THREE.MeshToonMaterial({
          map: tex,
          gradientMap: this.gradient,
          polygonOffset: true,
          polygonOffsetFactor: -2,
          polygonOffsetUnits: -2,
        });
      }
      case 'warn': {
        // Rigging hardware only — the davit jib noses and nothing else. See makeWarnTexture
        // for why this is allowed to be warm when the ledge striping is not.
        const tex = P.makeWarnTexture();
        this.textures.push(tex);
        return new THREE.MeshToonMaterial({
          map: tex,
          gradientMap: this.gradient,
          polygonOffset: true,
          polygonOffsetFactor: -2,
          polygonOffsetUnits: -2,
        });
      }
      case 'grate': {
        const tex = P.makeGrateTexture();
        this.textures.push(tex);
        return new THREE.MeshToonMaterial({
          map: tex,
          gradientMap: this.gradient,
          color: 0xb9c2d6,
          // Alpha-tested, never blended: a grate must not participate in transparency
          // sorting, or it would occlude or reveal players depending on draw order.
          alphaTest: 0.5,
          transparent: false,
          side: THREE.DoubleSide,
        });
      }
      case 'glass':
        return new THREE.MeshBasicMaterial({
          // Chroma-capped like everything else the teams do not own. Curtain walling read
          // as turquoise at full sky chroma, which is a third hue nobody asked for; a pale
          // cool grey still reads unmistakably as glass because glass is defined by its
          // *specular shape* and its transparency, not by being blue.
          color: zoneTone(PALETTE.sky, GLASS_LIGHTNESS),
          transparent: true,
          opacity: 0.26,
          depthWrite: false,
          side: THREE.DoubleSide,
          toneMapped: false,
        });

      /**
       * Skylight glazing.
       *
       * Unlit, so it holds its value no matter which way it faces — which is the entire
       * point. toon.ts puts every downward face at 21-24% display grey, so a roof plane is
       * the darkest thing in the frame by construction, and the prior review's verdict was
       * that it "reads as a HOLE in the render, not a roof". A hole is what you get when a
       * plane has no structure and no light source in it. Coffer ribs give it structure
       * (`P.soffitCoffers`); these give it light. A daylight opening overhead is also the
       * strongest possible statement that it is the middle of a sunny day, which is the
       * one thing the playtest said this arena was not saying.
       *
       * Bright but **not** a bloom source: a MeshBasicMaterial writes alpha 1, and the post
       * chain reads alpha as `1 - bloomMask`, so this is excluded from the bloom pyramid
       * automatically. The gun stays the only thing in the arena that burns.
       */
      case 'skylight':
        return new THREE.MeshBasicMaterial({
          color: mix(PALETTE.sun, PALETTE.base, 0.2),
          fog: false,
          toneMapped: false,
        });

      // AERIAL PERSPECTIVE, in three steps.
      //
      // Distant buildings wash toward the sky rather than silhouetting black, which is what
      // sells depth under a bright sky. Two tones made a skyline; three make a *city*, and
      // the one that was missing is this first one — the ring of roofs close enough that
      // the player is looking down onto them. It is barely hazed at all, so it holds a
      // hard edge against the paler city behind it, and that edge is the depth cue.
      case 'skylineNear':
        return new THREE.MeshBasicMaterial({
          color: mix(PALETTE.base, PALETTE.sky, 0.14),
          fog: false,
          toneMapped: false,
        });
      case 'skyline':
        return new THREE.MeshBasicMaterial({
          color: mix(PALETTE.base, PALETTE.sky, 0.42),
          fog: false,
          toneMapped: false,
        });
      case 'skylineFar':
        return new THREE.MeshBasicMaterial({
          color: mix(PALETTE.sky, PALETTE.base, 0.3),
          fog: false,
          toneMapped: false,
        });
      case 'skylineGlass': {
        const tex = P.makeGlazingTexture(this.random);
        this.textures.push(tex);
        return new THREE.MeshBasicMaterial({
          map: tex,
          transparent: true,
          opacity: 0.55,
          alphaTest: 0.05,
          depthWrite: false,
          fog: false,
          toneMapped: false,
        });
      }
      case 'void': {
        /**
         * The ground, a very long way down.
         *
         * It used to be an ink grid at 45% — a dark glowing floor under a dark interior.
         * Under an open sky that is wrong twice over: the bottom of a real city canyon
         * seen from a roof is not darker than the sky, it is *hazier*, washed out by the
         * same aerial perspective that pales the horizon. So the grid is now a faint
         * shadow-grey street pattern, further faded, on nothing. Falling still reads as
         * falling — the grid gives the eye a scale to measure the drop against — but the
         * frame no longer has a dark hole in the bottom of it.
         */
        const tex = P.makeVoidTexture();
        this.textures.push(tex);
        return new THREE.MeshBasicMaterial({
          map: tex,
          color: mix(PALETTE.shadow, PALETTE.sky, 0.35),
          transparent: true,
          opacity: 0.3,
          depthWrite: false,
          side: THREE.DoubleSide,
          fog: false,
          toneMapped: false,
        });
      }
      default:
        return makeToonMaterial({ color: PALETTE.base });
    }
  }

  /**
   * THE GRAPPLE ANCHOR MATERIAL.
   *
   * Playtest, verbatim: *"I can't really figure out how to get the grapple to work. I
   * tried using it, but it just didn't do anything."* Anchors were in range the whole
   * time. The player could not see them. A traversal verb nobody can find is not a verb.
   *
   * The previous fix made them **glow neon jade**, and it worked in an enclosed interior
   * for the wrong reason: everything else was dark. On a sunlit roof it fails twice. A
   * glow cannot beat an open sky — you cannot add light to something already near white —
   * and a pulsing green halo in an otherwise restrained world reads as a *powerup*, which
   * is a lie about what the object does. So the fixture is now believable rooftop rigging,
   * and it is found by the three things that actually survive a bright background:
   *
   *  1. **Silhouette.** A gantry beam with a stay truss over it and a hook block hanging
   *     under it. A long horizontal, a triangle and a pendant — nothing in a roofscape of
   *     decks, parapets and cores has that outline, so recognition never needs detail.
   *  2. **Both values, touching.** A graphite frame carrying white-painted end blocks.
   *     Against sky the graphite reads; against white concrete the paint reads. There is
   *     no background in this arena that swallows both at once, which is the property the
   *     old "dark machined object" proposal lacked and the old glow lacked in reverse.
   *  3. **A high-frequency warm band on each beam nose.** Alternating safety stripes at a
   *     20 cm pitch: a pattern survives to a distance where a flat tone has washed out.
   *     Matte, and it never touches the bloom mask — so it cannot be confused with the one
   *     emissive amber in the game, which is a chambered gun.
   *
   * Roles are packed into `uv.y` — 0 graphite frame, 1 white paint, 2 safety band — and a
   * per-anchor phase into `uv.x`, so the fixtures breathe fractionally out of sync and the
   * eye is caught by movement rather than having to sweep. The breath is a 12% swing on a
   * matte band, not a glow: enough to catch a glance, not enough to be a light.
   */
  private anchorMat(): THREE.ShaderMaterial {
    if (this.anchorMaterial) return this.anchorMaterial;
    const m = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uFrame: {
          value: new THREE.Color().setHex(mix(PALETTE.ink, PALETTE.shadow, 0.22), THREE.SRGBColorSpace),
        },
        uPaint: { value: new THREE.Color().setHex(PALETTE.base, THREE.SRGBColorSpace) },
        uWarn: { value: new THREE.Color().setHex(PALETTE.accentB, THREE.SRGBColorSpace) },
      },
      vertexShader: /* glsl */ `
        varying float vRole;
        varying float vPhase;
        varying vec3 vNormalW;
        void main() {
          vRole = uv.y;
          vPhase = uv.x;
          vNormalW = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uFrame;
        uniform vec3 uPaint;
        uniform vec3 uWarn;
        varying float vRole;
        varying float vPhase;
        varying vec3 vNormalW;
        void main() {
          // Three hard bands, matching the arena's toon ramp, so a davit shades like the
          // building it is bolted to rather than like a UI element floating in front of it.
          float up = clamp(vNormalW.y * 0.5 + 0.5, 0.0, 1.0);
          float band = up < 0.34 ? 0.62 : (up < 0.68 ? 0.82 : 1.0);
          vec3 c;
          if (vRole < 0.5) {
            c = uFrame * band;
          } else if (vRole < 1.5) {
            c = uPaint * band;
          } else {
            // Slow enough to read as "idling", fast enough that a glance catches it.
            float pulse = 0.5 + 0.5 * sin(uTime * 2.0 + vPhase * 6.28318);
            c = uWarn * band * (0.94 + 0.12 * pulse);
          }
          // Alpha carries ( 1 - bloomMask ). Nothing here opts in: the whole point is that
          // an anchor is a matte object found by shape and value, and the only thing in
          // this game allowed to burn is a chambered gun.
          gl_FragColor = vec4(c, 1.0);
          #include <colorspace_fragment>
        }
      `,
      toneMapped: false,
    });
    this.anchorMaterial = m;
    return m;
  }

  /**
   * The anchor halo: one small camera-facing additive ring per anchor, on the same clock
   * as the fixture.
   *
   * Deliberately reduced to a supporting role. It used to be a 2.9 m pulsing jade annulus
   * and it was the single thing that made an anchor read as a collectible. Against an open
   * sky an additive ring contributes almost nothing anyway; where it still earns its keep
   * is the other half of the frame — an anchor seen against the shaded flank of a tower,
   * or against the city far below — so it survives at half the radius, a third of the
   * intensity and in a warm tone that matches the fixture's own safety band rather than
   * introducing a hue of its own.
   *
   * It is depth-tested like everything else. An anchor behind a wall is not visible through
   * it, because a marker that ignores geometry teaches players to trust a thing that lies.
   */
  private anchorHaloMat(): THREE.ShaderMaterial {
    if (this.haloMaterial) return this.haloMaterial;
    const m = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: {
          value: new THREE.Color().setHex(mix(PALETTE.accentB, PALETTE.neutral, 0.4), THREE.SRGBColorSpace),
        },
        uRadius: { value: ANCHOR_HALO_RADIUS },
      },
      vertexShader: /* glsl */ `
        attribute vec3 aCenter;
        uniform float uTime;
        uniform float uRadius;
        varying vec2 vLocal;
        varying float vPulse;
        void main() {
          float pulse = 0.5 + 0.5 * sin(uTime * 2.2 + uv.x * 6.28318);
          vPulse = pulse;
          // The quad is authored in the XY plane about aCenter; billboard it by adding the
          // local offset to the anchor's own view-space position.
          vec3 local = position - aCenter;
          vLocal = local.xy / uRadius;
          vec4 mv = modelViewMatrix * vec4(aCenter, 1.0);
          mv.xy += local.xy * (0.94 + 0.14 * pulse);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        varying vec2 vLocal;
        varying float vPulse;
        void main() {
          float r = length(vLocal);
          // A thin annulus and nothing else. The fill went with the size: a filled disc is
          // what made this read as a pickup aura rather than as a marker on a fixture.
          float ring = exp(-pow((r - 0.62) / 0.13, 2.0));
          float a = ring * (0.16 + 0.2 * vPulse) * (1.0 - smoothstep(0.88, 1.0, r));
          if (a < 0.004) discard;
          gl_FragColor = vec4(uColor, a);
          #include <colorspace_fragment>
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
      fog: false,
    });
    this.haloMaterial = m;
    return m;
  }

  /**
   * Inverted-hull ink outline for the merged arena batches.
   *
   * `attachOutline` from @client/render/toon is the right tool for a self-contained prop
   * — the grapple anchors use it — but it welds hull normals across whatever geometry it
   * is handed, and the arena is handed to it as a few hundred brushes fused into one
   * buffer. At the T-junctions where brushes meet, the welded normal is ill-defined and
   * the miter correction blows the shell into black spikes across the wall. Per-face
   * extrusion has no such failure mode on box soup, whose hard edges are already split.
   *
   * The width matches toon.ts exactly: OUTLINE_THICKNESS is authored as a hull offset in
   * metres at OUTLINE_REF_DISTANCE, converted once to a fixed CSS-pixel weight, and then
   * held constant at every distance by scaling the clip-space offset by w.
   */
  private outlineMat(): THREE.ShaderMaterial {
    if (this.outlineMaterial) return this.outlineMaterial;
    const m = new THREE.ShaderMaterial({
      uniforms: {
        uPixels: { value: OUTLINE_PIXELS },
        uViewportHeight: { value: 1080 },
        uColor: { value: new THREE.Color().setHex(PALETTE.ink, THREE.SRGBColorSpace) },
      },
      vertexShader: /* glsl */ `
        uniform float uPixels;
        uniform float uViewportHeight;
        void main() {
          vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          vec3 nView = normalize(normalMatrix * normal);
          vec2 dir = (projectionMatrix * vec4(nView, 0.0)).xy;
          float l = length(dir);
          if (l > 1e-5) {
            dir /= l;
            // NDC spans 2 units over the viewport height, so one pixel is 2/height.
            float ndc = uPixels * 2.0 / uViewportHeight;
            dir.x /= projectionMatrix[1][1] / projectionMatrix[0][0];
            clip.xy += dir * ndc * clip.w;
          }
          gl_Position = clip;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        void main() {
          gl_FragColor = vec4(uColor, 1.0);
          #include <colorspace_fragment>
        }
      `,
      side: THREE.BackSide,
      toneMapped: false,
      fog: false,
    });
    this.outlineMaterial = m;
    return m;
  }

  // -- Brushes -------------------------------------------------------------

  /** Which faces of a brush are open to the world (5 samples each, cheap and reliable). */
  private exposedFaces(xf: THREE.Matrix4, half: THREE.Vector3): boolean[] {
    const out: boolean[] = [];
    const faces = P.boxFaces(half);
    for (const f of faces) {
      let open = false;
      const samples: [number, number][] = [
        [0, 0],
        [-f.w * 0.32, -f.h * 0.32],
        [f.w * 0.32, -f.h * 0.32],
        [-f.w * 0.32, f.h * 0.32],
        [f.w * 0.32, f.h * 0.32],
      ];
      for (const [du, dv] of samples) {
        P.facePoint(f, du, dv, _p1, 0.08).applyMatrix4(xf);
        if (!this.oracle.contains(_p1)) {
          open = true;
          break;
        }
      }
      out.push(open);
    }
    return out;
  }

  /**
   * Choose the skin for a brush.
   *
   * The default is white architecture — `panel` (the brightest tone) or `concrete` — and
   * that default covers the overwhelming majority of the building. Paint only reaches a
   * brush that won a slot in the colour budget (`chooseAccentBrushes`), which is a hard
   * cap of four masses per storey plane and two per team for the whole arena. Everything
   * else separates by value, which costs nothing and never competes with the gun.
   */
  private skinOptsFor(b: Brush, exposed: boolean[]): SkinOpts {
    let base: MatKey;
    if (b.surface === SurfaceKind.Metal) {
      base = 'metal';
    } else {
      // Weighted hard toward white. Mirror's Edge is a *white* building with a handful of
      // grey panels in it, not a grey building; the previous 42/40/18 split put nearly a
      // fifth of the arena on the darkest concrete and read as overcast. The variation
      // that matters here is between the tones the light makes, not between the albedos.
      const r = this.random();
      base = r < 0.56 ? 'panel' : r < 0.9 ? 'concrete' : 'concreteDark';
    }

    const budgeted = this.accentBrushes.get(b);
    if (budgeted) base = budgeted;
    // A landmark outranks a storey plane: it is a bigger statement, and it was chosen first.
    const landmark = this.landmarkBrushes.get(b);
    if (landmark) base = landmark;

    return {
      base,
      // A painted mass takes WHITE inner panels rather than the usual value step. On a
      // landmark the panel grid is the only thing keeping a monumental face from reading as
      // a swatch, and it has to be the architecture's own white to do that.
      inner: landmark ? 'panel' : base === 'panel' ? 'concrete' : 'panel',
      // Trim is machined metal, never a hue. Piping in a region colour was exactly the
      // "rainbow" failure: colour spent on things that carry no information.
      accent: 'metal',
      exposed,
      yaw: b.yaw,
    };
  }

  /**
   * May a decorative plate be drawn opaque?
   *
   * Collision skips these brushes entirely, so an opaque one occludes a sightline that
   * nothing in the simulated world occludes: a player stands behind it, sees nothing, and
   * is shot through it. That is the worst thing decoration can do in a shooter, and it is
   * worth giving up a lot of visual punch to be rid of.
   *
   * Two ways to earn it. Either the plate is *too low to hide anything* — a 14 cm kerb
   * painted along a deck lip — or its whole footprint is backed by collision geometry, in
   * which case it hides only what the wall it is bolted to already hides. The probe samples
   * a 5x5 grid a little way behind each large face and wants `SIGN_MOUNT_COVERAGE` of them
   * inside a solid; anything that cantilevers meaningfully into open air fails and is
   * rendered as a scrim instead.
   */
  private plateMayBeOpaque(xf: THREE.Matrix4, half: THREE.Vector3): boolean {
    const thin = Math.min(half.x, half.y, half.z);
    const axis: 'x' | 'y' | 'z' = thin === half.x ? 'x' : thin === half.y ? 'y' : 'z';
    if (axis === 'y' && half.y * 2 <= SIGN_FLAT_SAFE_HEIGHT) return true;

    const n = new THREE.Vector3(axis === 'x' ? 1 : 0, axis === 'y' ? 1 : 0, axis === 'z' ? 1 : 0);
    const uAxis = axis === 'x' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const vAxis = axis === 'z' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    const uHalf = axis === 'x' ? half.y : half.x;
    const vHalf = axis === 'z' ? half.y : half.z;
    const grid = [-0.9, -0.45, 0, 0.45, 0.9];
    const need = Math.ceil(grid.length * grid.length * SIGN_MOUNT_COVERAGE);

    for (const side of [1, -1]) {
      let solid = 0;
      for (const du of grid) {
        for (const dv of grid) {
          _p1
            .set(0, 0, 0)
            .addScaledVector(n, side * (thin + SIGN_MOUNT_PROBE))
            .addScaledVector(uAxis, du * uHalf)
            .addScaledVector(vAxis, dv * vHalf)
            .applyMatrix4(xf);
          if (this.oracle.contains(_p1)) solid++;
        }
      }
      if (solid >= need) return true;
    }
    return false;
  }

  /**
   * Does a decorative plate's face read as a *board*, or as a length of edge trim?
   *
   * Paint lands on boards. Strips — deck lip rims, pylon bands, capping runs — get the same
   * neutral pale-kerb-over-slate-reveal that every other edge in the building gets. This
   * one test is what finally killed the coloured piping: a painted line running the whole
   * gallery lip is hundreds of linear metres of colour that says nothing a painted *wall*
   * does not say better, and it was the largest single source of stray colour in the frame.
   */
  private plateIsBoard(half: THREE.Vector3): boolean {
    const dims = [half.x, half.y, half.z].sort((p, q) => p - q);
    const short = dims[1] * 2;
    const long = dims[2] * 2;
    return short >= SIGN_MIN_BOARD_DIM && long / short <= SIGN_MAX_BOARD_ASPECT;
  }

  /**
   * A decorative brush is invisible to collision, so its treatment has to make that
   * obvious. Three honest answers, chosen by what the brush actually is:
   *
   *  - **Mounted plates** (wall signage, the team-end boards): opaque painted panels. They
   *    are the arena's wayfinding — a single flat plane that names where you are — and
   *    they occlude nothing the wall they are bolted to does not occlude.
   *  - **Hung plates** (banners over the atrium): a framed translucent scrim. Same flat
   *    field, same stencilled name, but you can see a player through it.
   *  - **Service runs and everything else**: open pipework, grating or glass. See-through
   *    by construction, so none of it can block a sightline that collision does not.
   *
   * This is where the old build spent most of its neon. A lip strip glowing cyan told the
   * player nothing that a painted band does not, and it cost the gun its silhouette.
   */
  private buildDecorative(b: Brush, xf: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
    const thinnest = Math.min(half.x, half.y, half.z);
    if (b.surface === SurfaceKind.Grate) {
      P.grateBlock(this, xf, half, o);
      return;
    }
    if (b.surface === SurfaceKind.Metal) {
      // Service runs: pipes, ribs and roof trusses. Always unbacked — collision skips the
      // volume, so the render must not fill it with an opaque bar.
      P.pipeBundle(this, xf, half, o, false);
      return;
    }
    if (b.surface === SurfaceKind.Neon && thinnest <= SIGN_MAX_THICKNESS) {
      if (this.plateMayBeOpaque(xf, half)) P.signPanel(this, xf, half, o);
      else P.scrimPanel(this, xf, half, o);
      return;
    }
    P.glassBlock(this, xf, half, o);
  }

  /**
   * Stencil a signage board.
   *
   * A blank plate is a swatch; a plate with the deck's name cut into it is a sign. Two
   * marks only, and which one you get is decided by how big the board is, never rolled:
   *
   *  - **Big board → the name.** The storey you are on, or the team whose end you are
   *    looking at. It is a landmark and it says something true.
   *  - **Small board → the storey number.** The same numeral the walls carry, so a player
   *    sees one mark in three sizes across the whole building rather than a vocabulary.
   *
   * The old rule gave small boards a word off a list — `NO ENTRY`, `KEEP CLEAR` — which
   * is worse than it looks: nowhere in this arena is off limits, so the sign was a lie,
   * and a lying sign teaches a player to stop reading signs.
   *
   * The tint is picked for contrast against the plate, never for decoration: white cut out
   * of a dark plane, ink on a bright one. A stencil the same value as its board is not a
   * sign either. Both board faces are painted, because a banner hung over an atrium is
   * read from whichever side you happen to fall past.
   */
  private signageLabel(
    b: Brush,
    xf: THREE.Matrix4,
    half: THREE.Vector3,
    region: Region,
    paint: Paint,
    accent: MatKey,
  ): void {
    const faces = P.boxFaces(half);
    let bestArea = 0;
    for (const f of faces) bestArea = Math.max(bestArea, f.w * f.h);
    if (bestArea < SIGN_MIN_FACE_AREA) return;

    const named = bestArea >= SIGN_NAME_MIN_AREA;
    const cell = named ? this.atlas.label(paint.team ? this.teamLabel(paint.paint) : region.name) : null;
    if (named && !cell) return;
    // A team's name is the one mark allowed to be saturated, because it is the only thing
    // in the building whose job IS the team colour. On a pale team board that is also the
    // only tint with enough contrast to read; ink would say nothing about whose end it is.
    const color = paint.team ? (TEAM_PAINT_COLORS[paint.paint] ?? PALETTE.ink) : stencilOn(accent);

    for (const f of faces) {
      // Only the two board faces — the ones whose normal is the plate's thin axis.
      if (f.w * f.h < bestArea - 1e-3) continue;
      // Centred on the board. The old placement nudged the name 6% down to leave room for
      // registration ticks that no longer exist, and an off-centre stencil on a rectangular
      // plate is the single most reliable way to look unplanned.
      const pos = P.facePoint(f, 0, 0, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf);
      const normal = f.n.clone().transformDirection(xf);
      const rotation = P.faceDecalRoll(f, b.yaw);
      if (cell) {
        this.decal({
          cell,
          pos,
          normal,
          width: clamp(Math.min(f.w * 0.72, f.h * 3.4), 0.8, 16),
          color,
          strength: 1,
          rotation,
        });
      } else {
        this.decalKind('numeral', {
          pos,
          normal,
          width: Math.min(this.signTier(f.w * f.h), f.w * SIGN_MAX_FACE_FRACTION, f.h * SIGN_MAX_FACE_FRACTION),
          color,
          strength: P.SIGNAGE_STRENGTH,
          rotation,
          variant: region.numeral,
        });
      }
    }
  }

  private buildBrush(b: Brush): void {
    const half = vec(b.half);
    if (half.x <= 0 || half.y <= 0 || half.z <= 0) return;
    const xf = brushMatrix(b);
    const region = this.regionAt(b.pos.y - half.y);
    const exposed = b.decorative ? [true, true, true, true, true, true] : this.exposedFaces(xf, half);
    const o = this.skinOptsFor(b, exposed);

    if (b.decorative) {
      // Only a brush the arena author marked as signage, and thin enough to be a plate,
      // is treated as one. A pipe run and a glass canopy are decorative too, and painting
      // a storey name across a truss puts a stencil in mid-air between the pipes.
      const isPlate = b.surface === SurfaceKind.Neon && Math.min(half.x, half.y, half.z) <= SIGN_MAX_THICKNESS;
      // A signage *board* takes the region's paint across its whole face — that IS the
      // information. A signage *strip* does not: see `plateIsBoard`.
      const paint = this.paintFor(b, region);
      const board = isPlate && this.plateIsBoard(half);
      const accent: MatKey = board && paint.painted ? paint.paint : 'panel';
      this.buildDecorative(b, xf, half, { ...o, accent });
      if (board) this.signageLabel(b, xf, half, region, paint, accent);
      return;
    }

    switch (b.surface) {
      case SurfaceKind.Grate:
        P.grateBlock(this, xf, half, o);
        break;
      case SurfaceKind.Glass:
        P.glassBlock(this, xf, half, o);
        break;
      case SurfaceKind.Neon: {
        // A solid brush the arena author marked as signage. It gets the region's paint on
        // its whole face — a plane of colour that names where you are, not a light source.
        const paint = this.paintFor(b, region);
        P.signPanel(this, xf, half, { ...o, accent: paint.painted ? paint.paint : 'panel' });
        break;
      }
      case SurfaceKind.Metal:
        this.buildMetalBrush(xf, half, o);
        break;
      default:
        this.buildConcreteBrush(xf, half, o);
        break;
    }

    if (!surfaceIsSeeThrough(b.surface)) {
      this.dressBrush(b, xf, half, region, o);
    }
  }

  /**
   * ROOFTOP VOCABULARY, not interior vocabulary.
   *
   * What a brush is skinned as says what kind of place this is, and the old routing said
   * "interior" out loud: tall slim metal became a rack of relay units, and on this arena the
   * tall slim metal brushes are the antenna masts — including the 52 m one on the helipad,
   * the tallest object in the level and the thing every player orients off. Metal on a roof
   * is a mast, a duct run, a plant unit or a tank, and that is now the whole list.
   */
  private buildMetalBrush(xf: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
    const vol = half.x * half.y * half.z * 8;
    const wide = Math.max(half.x, half.z);
    const tall = half.y > wide * 1.4;
    const long = wide > Math.min(half.x, half.z) * 2.4;
    const r = this.random();
    if (tall && half.y > 1.4 && wide < 1.8) P.latticeMast(this, xf, half, o);
    else if (long && half.y < 2.2 && vol > 2 && r < 0.55) P.pipeBundle(this, xf, half, o);
    // Ceiling raised from 90 to 260 m³ so the rooftop water tanks and the stack vent banks
    // land here as louvred plant rather than as blank plated boxes. Plant is what is bolted
    // to a roof; a featureless slab is what is left when nothing decided what an object was.
    else if (vol > 2 && vol < 260 && r < 0.82) P.ventBlock(this, xf, half, o);
    else P.platedBlock(this, xf, half, o);
  }

  /**
   * Concrete on a roof is architecture or it is a plant enclosure. It is not a packing crate
   * and it is definitely not a bank of vending machines — both of those used to land here,
   * and a lit dispenser cabinet standing on an open roof under a blue sky is exactly the
   * category error that made the previous arena read as an interior no matter how it was lit.
   */
  private buildConcreteBrush(xf: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
    const cubeish =
      Math.max(half.x, half.y, half.z) < Math.min(half.x, half.y, half.z) * 2.2 && Math.max(half.x, half.y, half.z) < 2.6;
    const r = this.random();
    if (cubeish && r < 0.6) P.plantBox(this, xf, half, o);
    else P.panelledBlock(this, xf, half, o);
  }

  /**
   * The layer collision does not have: laid floors, bright soffits, ledge kerbs, hazard
   * striping over drops, wall-run chevrons, conduits and signage.
   *
   * There is no wear layer any more — see the "Floor markings" note above.
   */
  private dressBrush(b: Brush, xf: THREE.Matrix4, half: THREE.Vector3, region: Region, o: SkinOpts): void {
    const faces = P.boxFaces(half);
    const top = faces[2];
    const topY = b.pos.y + half.y;
    const topArea = half.x * half.z * 4;
    const isPlatform = o.exposed[2] && topArea >= PLATFORM_MIN_AREA && topY > this.oracle.floorY + 0.35;
    /**
     * ROOF OR FLOOR. This one boolean is the pivot the whole art direction turns on.
     *
     * DESIGN.md 4: the arena is played outdoors on the roofs of a sunlit city, and
     * interiors are the exception. So a deck with sky over it gets roofing membrane or
     * gravel ballast, a parapet, rooflights and plant; a deck with a slab over it gets the
     * polished terrazzo an interior floor actually has. Repeated lighting passes measured
     * *correct* on the previous build while still failing the brief, and this is why —
     * the building was an interior, and no grade fixes a building.
     */
    const isRoof = isPlatform && this.openToSky(b, half);

    // A laid surface, and a real ceiling on the underside. Between the two, a deck stops
    // being a white slab in white air.
    if (o.exposed[2] && topArea >= FLOOR_MIN_AREA) {
      if (isRoof) this.roofDeck(b, xf, half, top, topArea);
      else P.floorPlate(this, xf, top, region.y > this.midHeight ? 'floor' : 'floorDark', P.FLOOR_BAY);
    }
    if (o.exposed[3] && topArea >= FLOOR_MIN_AREA && b.pos.y - half.y > this.oracle.floorY + 1.5) {
      this.buildSoffit(b, xf, half, faces[3], topY, isRoof);
    }

    if (isPlatform) {
      this.ledgeTrim(xf, half, o, topY, isRoof);
    }

    // Planting at the foot of a wall standing on a deck. See `plantingBed`: this is the
    // only soft silhouette in the building and it is kerb height, so it can hide nothing.
    if (o.base !== 'metal' && half.y > 1.4) this.plantingPass(b, xf, half, o);

    // The landmark dressing: a white relief band, a striped awning and a guaranteed run of
    // planting at the foot. See `landmarkDressing`.
    if (this.landmarkBrushes.has(b)) this.landmarkDressing(b, xf, half, o);

    // The full-chroma team skirting. This is the entire budget for saturated team colour
    // on architecture — see TEAM_WALL_SATURATION for why the wall itself is pale.
    const band: MatKey | undefined = TEAM_BAND_MATS[o.base];
    if (band) {
      for (const f of faces) {
        if (!o.exposed[f.index] || !P.isVerticalFace(f) || f.w < 1.2) continue;
        P.addFaceSlab(this, band, xf, f, 0, -half.y + TEAM_BAND_HEIGHT * 0.5, f.w - 0.06, TEAM_BAND_HEIGHT, 0.06, 0);
        P.addFaceSlab(this, 'trim', xf, f, 0, -half.y + TEAM_BAND_HEIGHT + 0.03, f.w - 0.12, 0.06, 0.07, 0);
      }
    }

    // **One signage mark per brush**, on its biggest exposed wall.
    //
    // This is the rule that turns a pile of decals into signage. Marking every face of
    // every block put a stencil on all four sides of a two-metre crate and on every wall
    // of a corridor; a facilities team paints the face people arrive at and leaves the
    // other three alone, and the difference between those two behaviours is exactly the
    // difference between "designed" and "generated".
    let signFace: P.Face | null = null;
    for (const f of faces) {
      if (!o.exposed[f.index] || !P.isVerticalFace(f)) continue;
      if (!signFace || f.w * f.h > signFace.w * signFace.h) signFace = f;
    }

    for (const f of faces) {
      if (!o.exposed[f.index] || !P.isVerticalFace(f)) continue;

      // Wall-run chevrons: a runnable wall must announce itself. Painted, not lit — the
      // mark has to be *readable*, and hard ink on white reads further than a glow does.
      //
      // Always ink, never the storey's hue. There are dozens of runnable faces on this map
      // and tinting all of them turns a region colour back into scattered trim, which is
      // exactly what the colour budget exists to stop. The chevron already carries its own
      // meaning — "you can run this" — and it does not need a second one.
      if (b.wallrunnable && f.w > CHEVRON_MIN_FACE_W && f.h > CHEVRON_MIN_FACE_H) {
        const marks = clamp(Math.floor(f.w / CHEVRON_PITCH), 1, CHEVRON_MAX_PER_FACE);
        const flip = this.random() < 0.5;
        for (let i = 0; i < marks; i++) {
          const du = -f.w * 0.5 + (f.w / marks) * (i + 0.5);
          this.decalKind('chevron', {
            pos: P.facePoint(f, du, -f.h * 0.5 + SIGN_EYE_HEIGHT, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf),
            normal: f.n.clone().transformDirection(xf),
            width: CHEVRON_WIDTH,
            color: PALETTE.ink,
            strength: P.SIGNAGE_STRENGTH,
            rotation: flip ? Math.PI : 0,
          });
        }
      }

      // Conduits and cable trays on tall walls.
      if (f.h > 3 && f.w > 3 && this.random() < 0.4) {
        P.conduitRun(this, xf, f, f.h * 0.5 - 0.55 - this.random() * 0.8, 2 + ((this.random() * 3) | 0), 0.055, 'metal');
      }

      if (f === signFace) this.signagePass(b, f, xf, region);
    }

    // The painted lane down the long axis of a big deck — and only a genuinely big *interior*
    // one. A roof already has a galvanised walkway laid down its long axis, which is the
    // same information said in a stronger material; painting lane dashes over it as well
    // would be the same mark twice, which is how a signage system turns into wallpaper.
    if (!isRoof && isPlatform && topArea >= LANE_MIN_DECK_AREA) this.laneMarkings(xf, top);
  }

  /**
   * THE ROOF DECK — the surface the match is actually played on.
   *
   * Everything here is either laid flat or under `STEP_HEIGHT`, and that constraint is not
   * a compromise, it is the design. A roof is a *low* landscape: membrane, laps, curbs,
   * drains, walkway grating, rooflights and painted numbers. Every one of those things is
   * ankle height or flat, so the whole rooftop vocabulary can be applied to a deck that
   * collision knows nothing about without a single lie about cover. The tall rooftop
   * objects — water tanks, plant rooms — go on the surrounding city instead, where they
   * are free (see `skylineBlock`).
   *
   * Two roof surfaces, chosen deterministically per deck: membrane and gravel ballast sit a
   * clear value step apart, so adjacent decks name themselves the way real roofs do,
   * without spending a hue.
   */
  private roofDeck(b: Brush, xf: THREE.Matrix4, half: THREE.Vector3, top: P.Face, topArea: number): void {
    const gravel = this.random() < GRAVEL_ROOF_CHANCE;
    P.roofField(this, xf, top, gravel ? 'gravel' : 'membrane', gravel ? P.GRAVEL_TILE : P.MEMBRANE_TILE);

    const w = top.w;
    const h = top.h;
    const along = w >= h;
    // Everything below stays inboard of the parapet line and of the hazard band that runs
    // along any edge with a drop beyond it.
    const insetU = Math.max(0, w * 0.5 - 1.8);
    const insetV = Math.max(0, h * 0.5 - 1.8);

    // A service walkway down the long axis of a big deck. This is the strongest piece of
    // rooftop wayfinding available: a route drawn on the ground in a material nothing else
    // uses, followable at full speed without reading anything.
    if (topArea >= WALKWAY_MIN_AREA && Math.min(w, h) > P.WALKWAY_WIDTH * 3) {
      P.walkwayRun(this, xf, top, 0, 0, (along ? w : h) - 1.6, along);
    }

    // Rooflights. On a roof these matter more than the ones under a soffit: the player runs
    // across them, they are the brightest thing on a white deck, and they are what says
    // there is a building under this roof rather than a platform in the air.
    if (w > DECK_SKYLIGHT_MIN_SPAN && h > DECK_SKYLIGHT_MIN_SPAN) {
      const cols = clamp(Math.floor(w / DECK_SKYLIGHT_PITCH), 1, DECK_SKYLIGHT_MAX_PER_AXIS);
      const rows = clamp(Math.floor(h / DECK_SKYLIGHT_PITCH), 1, DECK_SKYLIGHT_MAX_PER_AXIS);
      const pw = Math.min((w / cols) * 0.34, DECK_SKYLIGHT_SIZE);
      const ph = Math.min((h / rows) * 0.34, DECK_SKYLIGHT_SIZE);
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const du = -w * 0.5 + (w / cols) * (i + 0.5);
          const dv = -h * 0.5 + (h / rows) * (j + 0.5);
          // Never over the walkway, and never over the edge band.
          if (along ? Math.abs(dv) < P.WALKWAY_WIDTH : Math.abs(du) < P.WALKWAY_WIDTH) continue;
          if (Math.abs(du) > insetU || Math.abs(dv) > insetV) continue;
          P.deckSkylight(this, xf, top, du, dv, pw, ph);
        }
      }
    }

    // Vent curbs and roof drains. Punctuation: one every thirteen metres, capped, and
    // spaced off the deck's own grid so they never line up into a pattern.
    const curbs = clamp(Math.floor(Math.sqrt(topArea) / CURB_PITCH) + 1, 0, CURB_MAX_PER_DECK);
    for (let i = 0; i < curbs; i++) {
      const du = (this.random() - 0.5) * insetU * 1.7;
      const dv = (this.random() - 0.5) * insetV * 1.7;
      if (along ? Math.abs(dv) < P.WALKWAY_WIDTH : Math.abs(du) < P.WALKWAY_WIDTH) continue;
      if (this.random() < 0.68) {
        P.ventCurb(this, xf, top, du, dv, 0.7 + this.random() * 1.1, 0.6 + this.random() * 0.9);
      } else {
        P.roofDrain(this, xf, top, du, dv, 0.22 + this.random() * 0.12);
      }
    }

    // Planting, flanking the walkway. The only soft silhouette in a world of straight
    // lines, rationed hard: two runs on a deck big enough to carry a walkway and none at
    // all anywhere else. Punctuation, not texture.
    if (topArea >= WALKWAY_MIN_AREA && this.random() < ROOF_PLANTER_CHANCE) {
      const runLen = Math.min((along ? w : h) * 0.3, PLANTER_MAX_RUN);
      const off = P.WALKWAY_WIDTH * 0.5 + P.PLANTER_DEPTH * 1.2;
      // Set in from one end of the walkway, one bed either side of it.
      const lead = -(along ? w : h) * 0.5 + runLen * 0.5 + 2.5;
      for (const s of [-1, 1]) {
        const du = along ? lead : s * off;
        const dv = along ? s * off : lead;
        if (Math.abs(du) > insetU || Math.abs(dv) > insetV) continue;
        P.roofPlanter(this, xf, top, du, dv, runLen, along);
      }
    }

    // The roof garden. Only on a deck big enough to be a terrace rather than a ledge.
    if (topArea >= GARDEN_MIN_DECK_AREA) this.roofGarden(b, xf, top, insetU, insetV, along);

    // The painted roof number: one stencilled numeral, big, set out square with the deck.
    // Real roofs carry them for the people in the helicopter, and here it does the same job
    // it does everywhere else in the kit — it says which level you are standing on, in the
    // one weight and the one ink the rest of the signage uses.
    if (topArea >= LANE_MIN_DECK_AREA && Math.min(w, h) > 8) {
      const region = this.regionAt(b.pos.y - half.y);
      const du = along ? -w * 0.5 + Math.min(w * 0.18, 7) : 0;
      const dv = along ? 0 : -h * 0.5 + Math.min(h * 0.18, 7);
      this.decalKind('numeral', {
        pos: P.facePoint(top, du, dv, new THREE.Vector3(), P.FLOOR_DECAL_LIFT).applyMatrix4(xf),
        normal: top.n.clone().transformDirection(xf),
        width: Math.min(SIGN_TIERS[2], Math.min(w, h) * 0.3),
        color: PALETTE.ink,
        strength: P.SIGNAGE_STRENGTH,
        rotation: P.faceDecalRoll(top, b.yaw) + (along ? Math.PI * 0.5 : 0),
        variant: region.numeral,
      });
    }
  }

  /**
   * THE ROOF GARDEN — trees, water and shade on the decks big enough to carry them.
   *
   * Playtest: *"it could feel a little more sunny. Like it's a beautiful day, you know, like
   * insanely pristine."* Everything up to here has been a *service* roof — membrane, curbs,
   * drains, grating, plant — which is correct, immaculate, and reads as a working building
   * nobody would choose to stand on. This is the half that makes it somewhere people go: a
   * line of clipped trees down the walkway, a reflecting pool catching the sky, and a shade
   * sail over it. Three objects, on a deck that has to be at least `GARDEN_MIN_DECK_AREA`,
   * and at most one pool and one sail each.
   *
   * Every one of them is honest, and by a different route, which is the only reason a
   * shooter can have a garden on its fighting deck at all:
   *
   *  - **The trees are narrower than a player** (`P.TREE_MAX_WIDTH` 0.5 m against a 0.88 m
   *    body), so nothing can hide behind one at any height. That is the same rule that lets
   *    a handrail post and a davit mast stand on brushes collision does not have.
   *  - **The pool is flat.** A 16 cm rim is well under `STEP_HEIGHT`; the sim would not
   *    notice it and neither would a bullet.
   *  - **The sail is see-through.** Translucent canvas, the same bargain `scrimPanel` makes
   *    with the hung banners. An opaque sheet 3.6 m over an open deck would be a soft ceiling
   *    nobody could shoot through, and that is not on offer.
   *
   * Everything is placed inboard of the parapet band and clear of the service walkway, on
   * the same test the vent curbs use, so none of it ever lands on a route or an edge.
   */
  private roofGarden(
    b: Brush,
    xf: THREE.Matrix4,
    top: P.Face,
    insetU: number,
    insetV: number,
    along: boolean,
  ): void {
    /**
     * NOT IN THE CONTESTED MIDDLE.
     *
     * The deck the helipad is painted on is the most fought-over surface in the arena and it
     * has to stay a clean open plane — it is also, literally, a helicopter landing deck, and
     * a landing deck with a hedge on it is wrong twice over. Every other roof on the map can
     * have a garden; this one gets paint, a windsock and nothing else.
     */
    const mid = this.arena.centerSpawn;
    const topY = b.pos.y + b.half.y;
    if (
      Math.hypot(b.pos.x - mid.x, b.pos.z - mid.z) < Math.max(top.w, top.h) * 0.5 &&
      Math.abs(topY - this.floorUnder(mid)) < 2
    ) {
      return;
    }

    const w = top.w;
    const h = top.h;
    /**
     * Inboard of the parapet band, and clear of the service walkway.
     *
     * `acrossHalf` is the object's half-size measured **across** the walkway, and passing the
     * object's *largest* dimension instead is the bug this signature replaced: a 7 x 3.9 m
     * pool laid alongside a 1.4 m walkway was tested as though it were seven metres deep
     * across the route, so every pool and every sail on every deck in the arena failed and
     * not one of them was ever built.
     */
    const clear = (du: number, dv: number, alongHalf: number, acrossHalf: number): boolean => {
      const padU = along ? alongHalf : acrossHalf;
      const padV = along ? acrossHalf : alongHalf;
      if (Math.abs(du) + padU > insetU || Math.abs(dv) + padV > insetV) return false;
      const across = along ? Math.abs(dv) : Math.abs(du);
      return across - acrossHalf > P.WALKWAY_WIDTH * 0.5 + 0.3;
    };

    // A row of clipped trees down one side of the walkway. An avenue reads as designed where
    // scattered planting reads as litter, so they are evenly spaced off a single origin.
    const side = this.random() < 0.5 ? -1 : 1;
    const treeOff = P.WALKWAY_WIDTH * 0.5 + 1.5;
    const run = (along ? w : h) - 6;
    const trees = clamp(Math.floor(run / TREE_SPACING), 0, TREES_PER_DECK);
    const treeHalf = P.TREE_MAX_WIDTH * 0.5 + 0.2;
    for (let i = 0; i < trees; i++) {
      const t = -run * 0.5 + (run / Math.max(1, trees)) * (i + 0.5);
      const du = along ? t : side * treeOff;
      const dv = along ? side * treeOff : t;
      if (!clear(du, dv, treeHalf, treeHalf)) continue;
      P.roofTree(this, xf, top, du, dv);
    }

    // A reflecting pool on the OTHER side of the walkway from the trees, set toward one end.
    // Still water is the only horizontal surface in the arena that takes a specular sky
    // highlight, which is most of what makes a deck read as being under a bright sky.
    if (this.random() < POOL_CHANCE) {
      const acrossRoom = (along ? insetV : insetU) - P.WALKWAY_WIDTH * 0.5 - 0.6;
      const pd = Math.min(POOL_MAX_SIZE * 0.6, acrossRoom);
      const pl = Math.min(POOL_MAX_SIZE, (along ? w : h) * 0.26);
      if (pd >= 1.6 && pl >= 1.6) {
        const off = P.WALKWAY_WIDTH * 0.5 + 0.5 + pd * 0.5;
        const t = (along ? w : h) * 0.22;
        const du = along ? t : -side * off;
        const dv = along ? -side * off : t;
        if (clear(du, dv, pl * 0.5, pd * 0.5)) {
          P.reflectPool(this, xf, top, du, dv, along ? pl : pd, along ? pd : pl);
        }
      }
    }

    // And a shade sail over the far end of the tree side.
    if (this.random() < SAIL_CHANCE) {
      const acrossRoom = (along ? insetV : insetU) - P.WALKWAY_WIDTH * 0.5 - 0.6;
      const sd = Math.min(SAIL_MAX_SIZE * 0.7, acrossRoom);
      const sl = Math.min(SAIL_MAX_SIZE, (along ? w : h) * 0.24);
      if (sd >= 2.5 && sl >= 2.5) {
        const off = P.WALKWAY_WIDTH * 0.5 + 0.5 + sd * 0.5;
        const t = -(along ? w : h) * 0.24;
        const du = along ? t : side * off;
        const dv = along ? side * off : t;
        if (clear(du, dv, sl * 0.5, sd * 0.5)) {
          P.shadeSail(this, xf, top, du, dv, along ? sl : sd, along ? sd : sl);
        }
      }
    }
  }

  /**
   * THE CEILING. Prior review: *"the ceiling is a flat near-black slab with no gradient or
   * thickness — it reads as a HOLE in the render, not a roof."*
   *
   * That was accurate and it was not a colour problem. toon.ts authors every downward face
   * at 21-24% display grey on purpose, so that a surface you cannot stand on can never be
   * mistaken for one you can; painting it brighter would break the value ladder the whole
   * renderer is built on. What made it read as absence is that it was one unbroken plane
   * with nothing in it — no structure, no thickness, no scale, no light.
   *
   * So this builds an actual roof, in four passes, and every one of them works by adding
   * *geometry* rather than by arguing with the ladder:
   *
   *  - a soffit plate, inset, so the deck has a lining;
   *  - **coffer ribs**, whose sides are vertical faces and therefore land in the *wall*
   *    band — a bright grid over shaded bays, which is the entire fix for "flat";
   *  - a perimeter downstand, which is the thickness the review said was missing;
   *  - **skylights** wherever the deck is the top of the world, which put the brightest
   *    value in the frame overhead where daylight belongs. That is what makes an interior
   *    read as sunlit rather than merely as lit.
   *
   * Plus ductwork, where there is enough air below for it to hang without being reachable.
   */
  private buildSoffit(
    b: Brush,
    xf: THREE.Matrix4,
    half: THREE.Vector3,
    f: P.Face,
    topY: number,
    isRoof: boolean,
  ): void {
    const w = f.w - P.FLOOR_INSET * 2;
    const h = f.h - P.FLOOR_INSET * 2;
    P.addFaceSlab(this, 'panel', xf, f, 0, 0, w, h, P.SOFFIT_LINING_THICKNESS, 0);
    P.soffitCoffers(this, xf, f, SOFFIT_BAY, SOFFIT_RIB_DEPTH);

    // Rooflights on the UNDERSIDE, for a deck that has something built over it — an
    // interior with daylight coming in. A deck whose top face is an open roof gets its
    // rooflights from `roofDeck` instead, and it must not get them twice: a pane visible
    // from above at one place and from below at another is the same object in two
    // positions, which is a hole in the fiction the moment a player looks up.
    if (!isRoof && topY >= this.topDeckY - DECK_SNAP_UP && w > SKYLIGHT_MIN_SPAN && h > SKYLIGHT_MIN_SPAN) {
      const cols = clamp(Math.floor(w / SKYLIGHT_PITCH), 1, SKYLIGHT_MAX_PER_AXIS);
      const rows = clamp(Math.floor(h / SKYLIGHT_PITCH), 1, SKYLIGHT_MAX_PER_AXIS);
      const pw = Math.min((w / cols) * 0.52, SKYLIGHT_MAX_SIZE);
      const ph = Math.min((h / rows) * 0.52, SKYLIGHT_MAX_SIZE);
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          P.skylightPanel(
            this,
            xf,
            f,
            -w * 0.5 + (w / cols) * (i + 0.5),
            -h * 0.5 + (h / rows) * (j + 0.5),
            pw,
            ph,
          );
        }
      }
      return;
    }

    // Plant and ductwork under an interior soffit. Only where the deck below is far enough
    // down that a hanging duct is above every head in the game — nothing here may ever
    // become something to hide behind.
    //
    // "How far is the floor below this ceiling" is answered from the storey ladder, not
    // from the height grid, for the same reason `openToSky` is: `maxHeightIn` reports the
    // tallest solid over a column, and the tallest solid over a deck's own column is the
    // deck itself, which makes every clearance come out negative.
    const soffitY = b.pos.y - half.y;
    if (soffitY - this.deckBelow(soffitY) < DUCT_MIN_CLEARANCE) return;
    if (this.random() > DUCT_CHANCE) return;
    const along = w >= h;
    const runs = clamp(Math.floor((along ? h : w) / DUCT_PITCH), 0, 3);
    for (let i = 0; i < runs; i++) {
      const across = along ? h : w;
      const t = -across * 0.5 + (across / (runs + 1)) * (i + 1);
      P.hangingDuct(
        this,
        xf,
        f,
        along ? 0 : t,
        along ? t : 0,
        (along ? w : h) - 1.2,
        0.42 + this.random() * 0.24,
        along,
      );
    }
  }

  /**
   * A planting bed along the foot of a wall.
   *
   * Placed only where the oracle can prove there is a walkable surface at the wall's base —
   * a bed floating half way up a shaft is the kind of detail that makes a generated
   * building look generated. Rationed hard: planting is punctuation, and this arena has
   * three hundred walls.
   */
  private plantingPass(b: Brush, xf: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
    if (this.random() > PLANTING_CHANCE) return;
    const faces = P.boxFaces(half);
    let best: P.Face | null = null;
    for (const f of faces) {
      if (!o.exposed[f.index] || !P.isVerticalFace(f)) continue;
      if (f.w < 2.4 || f.h < 2.4) continue;
      if (!best || f.w > best.w) best = f;
    }
    if (!best) return;

    // Which deck does this wall stand on? Asked of the storey ladder, not of the height
    // grid — for the third time in this file, because the grid stores the tallest solid
    // over a column and near a wall that is the wall. A probe taken far enough out to
    // escape the wall's own 1.25 m cell lands on whatever else is nearby instead, and
    // measured that way nine walls out of ten came back with the floor twelve to
    // twenty-eight metres above their own base.
    //
    // Wall brushes are also routinely sunk a metre or two into the slab they stand on, so
    // the bed goes on the *deck*, never on the brush's own base.
    const baseY = b.pos.y - half.y;
    const topY = b.pos.y + half.y;
    let deckY = -Infinity;
    for (const r of this.regions) {
      if (r.y >= baseY - PLANTING_MAX_SINK && r.y <= topY - PLANTING_MIN_RISE && r.y > deckY) deckY = r.y;
    }
    if (deckY === -Infinity) return;

    P.plantingBed(this, xf, best, 0, deckY - b.pos.y + P.FLOOR_TOP, Math.min(best.w * 0.62, PLANTING_MAX_RUN));
  }

  /**
   * WHAT MAKES A PAINTED MASS A BUILDING RATHER THAN A SWATCH.
   *
   * A landmark is the biggest single field of colour in the game — thousands of square
   * metres on this arena — and a flat field of anything at that scale reads as a colour
   * chip, not as architecture. Three things fix that, and each of them is also paying back
   * screen-area budget by replacing saturated pixels with white ones:
   *
   *  - **A white relief band** at two thirds height, in the same material as every soffit and
   *    coping in the building, with the kit's slate reveal under it. It gives the mass a
   *    horizontal, a shadow line and a scale — and it takes roughly a fifth of the painted
   *    area back out of the colour budget.
   *  - **A striped awning** wherever the mass stands on a deck a player can actually walk.
   *    Fabric against concrete, warm against warm, and a hard cast shadow. This is the single
   *    most direct thing the kit can say for "beautiful day", which is what the playtest
   *    asked for, and it is honest: 2.9 m up, 95 cm deep, hugging a wall (see `P.awningRun`).
   *  - **Planting at the foot**, guaranteed rather than rolled. Green against terracotta is
   *    the oldest colour pairing architecture has, and it puts the one soft silhouette in the
   *    building exactly where the eye is already going.
   */
  private landmarkDressing(b: Brush, xf: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
    const faces = P.boxFaces(half);
    const bandDv = -half.y + half.y * 2 * LANDMARK_BAND_FRACTION;

    let widest: P.Face | null = null;
    let widestDeckY = -Infinity;
    for (const f of faces) {
      if (!o.exposed[f.index] || !P.isVerticalFace(f)) continue;
      if (f.w < 3 || f.h < 4) continue;
      P.addFaceSlab(this, 'panel', xf, f, 0, bandDv, f.w - 0.4, LANDMARK_BAND_HEIGHT, 0.07, 0);
      P.addFaceSlab(this, 'trim', xf, f, 0, bandDv - LANDMARK_BAND_HEIGHT * 0.5 - 0.05, f.w - 0.6, 0.08, 0.09, 0);
      const deckY = this.deckOutside(b, xf, half, f);
      if (deckY === -Infinity) continue;
      if (!widest || f.w > widest.w) {
        widest = f;
        widestDeckY = deckY;
      }
    }
    if (!widest) return;

    const footDv = widestDeckY - b.pos.y + P.FLOOR_TOP;
    // The awning has to be wholly on the face, and high enough above the deck that nothing
    // can be behind it: `P.AWNING_HEIGHT` is 2.9 m against a 2.05 m player.
    if (footDv + P.AWNING_HEIGHT < half.y - 0.6 && widest.w > 5) {
      P.awningRun(this, xf, widest, 0, footDv + P.AWNING_HEIGHT, Math.min(widest.w * 0.55, 11));
    }
    P.plantingBed(this, xf, widest, 0, footDv, Math.min(widest.w * 0.7, PLANTING_MAX_RUN * 1.4));
  }

  /**
   * The height of the walkable surface immediately OUTSIDE a wall face — measured, not
   * inferred from the storey ladder.
   *
   * `plantingPass` picks the nearest storey level in a plausible band and accepts it, which
   * is fine for a bed placed at 34% odds on one wall in three. It is not fine for a
   * landmark: there are three of those in the whole arena, they carry an awning and a run of
   * planting each, and the storey ladder is a list of heights that exist *somewhere* on the
   * map, not a claim that any of them exists *here*. On this arena that difference is not
   * theoretical — the core pedestal stands at the centre with the mid ring at y20 around it,
   * and the ladder happily offered y26, which is the roof level sixty-nine metres away at
   * the two ends of the map. The awning and the bed would have hung six metres in the air.
   *
   * So the oracle is asked directly: sample a short way out from the face and take the
   * highest solid at or below the wall's own top. Returns -Infinity when there is nothing
   * out there to stand on, and the caller then skips the face entirely.
   */
  private deckOutside(b: Brush, xf: THREE.Matrix4, half: THREE.Vector3, f: P.Face): number {
    const topY = b.pos.y + half.y;
    const baseY = b.pos.y - half.y;
    let best = -Infinity;
    // Three samples across the face, so a wall standing on the edge of a deck still finds it.
    for (const du of [-f.w * 0.3, 0, f.w * 0.3]) {
      P.facePoint(f, du, 0, _p1, 1.6).applyMatrix4(xf);
      const y = this.oracle.surfaceUnder(_p1.x, _p1.z, topY - 0.5);
      if (y > best) best = y;
    }
    // It has to be a surface this wall actually rises out of: not below its own foot by more
    // than a brush is normally sunk into its slab, and far enough below its top to be a wall.
    if (best === -Infinity || best < baseY - PLANTING_MAX_SINK || best > topY - PLANTING_MIN_RISE) {
      return -Infinity;
    }
    return best;
  }

  /**
   * Painted lane lines down the long axis of a deck.
   *
   * Real circulation paint: two dashed lines defining a route. It costs no hue, gives the
   * eye something to measure a long floor against, and — being aligned with the deck's own
   * axis — quietly tells a player which way the building runs.
   */
  private laneMarkings(xf: THREE.Matrix4, top: P.Face): void {
    const alongU = top.w >= top.h;
    const runLen = alongU ? top.w : top.h;
    const across = alongU ? top.h : top.w;
    const dashes = clamp(Math.floor(runLen / LANE_PITCH), 2, 24);
    const lift = P.FLOOR_DECAL_LIFT;
    for (const side of [-1, 1]) {
      const offset = side * Math.min(across * 0.28, LANE_SPACING);
      for (let i = 0; i < dashes; i++) {
        const t = -runLen * 0.5 + (runLen / dashes) * (i + 0.5);
        const du = alongU ? t : offset;
        const dv = alongU ? offset : t;
        this.decalKind('laneDash', {
          pos: P.facePoint(top, du, dv, new THREE.Vector3(), lift).applyMatrix4(xf),
          normal: top.n.clone().transformDirection(xf),
          width: LANE_PITCH * 0.55,
          height: LANE_WIDTH,
          color: PALETTE.ink,
          strength: P.SIGNAGE_STRENGTH,
          rotation: alongU ? Math.PI * 0.5 : 0,
        });
      }
    }
  }

  /**
   * Kerb + shadow gap under a platform's top edge, and hazard striping over a real drop.
   *
   * The old version painted a coloured band and an emissive rim on every ledge in the map,
   * which is where most of the arena's stray colour came from. A dark reveal under a pale
   * kerb does the same job better: it is how a real concrete edge is detailed, it reads at
   * any distance, and it costs the colour budget nothing.
   */
  private ledgeTrim(xf: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts, topY: number, isRoof: boolean): void {
    const faces = P.boxFaces(half);
    for (const f of faces) {
      if (!P.isVerticalFace(f) || !o.exposed[f.index]) continue;
      if (f.w < 0.6) continue;
      // Pale kerb along the very edge.
      P.addFaceSlab(this, 'panel', xf, f, 0, half.y - 0.09, f.w, 0.18, 0.04, 0);
      // Slate reveal beneath it: the hard line that puts the ledge in space.
      P.addFaceSlab(this, 'trim', xf, f, 0, half.y - 0.27, f.w - 0.08, 0.1, 0.025, 0);

      // Hazard striping wherever the ledge overlooks a real drop.
      P.facePoint(f, 0, half.y, _p1, 0.9).applyMatrix4(xf);
      const beyond = this.oracle.heightAt(_p1.x, _p1.z);
      if (topY - beyond >= LEDGE_DROP) {
        /**
         * THE PARAPET. The defining line of a rooftop, and the reason a roof edge reads
         * from fifty metres instead of only from ten.
         *
         * A white concrete upstand with an aluminium coping on it, running the whole rim of
         * every roof with a drop beyond it. See `P.PARAPET_HEIGHT` for why it is 30 cm and
         * not the metre a real one would be: the height is supplied by the skeletal railing
         * that stands on it, because a solid metre of decorative parapet would be chest-high
         * cover that collision does not have, on every ledge on the map.
         */
        if (isRoof) P.roofParapet(this, xf, f, half.y, f.w);
        // A guard rail on every edge with a fall beyond it.
        //
        // This is the strongest single piece of Mirror's Edge language available to this
        // arena and it does three jobs at once: it draws the edge of a deck from across
        // the map, it gives a hundred-metre white plane a 2.2 m rhythm to measure distance
        // against, and its pale wood cap is the only warm material in the building.
        //
        // Skeletal on purpose. `RAILING_HEIGHT` is 1.25 m against a 2.05 m player, so a
        // *solid* parapet here would be chest-high cover that collision does not have —
        // the worst lie decoration can tell in a shooter. Posts and two horizontals hide
        // nobody, and the height still reads correctly at the new body scale.
        // On a roof the rail stands tight against the back of the parapet upstand, so the
        // two read as one 1.55 m parapet-and-handrail rather than as a kerb with a fence
        // half a metre behind it. Indoors there is no upstand, so it keeps its own inset.
        //
        // Some roof edges take a frameless GLASS balustrade instead, and the alternation is
        // the point. DESIGN.md 4 asks for spotless glass and brushed aluminium as well as
        // painted steel; a hundred edges detailed one single way is one detail repeated a
        // hundred times, and a building that has both a service edge and a showpiece edge is
        // a building somebody designed. Glass is honest at full height for the same reason
        // the curtain walling is — you see straight through it.
        if (f.w >= RAILING_MIN_EDGE) {
          const inset = isRoof ? ROOF_RAILING_INSET : RAILING_INSET;
          if (isRoof && this.random() < GLASS_BALUSTRADE_SHARE) {
            P.glassBalustrade(this, xf, f, half.y, f.w - 0.5, inset);
          } else {
            P.edgeRailing(this, xf, f, half.y, f.w - 0.5, inset);
          }
        }

        const bandW = f.w - 0.24;
        const bandD = 0.36;
        if (bandW > 0.4) {
          const local = P.facePoint(f, 0, half.y, new THREE.Vector3(), 0);
          // Offset inward from the edge so the stripe sits on the walkable surface.
          local.addScaledVector(f.n, -bandD * 0.5 - 0.02);
          const m = new THREE.Matrix4().makeTranslation(
            local.x,
            local.y + P.FLOOR_TOP + 0.01,
            local.z,
          );
          m.multiply(new THREE.Matrix4().makeRotationY(Math.atan2(f.n.x, f.n.z)));
          P.addOwned(
            this,
            'hazard',
            P.texturedFloorQuad(bandW, bandD, bandW / P.HAZARD_TILE, 1),
            m.premultiply(xf),
          );

          // A painted keep-clear box inboard of the band. Hatched rectangles are the
          // universal floor language for "the ground stops being ground here", they are
          // orthogonal and stencilled rather than sprayed, and they give the eye something
          // deliberate to read on a deck now that the random scuffing is gone.
          const inset = bandD + KEEP_CLEAR_SIZE * 0.5 + 0.25;
          // The platform also has to be deep enough for the box to land wholly on it: a
          // narrow catwalk gets the hazard band and nothing else.
          const depthHalf = f.index === 0 || f.index === 1 ? half.x : half.z;
          if (bandW >= KEEP_CLEAR_MIN_WIDTH && depthHalf >= inset + KEEP_CLEAR_SIZE * 0.5) {
            const inboard = P.facePoint(f, 0, half.y, new THREE.Vector3(), 0);
            inboard.addScaledVector(f.n, -inset);
            this.decalKind('keepClear', {
              pos: inboard
                .setY(inboard.y + P.FLOOR_DECAL_LIFT)
                .applyMatrix4(xf),
              normal: new THREE.Vector3(0, 1, 0).transformDirection(xf),
              width: KEEP_CLEAR_SIZE,
              height: KEEP_CLEAR_SIZE,
              color: PALETTE.ink,
              strength: P.SIGNAGE_STRENGTH,
              rotation: Math.atan2(f.n.x, f.n.z),
            });
          }
        }
      }
    }
  }

  /**
   * Wall signage. **One mark, and only two marks exist here.**
   *
   * Called once per brush, on its largest exposed wall. Either that wall is one of the
   * biggest in its storey, in which case it carries the storey's *name*, or it is merely
   * large, in which case it carries the storey's *number*. Anything smaller carries
   * nothing, because a small plane on a white building is already doing its job.
   *
   * That is the whole rework of critique 4. What used to be here was a random roll over
   * four marks, one to three of them on every face of every brush, each at a randomly-rolled
   * width and a randomly-rolled offset across the wall. It produced exactly what the
   * verifier saw: a page-of-text glyph, a stray bracket and a scribble at inconsistent
   * scales on the same plane, which is indistinguishable from leftover debug art. Designed
   * signage has none of those properties. It is centred on the plane, it is one of three
   * sizes, it is one colour at one strength, and it says something true.
   */
  private signagePass(b: Brush, f: P.Face, xf: THREE.Matrix4, region: Region): void {
    const area = f.w * f.h;
    if (area < SIGN_MIN_FACE_AREA) return;
    const n = f.n.clone().transformDirection(xf);

    // A wall big enough to carry the storey's name becomes a candidate. The largest few
    // per region win, so the name stays a landmark instead of wallpaper.
    if (area >= NAME_MIN_FACE_AREA && f.w > 5 && f.h > 3) {
      const paint = this.paintFor(b, region);
      const label = paint.team ? this.teamLabel(paint.paint) : region.name;
      const cell = this.atlas.label(label);
      if (cell) {
        const width = clamp(f.w * 0.5, 2, 12);
        const pos = P.facePoint(f, 0, 0, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf);
        // A team's name is the one thing on a wall allowed to be saturated, because the
        // wall belongs to that team; everything else is the kit's single ink.
        const color = paint.team ? TEAM_PAINT_COLORS[paint.paint] : PALETTE.ink;
        this.addNameSite(region.key, area, () => {
          this.decal({ cell, pos, normal: n, width, color, strength: 1 });
        });
        return;
      }
    }

    // Otherwise: one numeric zone marker, centred on the plane, sized from the plane's own
    // area. A number is the least decorative mark there is and it answers the only question
    // a blank wall can answer — which storey is this. Dead-centre placement is doing real
    // work: it is what a mark being *set out* looks like, as against a mark being dropped
    // at a random offset, which is how the old pass placed everything.
    //
    // `zoneMark` rather than a bare `numeral`: a lone digit on a large white wall still
    // read as a character left behind rather than as signage. The rule pair above and
    // below it is what makes it look drawn with a straight edge — and it survives to a
    // distance where the digit itself no longer does.
    this.decalKind('zoneMark', {
      pos: P.facePoint(f, 0, 0, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf),
      normal: n,
      width: Math.min(this.signTier(area), f.w * SIGN_MAX_FACE_FRACTION, f.h * SIGN_MAX_FACE_FRACTION),
      color: PALETTE.ink,
      strength: P.SIGNAGE_STRENGTH,
      rotation: P.faceDecalRoll(f, b.yaw),
      variant: region.numeral,
    });
  }

  /** Defer a name placement; only the biggest walls in a region will actually get one. */
  private addNameSite(key: string, area: number, place: () => void): void {
    let list = this.nameSites.get(key);
    if (!list) {
      list = [];
      this.nameSites.set(key, list);
    }
    list.push({ area, place });
  }

  /** Run the deferred name placements, largest wall first, capped per region. */
  private flushNameSites(): void {
    for (const list of this.nameSites.values()) {
      list.sort((a, b) => b.area - a.area);
      for (let i = 0; i < Math.min(NAME_DECALS_PER_REGION, list.length); i++) list[i].place();
    }
    this.nameSites.clear();
  }

  private teamLabel(paint: MatKey): string {
    return paint === 'teamA' ? TEAM_NAMES[0] : TEAM_NAMES[1];
  }

  // -- Ramps ---------------------------------------------------------------

  /**
   * Surf ramps are rendered as the inclined slab their collider is, with glowing
   * rails and downhill chevrons. Deliberately no legs or understructure: the space
   * under a ramp is ambiguous in the data, and drawing supports there would assert
   * something about solidity that this file cannot verify.
   */
  private buildRamp(r: Ramp): void {
    const half = vec(r.half);
    if (half.x <= 0 || half.y <= 0 || half.z <= 0) return;
    const xf = rampMatrix(r);
    const exposed = [true, true, true, true, true, true];
    /**
     * A ramp is made of what the level says it is made of.
     *
     * Every ramp used to be brushed aluminium regardless. On a rooftop complex whose surf
     * network is mostly *cast concrete* — the chutes off the helipad, the slips down to the
     * apron, the ramps between the home roof and the deck — that put twenty metal slabs
     * through the middle of a white concrete building and made the traversal network read as
     * bolted-on equipment rather than as part of the architecture. The steel ramps (the ones
     * spanning between separate buildings, which really would be steel) still are.
     */
    const steel = r.surface === SurfaceKind.Metal || r.surface === SurfaceKind.Grate;
    const o: SkinOpts = {
      base: steel ? 'metal' : 'panel',
      inner: steel ? 'metalDark' : 'concrete',
      accent: 'metal',
      exposed,
      yaw: r.yaw,
    };
    P.platedBlock(this, xf, half, o);

    const faces = P.boxFaces(half);
    const top = faces[2];

    // Kerb rails down both long edges: a pale lip over a slate reveal, and a slate cheek
    // under it. Neutral, all of it. There are twenty-four ramps on this map, so painting
    // their cheeks in the storey hue meant fifty coloured lines threaded through the whole
    // building — the definition of colour spent as trim. A ramp is already the only
    // brushed-metal object in a concrete room; it does not need a hue to be spotted.
    for (const sx of [-1, 1]) {
      P.addBox(this, 'panel', xf, sx * (half.x - 0.09), half.y - 0.03, 0, 0.16, 0.07, half.z * 2 - 0.05);
      P.addBox(this, 'trim', xf, sx * (half.x - 0.2), half.y - 0.02, 0, 0.1, 0.05, half.z * 2 - 0.05);
      P.addBox(this, 'trim', xf, sx * half.x, 0, 0, 0.06, half.y * 1.4, half.z * 2 - 0.1);
    }

    // Downhill chevrons: which way the ramp accelerates you should be obvious. One every
    // four metres and never more than three — the old rule laid eight down a long ramp,
    // and twenty-four ramps at eight marks each is two hundred chevrons in one building.
    const marks = clamp(Math.floor((half.z * 2) / RAMP_CHEVRON_PITCH), 1, CHEVRON_MAX_PER_FACE);
    for (let i = 0; i < marks; i++) {
      const dv = -top.h * 0.5 + (top.h / marks) * (i + 0.5);
      this.decalKind('chevron', {
        pos: P.facePoint(top, 0, dv, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf),
        normal: top.n.clone().transformDirection(xf),
        width: Math.min(CHEVRON_WIDTH, top.w * 0.55),
        color: PALETTE.ink,
        strength: P.SIGNAGE_STRENGTH,
        // Chevron art points along +right; rotate so it points down the slope.
        rotation: P.faceDecalRoll(top, r.yaw) + (r.pitch >= 0 ? Math.PI * 0.5 : -Math.PI * 0.5),
      });
    }
  }

  // -- Landmarks -----------------------------------------------------------

  /**
   * Every region gets one structure you can name from across the map: region colour
   * banding, a huge painted name, a numeral, and a distinct roof crown. All of it is
   * either painted onto, or skeletal clutter above, geometry that already exists.
   */
  private buildLandmarks(): void {
    const heights = this.arena.brushes.map((b) => b.pos.y + b.half.y);
    const maxTop = heights.length ? Math.max(...heights) : this.boundsMax.y;

    for (const region of this.regions) {
      let best: Brush | null = null;
      let bestScore = -Infinity;
      for (const b of this.arena.brushes) {
        if (b.decorative) continue;
        if (this.regionAt(b.pos.y - b.half.y).key !== region.key) continue;
        const area = b.half.x * b.half.z * 4;
        if (area < 3.2) continue;
        const top = b.pos.y + b.half.y;
        const score = (top - this.oracle.floorY) * 2 + Math.min(area, 60) * 0.12;
        if (score > bestScore) {
          bestScore = score;
          best = b;
        }
      }
      if (!best) continue;
      this.buildLandmark(best, region, maxTop);
    }
  }

  private buildLandmark(b: Brush, region: Region, maxTop: number): void {
    const half = vec(b.half);
    const xf = brushMatrix(b);
    const exposed = this.exposedFaces(xf, half);
    const faces = P.boxFaces(half);
    const topY = b.pos.y + half.y;

    // One monumental painted plane down each open face. This — a single flat field, floor
    // to ceiling — is the whole wayfinding idea: you do not read the landmark, you
    // recognise it. Neutral storeys band in slate instead, and read by value.
    const bandMat: MatKey = region.painted ? region.paint : 'trim';
    for (const f of faces) {
      if (!P.isVerticalFace(f) || !exposed[f.index]) continue;
      const bands = clamp(Math.floor(f.w / 7), 1, 2);
      for (let i = 0; i < bands; i++) {
        const du = -f.w * 0.5 + (f.w / bands) * (i + 0.5);
        P.addFaceSlab(this, bandMat, xf, f, du, 0, Math.min(f.w / bands - 0.6, 2.6), f.h - 0.5, 0.05, 0);
      }
    }

    // The name, as big as the biggest open face will take.
    let front: P.Face | null = null;
    for (const f of faces) {
      if (!P.isVerticalFace(f) || !exposed[f.index]) continue;
      if (!front || f.w * f.h > front.w * front.h) front = f;
    }
    if (front) {
      const cell = this.atlas.label(region.name);
      if (cell && front.w > 3) {
        const width = clamp(front.w * 0.72, 1.5, 14);
        this.decal({
          cell,
          pos: P.facePoint(front, 0, front.h * 0.22, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf),
          normal: front.n.clone().transformDirection(xf),
          width,
          // Painted on the band, so it has to read against it. Value decision only.
          color: stencilOn(bandMat),
          strength: 1,
          rotation: 0,
        });
      }
      if (front.h > 3) {
        this.decalKind('zoneMark', {
          pos: P.facePoint(front, 0, -front.h * 0.22, new THREE.Vector3(), P.DECAL_LIFT).applyMatrix4(xf),
          normal: front.n.clone().transformDirection(xf),
          width: clamp(front.h * 0.2, SIGN_TIERS[0], SIGN_TIERS[2]),
          color: stencilOn(bandMat),
          strength: P.SIGNAGE_STRENGTH,
          variant: region.numeral,
        });
      }
      // Blank sign boards flanking the name: a slate frame and a white face, and nothing
      // stencilled on them. The "notice plate" they used to carry was the page-of-text
      // glyph the verifier read as clip art, and two of them sat either side of every
      // landmark in the building.
      if (front.w > 6 && front.h > 4) {
        for (const side of [-1, 1]) {
          P.hangingSign(this, xf, front, side * front.w * 0.3, -front.h * 0.05, 1.6, 1.1, 'panel');
        }
      }
    }

    // Roof crown — the far-silhouette read. Only if nothing is stacked above it.
    const clearAbove = this.oracle.maxHeightIn(
      b.pos.x - half.x,
      b.pos.z - half.z,
      b.pos.x + half.x,
      b.pos.z + half.z,
    );
    if (clearAbove <= topY + 0.05 && topY > this.oracle.floorY + 2) {
      const room = Math.max(3, (maxTop - topY) * 0.5 + 4);
      this.roofCrown(xf, half, region, room);
      P.antennaCluster(this, xf, half, Math.min(room, 7), 'metal');
    }
  }

  /**
   * Per-region skeletal roof structure — the far-silhouette read. Thin by construction,
   * never mistakable for cover, and painted rather than lit: against a bright sky a white
   * mast against blue is a stronger shape than any glow could be.
   */
  private roofCrown(xf: THREE.Matrix4, half: THREE.Vector3, region: Region, room: number): void {
    const top = half.y;
    const r = Math.min(half.x, half.z) * 0.8;
    /** The crown's one tonal note: the storey's plane, or slate on a neutral storey. */
    const cap: MatKey = region.painted ? region.paint : 'trim';
    switch (region.crown) {
      case 'ring': {
        const h = Math.min(room, 5);
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
          P.addCyl(this, 'metalDark', xf, Math.cos(a) * r, top + h * 0.5, Math.sin(a) * r, 0.07, h);
        }
        this.add(
          cap,
          P.UNIT_TORUS,
          P.localXf(xf, 0, top + h, 0, r * 2.6, r * 2.6, r * 2.6, Math.PI / 2, 0, 0),
        );
        this.add(
          'trim',
          P.UNIT_TORUS,
          P.localXf(xf, 0, top + h - 0.16, 0, r * 2.4, r * 2.4, r * 2.4, Math.PI / 2, 0, 0),
        );
        break;
      }
      case 'spires': {
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2;
          const h = Math.min(room, 4 + i);
          P.addCyl(this, 'metalDark', xf, Math.cos(a) * r * 0.7, top + h * 0.5, Math.sin(a) * r * 0.7, 0.09, h, 0.18, a, 0);
          P.addSphere(this, cap, xf, Math.cos(a) * r * 0.7, top + h, Math.sin(a) * r * 0.7, 0.16);
        }
        break;
      }
      case 'pylon': {
        const h = Math.min(room * 1.4, 11);
        P.addCyl(this, 'metal', xf, 0, top + h * 0.5, 0, 0.16, h);
        for (let i = 0; i < 4; i++) {
          const y = top + h * (0.28 + i * 0.2);
          this.add(
            i % 2 === 0 ? cap : 'trim',
            P.UNIT_TORUS,
            P.localXf(xf, 0, y, 0, 1.5 - i * 0.22, 1.5 - i * 0.22, 1.5 - i * 0.22, Math.PI / 2, 0, 0),
          );
        }
        P.addSphere(this, cap, xf, 0, top + h + 0.2, 0, 0.3);
        break;
      }
      case 'dish': {
        const h = Math.min(room, 4.5);
        P.addCyl(this, 'metalDark', xf, 0, top + h * 0.5, 0, 0.11, h);
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2;
          this.add(
            'metal',
            P.UNIT_CONE,
            P.localXf(xf, Math.cos(a) * r * 0.6, top + h * 0.85, Math.sin(a) * r * 0.6, 1.7, 0.7, 1.7, -1.05, a, 0),
          );
          P.addSphere(this, cap, xf, Math.cos(a) * r * 0.6, top + h * 0.85, Math.sin(a) * r * 0.6, 0.11);
        }
        break;
      }
      case 'lattice': {
        const h = Math.min(room, 6.5);
        const legs: THREE.Vector3[] = [];
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
          const x = Math.cos(a) * r;
          const z = Math.sin(a) * r;
          P.addCyl(this, 'metalDark', xf, x * 0.5, top + h * 0.5, z * 0.5, 0.06, h, 0, 0, 0);
          legs.push(new THREE.Vector3(x * 0.5, top + h, z * 0.5));
        }
        for (let i = 0; i < 4; i++) {
          P.addTubeBetween(this, 'metalDark', xf, legs[i], legs[(i + 1) % 4], 0.04);
          P.addTubeBetween(this, cap, xf, legs[i], legs[(i + 2) % 4], 0.035);
        }
        P.addSphere(this, cap, xf, 0, top + h + 0.25, 0, 0.22);
        break;
      }
    }
  }

  // -- Spawns, gun pads, the centre -----------------------------------------

  /**
   * Height of the walkable surface under a spawn or pad point.
   *
   * Asked of `surfaceUnder`, not of the height grid. The grid reports the tallest solid over
   * the whole column, and the arena's centre spawn stands directly under a 52 m antenna
   * mast — so the old query came back with the mast, failed its own sanity check, and fell
   * back to the *spawn's* Y, which sits 60 cm above the deck. The result was the helipad
   * mark, its touchdown ring and its keep-clear boxes all floating over the pad they are
   * painted on, in the middle of the map.
   */
  private floorUnder(p: Vec3): number {
    const s = this.oracle.surfaceUnder(p.x, p.z, p.y + 0.4);
    if (s > -Infinity && p.y - s < SPAWN_FLOOR_PROBE) return s;
    const h = this.oracle.heightAt(p.x, p.z);
    return Math.abs(h - p.y) < SPAWN_FLOOR_PROBE ? h : p.y;
  }

  /**
   * A team's end of the roof.
   *
   * **This is the fix for the single worst thing in the previous screenshot.** The most
   * saturated element in the frame was a pair of enormous glowing blue arcs painted on the
   * ground — a 0.42 m full-chroma band at 3.6 m radius plus two emissive rings at 3.0 m and
   * 4.4 m — while the architecture around them was neutral grey. DESIGN.md 4 is explicit:
   * *if a screenshot's most saturated element is something painted on the ground, the colour
   * budget has been spent in the wrong place.* It was, and it was spent there twice, in the
   * one place the player's camera points at most.
   *
   * The information a spawn marker has to carry has not changed, so it is carried by three
   * things that are architecture instead of paint:
   *
   *  - **A painted outline.** One 12 cm line of team colour and one ink line inboard of it.
   *    An outline says "this is the pad" exactly as well as a filled disc does, and it costs
   *    the frame a few square metres instead of fifty.
   *  - **A low parapet in the team's colour**, arcing round the back of the pad. Vertical,
   *    30 cm tall (under `STEP_HEIGHT`, so it is honest), and it puts the saturated colour
   *    on a *plane you look at* rather than one you look down on.
   *  - **A banner on a mast.** The team's identity, up in the air, silhouetted against open
   *    sky, readable from the far end of the map — which the floor decal never was.
   *
   * Nothing here glows. On a sunlit roof an emissive floor ring is both invisible and wrong,
   * and every unit of glow spent in the arena is a unit stolen from the one thing DESIGN.md
   * calls the most important readability rule in the project: a chambered gun.
   */
  private buildTeamZone(team: number, spawns: Vec3[], gunSpawn: Vec3): void {
    const color = TEAM_COLORS[team] ?? PALETTE.neutral;
    // The spawn pad is the second of the two places full-chroma team colour is spent (the
    // other is the wall skirting). It is a few square metres of deck that a player has to
    // find in the first half-second of a round, it is under their feet rather than behind
    // an enemy, and it is surrounded by white — so it can be as saturated as it likes.
    const paint: MatKey = team === 0 ? 'teamBandA' : 'teamBandB';
    // The name the HUD uses. A floor stencil that disagrees with the scoreboard is worse
    // than no stencil at all.
    const label = TEAM_NAMES[team] ?? TEAM_NAMES[0];

    const cx = spawns.reduce((s, p) => s + p.x, 0) / Math.max(1, spawns.length);
    const cz = spawns.reduce((s, p) => s + p.z, 0) / Math.max(1, spawns.length);
    const cy = this.floorUnder({ x: cx, y: spawns[0]?.y ?? this.oracle.floorY, z: cz });
    const centre = new THREE.Vector3(cx, cy, cz);

    const toCentre = Math.atan2(this.arena.centerSpawn.z - cz, this.arena.centerSpawn.x - cx);
    const away = toCentre + Math.PI;

    // The pad, as an OUTLINE. One team line and one ink line, both 12 cm or less.
    P.groundRing(this, centre, 3.6, 0.12, paint);
    P.groundRing(this, centre, 3.28, 0.05, 'trim');

    // The low team parapet round the back of the pad, a shade canopy over it, and the
    // banner mast behind that. Three pieces of *architecture* in the team's colour, which
    // is where spawn identity lives now — DESIGN.md 4 is explicit that if the most
    // saturated thing in a frame is painted on the ground, the budget went to the wrong
    // place, and a spawn is where that temptation is strongest.
    this.spawnParapet(centre, away, paint);
    this.spawnCanopy(centre, away, paint);

    const mast = new THREE.Vector3(
      cx + Math.cos(away) * SPAWN_MAST_RADIUS,
      cy,
      cz + Math.sin(away) * SPAWN_MAST_RADIUS,
    );
    const field: MatKey = team === 0 ? 'teamA' : 'teamB';
    // `bannerMast`'s banner plane faces its local +Z, which yaw ry sends to
    // (sin ry, 0, cos ry); pointing that at the middle of the map is ry = pi/2 - toCentre.
    const boards = P.bannerMast(this, mast, 6.4, Math.PI * 0.5 - toCentre, field, paint);
    const nameCell = this.atlas.label(label);
    if (nameCell) {
      for (const board of boards) {
        this.decal({
          cell: nameCell,
          pos: board.pos,
          normal: board.normal,
          width: 1.25,
          // The team's own colour, out of its own pale field. This is the one saturated
          // stencil in the arena, and it is now six metres in the air rather than underfoot.
          color,
          strength: 1,
        });
      }
    }

    // The pad's name, stencilled on the deck in the kit's one ink at the kit's one weight.
    // It used to be 5.2 m of full-chroma team colour lying flat on the floor, which is the
    // exact failure DESIGN.md 4 names. The team's colour is on the parapet and the banner
    // now; the deck only has to say which pad this is, and ink says that.
    if (nameCell) {
      this.decal({
        cell: nameCell,
        pos: new THREE.Vector3(cx, cy + P.FLOOR_DECAL_LIFT, cz),
        normal: new THREE.Vector3(0, 1, 0),
        width: 3.0,
        color: PALETTE.ink,
        strength: P.SIGNAGE_STRENGTH,
        rotation: toCentre,
      });
    }

    // Directional chevrons on the deck pointing at the middle of the map. Ink, one weight,
    // one size, one spacing — the same mark the ramps and the runnable walls use, so a
    // player learns one shape and it means one thing everywhere.
    for (let i = 0; i < 3; i++) {
      const d = 5.6 + i * 1.5;
      this.decalKind('chevron', {
        pos: new THREE.Vector3(cx + Math.cos(toCentre) * d, cy + P.FLOOR_DECAL_LIFT, cz + Math.sin(toCentre) * d),
        normal: new THREE.Vector3(0, 1, 0),
        width: CHEVRON_WIDTH,
        color: PALETTE.ink,
        strength: P.SIGNAGE_STRENGTH,
        rotation: -toCentre + Math.PI * 0.5,
      });
    }

    // Individual spawn studs: a painted outline where each player stands, and nothing else.
    // The emissive ring that used to sit under every one of these was one more glowing
    // thing on the floor competing with the gun.
    for (const s of spawns) {
      const y = this.floorUnder(s);
      P.groundRing(this, new THREE.Vector3(s.x, y, s.z), 0.62, 0.07, paint, 20);
      this.decalKind('ring', {
        pos: new THREE.Vector3(s.x, y + P.FLOOR_DECAL_LIFT, s.z),
        normal: new THREE.Vector3(0, 1, 0),
        width: SIGN_TIERS[1] * 0.7,
        color: PALETTE.ink,
        strength: P.SIGNAGE_STRENGTH,
      });
    }

    // Gun spawn pad — the most important marker on each half of the map.
    this.buildGunPad(gunSpawn, paint);

    // Team banners on nearby walls.
    this.bannerPass(cx, cz, 16, color, label);
  }

  /**
   * A low parapet in the team's colour, arcing round the back of a spawn pad.
   *
   * This is where the team's saturated colour goes now that it is off the floor, and the
   * geometry is chosen so that it can be as saturated as it likes: it is behind the player
   * as they leave the pad, it faces into the arena rather than out at an enemy sightline,
   * and — at 30 cm, under `STEP_HEIGHT` — it is honest about hiding nothing. A full-chroma
   * wall an enemy could stand in front of would be a wall that eats their silhouette; a
   * knee-high one nobody can stand behind cannot.
   *
   * The white coping on top is the same detail the roof parapets carry, so a spawn reads as
   * part of the same building rather than as a gameplay decoration bolted onto it.
   */
  private spawnParapet(centre: THREE.Vector3, away: number, band: MatKey): void {
    const id = new THREE.Matrix4();
    const radius = 4.4;
    const segs = 13;
    const arc = Math.PI * 0.92;
    const len = ((arc * radius) / segs) * 1.1;
    for (let i = 0; i < segs; i++) {
      const a = away + ((i + 0.5) / segs - 0.5) * arc;
      const x = centre.x + Math.cos(a) * radius;
      const z = centre.z + Math.sin(a) * radius;
      // Local +X runs along the tangent; yaw maps +X to angle t as ry = -t.
      const ry = -(a + Math.PI * 0.5);
      P.addBox(this, band, id, x, centre.y + P.PARAPET_HEIGHT * 0.5, z, len, P.PARAPET_HEIGHT, 0.28, 0, ry, 0);
      P.addBox(
        this,
        'panel',
        id,
        x,
        centre.y + P.PARAPET_HEIGHT + P.PARAPET_COPING * 0.5,
        z,
        len,
        P.PARAPET_COPING,
        0.36,
        0,
        ry,
        0,
      );
    }
  }

  /**
   * A SHADE CANOPY over the back of a spawn pad.
   *
   * The third piece of team-coloured architecture at a spawn, after the parapet and the
   * banner, and the one that carries the "beautiful day" note the playtest asked for: a
   * tensioned canvas awning is what a terrace has, and it puts the team's saturated colour
   * on a *fascia band up in the air* rather than anywhere near the deck.
   *
   * Honest three ways. The canvas is translucent (`canvas`, opacity 0.34) so a player under
   * it is still visible from above — an opaque lid over a spawn would be a soft ceiling that
   * collision does not have. The masts are 8 cm. And the whole thing sits *behind* the pad,
   * on the far side from the middle of the map, so it never overhangs the route a player
   * takes out of spawn or the sightline anybody has into it.
   */
  private spawnCanopy(centre: THREE.Vector3, away: number, band: MatKey): void {
    const id = new THREE.Matrix4();
    // A frame whose local +Z points away from the middle of the map: yaw ry sends +Z to
    // (sin ry, 0, cos ry), so pointing that along `away` is ry = pi/2 - away.
    const frame = P.localXf(id, centre.x, centre.y, centre.z, 1, 1, 1, 0, Math.PI * 0.5 - away, 0);
    const halfW = 3.6;
    // The canvas stops short of the banner mast (`SPAWN_MAST_RADIUS`), which is 6.4 m tall
    // and would otherwise spear straight through the middle of it.
    const near = 2.4;
    const far = 5.2;
    const h = P.SAIL_HEIGHT;

    for (const sx of [-1, 1]) {
      for (const dz of [near, far]) {
        P.addCyl(this, 'rail', frame, sx * halfW, h * 0.5, dz, P.SAIL_MAST_STOCK * 0.5, h, 0, 0, 0, true);
        P.addBox(this, 'trim', frame, sx * halfW, 0.05, dz, 0.34, 0.1, 0.34);
      }
    }
    const midZ = (near + far) * 0.5;
    const depth = far - near;
    // The canvas, and a second smaller sheet over it so the sail reads as hipped and
    // tensioned rather than as a flat lid.
    P.addBox(this, 'canvas', frame, 0, h, midZ, halfW * 2 + 0.5, 0.04, depth + 0.5);
    P.addBox(this, 'canvas', frame, 0, h + 0.3, midZ, halfW * 1.5, 0.04, depth * 0.72);
    // The team's colour, as a fascia band along the leading edge — about a square metre of
    // full chroma, three and a half metres up, facing the middle of the map.
    P.addBox(this, band, frame, 0, h - 0.12, near - 0.2, halfW * 2 + 0.5, 0.3, 0.07);
    P.addBox(this, 'panel', frame, 0, h + 0.06, near - 0.2, halfW * 2 + 0.6, 0.07, 0.12);
    // Edge cables back down to the outer mast feet, so it is obviously in tension.
    for (const sx of [-1, 1]) {
      P.addTubeBetween(
        this,
        'rubber',
        frame,
        new THREE.Vector3(sx * halfW, h, far),
        new THREE.Vector3(sx * (halfW + 1.5), 0.1, far + 1.6),
        0.02,
      );
    }
  }

  /**
   * The gun pad: a marked-out equipment stand on the roof, found by a shape in the air.
   *
   * Two things went from this and both were glow on the ground. The emissive rings are gone
   * for the same reason the spawn's are — a lit floor ring is invisible in sunlight and
   * costs the frame saturated colour in the worst possible place — and so is the vertical
   * light shaft, which under an open sky was a column of haze standing over the one square
   * metre a player most needs to read a silhouette against.
   *
   * What replaces them is a **stanchion with a small pennant**, set a metre and a half off
   * the pad so it never fouls the gun. It is 8 cm of steel and a 40 cm flag, it hides
   * nothing, and it does the job the glow was supposed to do better: it is visible over a
   * parapet, from across the map, and at any time of day, because it works by silhouette
   * against sky rather than by adding light to a lit world.
   */
  private buildGunPad(p: Vec3, band: MatKey): void {
    const y = this.floorUnder(p);
    const c = new THREE.Vector3(p.x, y, p.z);
    P.groundRing(this, c, 1.5, 0.2, 'hazard');
    P.groundRing(this, c, 1.86, 0.06, band, 28);
    this.decalKind('ring', {
      pos: new THREE.Vector3(p.x, y + P.FLOOR_DECAL_LIFT, p.z),
      normal: new THREE.Vector3(0, 1, 0),
      width: SIGN_TIERS[1],
      color: PALETTE.ink,
      strength: P.SIGNAGE_STRENGTH,
    });

    // The stanchion. Offset toward the middle of the map so it marks the pad without
    // standing over it.
    const to = Math.atan2(this.arena.centerSpawn.z - p.z, this.arena.centerSpawn.x - p.x);
    const sx = p.x + Math.cos(to) * 1.55;
    const sz = p.z + Math.sin(to) * 1.55;
    const id = new THREE.Matrix4();
    const h = 2.9;
    P.addBox(this, 'trim', id, sx, y + 0.05, sz, 0.42, 0.1, 0.42);
    P.addCyl(this, 'rail', id, sx, y + h * 0.5, sz, 0.045, h);
    P.addSphere(this, 'metal', id, sx, y + h + 0.07, sz, 0.07);
    // A small pennant in the team's full-chroma colour: about a third of a square metre of
    // saturation, up at head height, where it competes with nothing on the ground.
    P.addBox(this, band, id, sx + Math.cos(to) * 0.3, y + h - 0.34, sz + Math.sin(to) * 0.3, 0.6, 0.44, 0.03, 0, -to, 0);
  }

  /**
   * Giant team banners painted on whatever wall faces the spawn.
   *
   * Two, not three. These are the largest saturated fields in the game and they are the
   * right place to spend the team hue — but a third one starts to wrap the spawn in colour,
   * and a spawn wrapped in signal red is a spawn where a red enemy silhouette does not read.
   */
  private bannerPass(cx: number, cz: number, radius: number, color: number, label: string): void {
    const cell = this.atlas.label(label);
    let placed = 0;
    for (const b of this.arena.brushes) {
      if (placed >= TEAM_BANNERS_PER_END) break;
      if (b.decorative || b.half.y < 2.2) continue;
      const dx = b.pos.x - cx;
      const dz = b.pos.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 > radius * radius || d2 < 9) continue;

      const half = vec(b.half);
      const xf = brushMatrix(b);
      const exposed = this.exposedFaces(xf, half);
      const faces = P.boxFaces(half);
      // The face whose outward normal points back toward the spawn.
      let bestFace: P.Face | null = null;
      let bestDot = 0.35;
      for (const f of faces) {
        if (!P.isVerticalFace(f) || !exposed[f.index] || f.w < 2.4 || f.h < 2.4) continue;
        const n = f.n.clone().transformDirection(xf);
        const dot = (-dx * n.x - dz * n.z) / Math.sqrt(d2);
        if (dot > bestDot) {
          bestDot = dot;
          bestFace = f;
        }
      }
      if (!bestFace) continue;

      const w = Math.min(bestFace.w * 0.8, 7);
      const isA = color === PALETTE.teamA;
      const field: MatKey = isA ? 'teamA' : 'teamB';
      const bandMat: MatKey = isA ? 'teamBandA' : 'teamBandB';
      const bh = bestFace.h * 0.62;
      P.addFaceSlab(this, 'trim', xf, bestFace, 0, 0, w + 0.3, bh + 0.3, 0.04, 0);
      P.addFaceSlab(this, field, xf, bestFace, 0, 0, w, bh, 0.03, 0.04);
      // A full-chroma head and foot band on a pale field. The board still reads as that
      // team's from across the atrium — arguably harder than it did as a solid colour,
      // because the saturated strips now have something pale to be saturated *against* —
      // and a player standing in front of it keeps their silhouette.
      for (const s of [-1, 1]) {
        P.addFaceSlab(this, bandMat, xf, bestFace, 0, s * (bh * 0.5 - TEAM_BAND_HEIGHT * 0.5), w, TEAM_BAND_HEIGHT, 0.04, 0.07);
      }
      if (cell) {
        this.decal({
          cell,
          pos: P.facePoint(bestFace, 0, 0, new THREE.Vector3(), 0.12).applyMatrix4(xf),
          normal: bestFace.n.clone().transformDirection(xf),
          width: w * 0.85,
          // The team's own colour, out of its own pale plaster. Ink would name the board
          // but not the team; white on pale would not read at all.
          color,
          strength: 1,
        });
      }
      // Slate reveals down the sides, so the painted field has a hard edge to end on.
      for (const side of [-1, 1]) {
        P.addFaceSlab(this, 'trim', xf, bestFace, side * (w * 0.5 + 0.28), 0, 0.1, bestFace.h * 0.72, 0.05, 0);
      }
      placed++;
    }
  }

  /**
   * The contested middle: a helipad.
   *
   * The most exposed place on the map has to be instantly nameable from anywhere, and on a
   * roof there is exactly one mark that does that job — a bordered circle with an H in it.
   * It is universally read, it is legible from the air and from across the map, it is the
   * only large ground mark in the arena, and it is painted in the same ink at the same
   * weight as every other stencil in the kit. It also earns the callout: "the pad" now
   * means something a player can see rather than a set of concentric rings.
   *
   * Painted rather than lit, like everything else on the ground. A shaft of light over the
   * middle would put haze on the one square metre where a player most needs to read a
   * silhouette clearly.
   */
  private buildCentre(): void {
    const p = this.arena.centerSpawn;
    const y = this.floorUnder(p);
    const c = new THREE.Vector3(p.x, y, p.z);
    const lift = P.FLOOR_DECAL_LIFT;

    this.decalKind('helipad', {
      pos: new THREE.Vector3(p.x, y + lift, p.z),
      normal: new THREE.Vector3(0, 1, 0),
      width: 11.0,
      height: 11.0,
      color: PALETTE.ink,
      strength: P.SIGNAGE_STRENGTH,
    });
    // A painted touchdown ring outside the mark, and a hazard band at the perimeter. Both
    // are paint on the deck a couple of centimetres thick; neither is cover.
    P.groundRing(this, c, 6.6, 0.22, 'trim');
    P.groundRing(this, c, 6.2, 0.1, 'panel');

    for (const side of [-1, 1]) {
      this.decalKind('keepClear', {
        pos: new THREE.Vector3(p.x + side * 8.6, y + lift, p.z),
        normal: new THREE.Vector3(0, 1, 0),
        width: SIGN_TIERS[2] * 0.64,
        color: PALETTE.ink,
        strength: P.SIGNAGE_STRENGTH,
      });
    }

    /**
     * A WINDSOCK at the edge of the pad.
     *
     * A helipad without one is a circle with a letter in it. This is the whole of what
     * `accentSun` is rationed for — machinery yellow, on a piece of machinery — and at
     * roughly a third of a square metre, three metres up and off to one side of the mark, it
     * is a bright note in the middle of the map rather than a field of colour. It is also
     * the one object in the arena that says the air is moving, which is most of the
     * difference between a bright day and a still render.
     */
    for (const [du, dv] of [
      [9.4, -9.4],
      [-9.4, 9.4],
    ] as [number, number][]) {
      // Only where the pad actually continues that far: the mark is centred on the deck but
      // the deck is whatever shape the arena author built, and a windsock hanging in the air
      // off the corner of it would be exactly the kind of unverified decoration this file
      // spends most of its length refusing to emit.
      const sx = p.x + du;
      const sz = p.z - dv;
      if (Math.abs(this.oracle.surfaceUnder(sx, sz, y + 0.5) - y) > 0.6) continue;
      P.windsock(this, new THREE.Matrix4(), flatFaceAt(c), du, dv, 3.2, Math.PI * 0.25);
    }
  }

  // -- Grapple anchors -----------------------------------------------------

  /**
   * Anchors are interactive, so they get their own meshes, their own animated shaders and
   * an ink outline. They must never blend into decoration — see `anchorMat`.
   *
   * All nineteen fixtures merge into one body mesh, one ink shell and one halo mesh: three
   * draw calls for the whole set, however many anchors an arena declares.
   */
  /**
   * Where the crane that carries an anchor stands.
   *
   * Directly under the anchor if there is a deck there, otherwise the nearest one — searched
   * outward in rings, taking the *highest* deck found at the first radius that hits anything,
   * because that is the shortest mast and therefore the least new geometry in the frame.
   *
   * Each candidate is sampled on a 3x3 rather than as a single point, and the answer is the
   * LOWEST surface in that patch. A single probe lands on whatever piece of furniture happens
   * to be under the anchor — on this arena, four of them sit over a water tank — and a
   * twenty-metre crane growing out of the top of a water tank is a worse lie than the
   * floating fixture it replaced. The lowest sample in a 3.5 m patch is the deck.
   */
  private anchorRoot(p: Vec3): THREE.Vector3 | null {
    const ceil = p.y - 2.0;
    /**
     * The deck under (x, z), or -Infinity when there is not enough of one to stand a crane
     * on. Sampled across 4.4 m and requiring seven of nine hits, which is what makes this a
     * test for a *deck* rather than for a surface: the pipe bridge is 3.6 m wide, so a
     * single probe happily sited a twenty-metre mast in the middle of the one crossing on
     * the map with no cover on it. Seven of nine at ±2.2 m rejects it and the search walks
     * out to the apron instead, which is where a crane reaching over that canyon belongs.
     */
    const patch = (x: number, z: number): number => {
      let lowest = Infinity;
      let hits = 0;
      for (const ox of [-2.2, 0, 2.2]) {
        for (const oz of [-2.2, 0, 2.2]) {
          const s = this.oracle.surfaceUnder(x + ox, z + oz, ceil);
          if (s === -Infinity) continue;
          hits++;
          // The lowest sample in the patch, not the highest: a single probe lands on
          // whatever furniture is under the anchor, and four of these sit over a water tank.
          if (s < lowest) lowest = s;
        }
      }
      return hits >= 7 ? lowest : -Infinity;
    };

    /**
     * The mast has to fit where it stands.
     *
     * Four of the anchors sit above a billboard, so the obvious root — the deck directly
     * underneath — would drive a twenty-metre mast straight up through a thirteen-metre
     * sign. A candidate is only a candidate if its column is clear all the way to the jib.
     */
    const columnIsClear = (x: number, z: number, y0: number, y1: number): boolean => {
      const n = 8;
      let blocked = 0;
      for (let i = 0; i < n; i++) {
        _p1.set(x, y0 + 1.0 + ((y1 - y0 - 1.0) * (i + 0.5)) / n, z);
        if (this.oracle.contains(_p1)) blocked++;
      }
      return blocked <= 1;
    };
    const beamY = p.y + ANCHOR_HOOK_DROP;

    let root: THREE.Vector3 | null = null;
    const direct = patch(p.x, p.z);
    if (direct > -Infinity && columnIsClear(p.x, p.z, direct, beamY)) {
      root = new THREE.Vector3(p.x, direct, p.z);
    } else {
      for (const radius of [4, 8, 12, 17, 23]) {
        let best: THREE.Vector3 | null = null;
        for (let i = 0; i < 16; i++) {
          const t = (i / 16) * Math.PI * 2;
          const x = p.x + Math.cos(t) * radius;
          const z = p.z + Math.sin(t) * radius;
          const s = patch(x, z);
          if (s === -Infinity || !columnIsClear(x, z, s, beamY)) continue;
          // Highest deck at the first radius that hits: the shortest mast, and therefore the
          // least new geometry standing in the frame.
          if (!best || s > best.y) best = new THREE.Vector3(x, s, z);
        }
        if (best) {
          root = best;
          break;
        }
      }
    }
    if (!root) return null;

    /**
     * Walk the crane off the middle of the deck.
     *
     * A crane in the middle of a roof stands in the middle of the route across it, and this
     * is decorative geometry, so a player runs through it. Two places it can go instead, in
     * order of preference, and both of them are where a real one would be:
     *
     *  - **A drop.** On the parapet line with the jib out over the void. That is the image
     *    the brief asks for, it puts the mast out of the running lane, and it makes the jib
     *    do what a jib is for — reach from somewhere you stand to somewhere you cannot.
     *  - **A wall.** Tight against the flank of whatever rises beside the deck, which is
     *    where a facade davit is bolted. On the mid ring — 46 m square, so no edge is within
     *    reach — this is what tucks the two pad cranes against the core instead of leaving
     *    them standing in the crossroads.
     */
    const walk = (wantDrop: boolean, inset: number): boolean => {
      let bestDist = Infinity;
      let bestX = 0;
      let bestZ = 0;
      for (let i = 0; i < 16; i++) {
        const t = (i / 16) * Math.PI * 2;
        const ux = Math.cos(t);
        const uz = Math.sin(t);
        for (let d = 3.0; d <= 8.0; d += 0.7) {
          const s = this.oracle.surfaceUnder(root!.x + ux * d, root!.z + uz * d, ceil);
          const isDrop = s === -Infinity || root!.y - s > 3.0;
          const isWall = s > -Infinity && s - root!.y > 3.0;
          if (wantDrop ? !isDrop : !isWall) continue;
          if (d < bestDist) {
            bestDist = d;
            bestX = ux;
            bestZ = uz;
          }
          break;
        }
      }
      if (bestDist === Infinity) return false;
      const shift = bestDist - inset;
      if (shift <= 0.4) return true;
      const nx = root!.x + bestX * shift;
      const nz = root!.z + bestZ * shift;
      // The probe only locates the boundary to within its own step, so confirm the shifted
      // position is still a deck before committing. A crane base hanging over the void is a
      // worse answer than a crane in the middle of the roof.
      if (patch(nx, nz) === -Infinity || !columnIsClear(nx, nz, root!.y, beamY)) return true;
      root!.x = nx;
      root!.z = nz;
      return true;
    };
    // 2.2 m back from a drop leaves the 0.9 m mast footprint wholly on the roof even though
    // the probe only locates the edge to within its 0.7 m step.
    if (!walk(true, 2.2)) walk(false, 1.5);
    return root;
  }

  /**
   * Is this anchor already carried by something the arena itself built?
   *
   * Three of them sit on top of real masts, and a crane there would be a second structure
   * holding up the first. The test is whether anything solid *tops out near the hook* — not
   * whether the column below is solid, which was the first version and which counted the
   * four anchors floating four metres above a billboard as supported. They are not: nothing
   * reaches them, which is precisely why they need a crane.
   */
  private anchorIsSupported(p: Vec3): boolean {
    const top = this.oracle.surfaceUnder(p.x, p.z, p.y - 0.5);
    return top > -Infinity && p.y - top <= 3.5;
  }

  private buildAnchors(): void {
    const anchors = this.arena.anchors;
    if (anchors.length === 0) return;

    /**
     * THE CRANES. Built before the fixtures, so they share the arena's merged `rail` and
     * `panel` batches and cost no extra draw calls at all.
     *
     * An anchor with nothing holding it up reads as a *pickup*, and on this arena fifteen of
     * the nineteen hang between twelve and twenty-one metres clear of the nearest roof.
     * Silhouette work on the fixture cannot answer the question the eye is actually asking,
     * which is "what is that bolted to". A crane answers it, points at the hook, and is
     * exactly the believable rooftop hardware DESIGN.md 4 asks a grapple point to be.
     *
     * Skipped where the arena already provides the support — the mast anchors sit on top of
     * real masts, and a crane there would be a second structure holding up the first.
     */
    for (const a of anchors) {
      const root = this.anchorRoot(a.pos);
      if (!root || a.pos.y - root.y <= 2.5) continue;
      if (this.anchorIsSupported(a.pos)) continue;
      P.anchorRig(this, root, vec(a.pos), ANCHOR_HOOK_DROP);
    }

    const base = buildAnchorGeometry();
    const haloBase = buildAnchorHaloGeometry();
    const bodies: THREE.BufferGeometry[] = [];
    const halos: THREE.BufferGeometry[] = [];

    for (const a of anchors) {
      // uv.x carries the pulse phase, so anchors breathe independently and the eye is
      // caught by movement rather than having to sweep for a static shape.
      const phase = this.random();
      const xf = new THREE.Matrix4().makeTranslation(a.pos.x, a.pos.y, a.pos.z);

      const body = base.clone();
      body.applyMatrix4(xf);
      setUvX(body, phase);
      bodies.push(body);

      const halo = haloBase.clone();
      halo.applyMatrix4(xf);
      setUvX(halo, phase);
      // The billboard needs the fixture's own centre, because the quad's local offset is
      // recovered as (position - aCenter) in the vertex shader.
      const n = halo.getAttribute('position').count;
      const centre = new Float32Array(n * 3);
      for (let v = 0; v < n; v++) {
        centre[v * 3] = a.pos.x;
        centre[v * 3 + 1] = a.pos.y;
        centre[v * 3 + 2] = a.pos.z;
      }
      halo.setAttribute('aCenter', new THREE.BufferAttribute(centre, 3));
      halos.push(halo);
    }
    base.dispose();
    haloBase.dispose();

    const bodyGeo = bodies.length === 1 ? bodies[0] : mergeGeometries(bodies, false);
    const haloGeo = halos.length === 1 ? halos[0] : mergeGeometries(halos, false);
    if (bodies.length > 1) for (const g of bodies) g.dispose();
    if (halos.length > 1) for (const g of halos) g.dispose();
    if (!bodyGeo || !haloGeo) return;
    bodyGeo.computeBoundingSphere();
    haloGeo.computeBoundingSphere();

    const mat = this.anchorMat();
    const mesh = new THREE.Mesh(bodyGeo, mat);
    mesh.name = 'grapple-anchors';
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = 1;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    this.disposables.push(bodyGeo);

    // The ink shell. This is what holds the fixture together against a bright sky, where
    // an emissive alone would blow out and a dark mast alone would be a smudge.
    const shell = new THREE.Mesh(bodyGeo, this.outlineMat());
    shell.name = 'grapple-anchors-ink';
    shell.castShadow = false;
    shell.receiveShadow = false;
    shell.renderOrder = 0;
    shell.frustumCulled = false;
    this.group.add(shell);

    const halo = this.anchorHaloMat();
    const haloMesh = new THREE.Mesh(haloGeo, halo);
    haloMesh.name = 'grapple-anchor-halos';
    haloMesh.castShadow = false;
    haloMesh.receiveShadow = false;
    haloMesh.renderOrder = 4;
    // Billboarded in the vertex shader, so the authored bounds do not describe where the
    // quads actually land.
    haloMesh.frustumCulled = false;
    this.group.add(haloMesh);
    this.disposables.push(haloGeo);

    // One mesh drives the shared clock for both materials.
    const t0 = performance.now();
    mesh.onBeforeRender = (): void => {
      const t = (performance.now() - t0) * 0.001;
      mat.uniforms.uTime.value = t;
      mat.uniformsNeedUpdate = true;
      halo.uniforms.uTime.value = t;
      halo.uniformsNeedUpdate = true;
    };
  }

  // -- Overhead clutter, skyline, void --------------------------------------

  /** Cables and hanging lamps, strung only across verified-clear airspace. */
  private buildOverhead(): void {
    const tops: { p: THREE.Vector3; half: THREE.Vector3 }[] = [];
    for (const b of this.arena.brushes) {
      if (b.decorative) continue;
      const top = b.pos.y + b.half.y;
      if (top < this.oracle.floorY + 4) continue;
      if (b.half.x * b.half.z * 4 < 2) continue;
      tops.push({ p: new THREE.Vector3(b.pos.x, top, b.pos.z), half: vec(b.half) });
    }
    if (tops.length < 2) return;

    let strung = 0;
    const maxCables = 14;
    for (let i = 0; i < tops.length && strung < maxCables; i++) {
      for (let j = i + 1; j < tops.length && strung < maxCables; j++) {
        const a = tops[i];
        const b = tops[j];
        const d = a.p.distanceTo(b.p);
        if (d < 9 || d > 30) continue;
        if (this.random() > 0.35) continue;

        const ay = a.p.y + 1.6 + this.random() * 1.4;
        const by = b.p.y + 1.6 + this.random() * 1.4;
        const pa = new THREE.Vector3(a.p.x, ay, a.p.z);
        const pb = new THREE.Vector3(b.p.x, by, b.p.z);
        const sag = d * 0.06;
        const lowest = P.catenaryLowestY(pa, pb, sag);
        if (!this.oracle.spanIsClear(pa, pb, lowest, OVERHEAD_CLEARANCE)) continue;

        // Poles the cable actually hangs from, standing on the roofs.
        for (const [root, y] of [
          [a, ay],
          [b, by],
        ] as [{ p: THREE.Vector3; half: THREE.Vector3 }, number][]) {
          const id = new THREE.Matrix4();
          P.addCyl(this, 'metalDark', id, root.p.x, (root.p.y + y) * 0.5, root.p.z, 0.075, y - root.p.y);
        }
        P.catenaryCable(this, pa, pb, sag, 0.05, 'rubber');

        // Lamps along the span. It is the middle of a bright day, so they are off: a dark
        // housing with a pale lens, silhouetted against the sky. A string of glowing beads
        // overhead would be one more thing pulling the eye away from the gun.
        const lamps = 2 + ((this.random() * 3) | 0);
        for (let k = 1; k <= lamps; k++) {
          const t = k / (lamps + 1);
          const x = pa.x + (pb.x - pa.x) * t;
          const z = pa.z + (pb.z - pa.z) * t;
          const yy = pa.y + (pb.y - pa.y) * t - sag * 1.25 * (1 - Math.abs(t - 0.5) * 2) - 0.3;
          const id = new THREE.Matrix4();
          P.addCyl(this, 'rubber', id, x, yy + 0.18, z, 0.02, 0.36);
          P.addCyl(this, 'metalDark', id, x, yy + 0.04, z, 0.17, 0.1);
          P.addSphere(this, 'panel', id, x, yy - 0.04, z, 0.15);
        }
        strung++;
      }
    }
  }

  /**
   * THE CITY. Entirely outside the arena bounds, non-colliding, and most of the feeling.
   *
   * The arena is a rooftop, and a rooftop is only a rooftop if there is a city under it and
   * a horizon past it (DESIGN.md 4). This is cheap — a couple of hundred unlit boxes that
   * neither cast nor receive — and it is doing more for the brief than any lighting change
   * can, because it is what tells the player where they are standing.
   *
   * Three tiers of aerial perspective, and the near one is the important one. Its roofs are
   * seated `SKYLINE_NEAR_DROP` metres *below* the arena's own decks, so a player looking
   * out over a parapet is looking DOWN onto parapets, water tanks and plant rooms on other
   * roofs — which is the single most direct way to say "you are high up", far more direct
   * than a hazed silhouette on the horizon. Past that the city hazes toward the sky until
   * the far band is barely more than a tint, which is the only depth cue available at that
   * range and therefore has to do all of the work.
   *
   * **The near ring is swept, the rest is sampled**, and that split is the fix for a
   * measured hole: drawing all three tiers from one random spiral left the near ring with
   * twenty-degree gaps, i.e. whole directions in which the player's own roof had no
   * neighbours at all. See `SKYLINE_NEAR_BLOCKS`.
   *
   * Nothing in here is lit from within. It is the middle of a bright day; a distant tower's
   * windows are mirrors, not lamps.
   */
  private buildSkyline(): void {
    const cx = (this.boundsMin.x + this.boundsMax.x) * 0.5;
    const cz = (this.boundsMin.z + this.boundsMax.z) * 0.5;
    const inner = this.arenaSpan * 0.62 + 20;
    /**
     * The lowest deck a player can stand on — NOT `bounds.min.y`.
     *
     * This is the fix for the single most damaging thing in the file after the arena was
     * rebuilt as a rooftop complex. The near city is seated a fixed distance below the play
     * space, and it used to measure that from the arena's bounding box floor. That was the
     * old arena's ground plane, so it worked; the new arena's box floor is 50 m below the
     * kill plane, at the bottom of the building shafts, and the near tier came out with a
     * *negative* height — every one of those roofs collapsed. Which is to say the ring of
     * roofs the player is supposed to look down onto, the thing this file's own header calls
     * "the cheapest and most direct statement of altitude available", was simply not there.
     */
    const deckY = this.regions.length > 0 ? this.regions[0].y : this.boundsMin.y;
    const baseY = this.arena.killPlaneY - 6;
    const reach = this.arenaSpan * 3.2;

    // THE ROOFS NEXT DOOR. Broad and low, with their decks a fixed distance below the play
    // space so the near horizon line lands under the player's feet rather than at eye level —
    // and so the roofs themselves are read as *surfaces*, not silhouettes.
    //
    // Built downward from the roof line rather than upward from a shared base: the roof
    // height is the thing that has to be right (it is what the player looks at), and the
    // block simply has to be tall enough to stand under it.
    //
    // Swept at a fixed angular pitch, alternating between an inner and an outer sub-band, so
    // that "there is always a neighbouring roof out there" is guaranteed by construction
    // rather than left to the seed.
    const pitch = (Math.PI * 2) / SKYLINE_NEAR_BLOCKS;
    const band = this.arenaSpan * SKYLINE_NEAR_BAND;
    for (let i = 0; i < SKYLINE_NEAR_BLOCKS; i++) {
      const a = (i + 0.5) * pitch + (this.random() - 0.5) * 2 * SKYLINE_NEAR_JITTER * pitch;
      const dist = inner + ((i & 1) * 0.5 + this.random() * 0.5) * band;
      const x = cx + Math.cos(a) * dist;
      const z = cz + Math.sin(a) * dist;
      const w = 16 + this.random() * 26;
      const d = 16 + this.random() * 26;
      const roofY = deckY - SKYLINE_NEAR_DROP - this.random() * 22;
      const h = Math.max(24, roofY - baseY);
      P.skylineBlock(this, new THREE.Vector3(x, roofY - h, z), w, h, d, this.random() * Math.PI, 0);
    }

    // The hazed city past it. Two tiers, sampled off a wrapping spiral so the ring reads as
    // depth rather than as a fence, and starting beyond the near band so the two never fight.
    const outer = inner + band;
    for (let i = 0; i < SKYLINE_BLOCKS; i++) {
      const a = (i / SKYLINE_BLOCKS) * Math.PI * 2 * SKYLINE_RINGS + this.random() * 0.09;
      const t = this.random();
      const dist = outer + t * reach;
      const x = cx + Math.cos(a) * dist;
      const z = cz + Math.sin(a) * dist;
      const tier: P.SkylineTier = t < SKYLINE_MID_FRACTION ? 1 : 2;

      const w = 9 + this.random() * 20;
      const d = 9 + this.random() * 20;
      // Far towers run taller, so the horizon stays busy instead of trailing off.
      const h = 30 + this.random() * (80 + dist * 0.4);
      P.skylineBlock(this, new THREE.Vector3(x, baseY, z), w, h, d, this.random() * Math.PI, tier);
    }
  }

  /** The glowing grid far below: falling out of the world has to look like falling. */
  private buildVoid(): void {
    const cx = (this.boundsMin.x + this.boundsMax.x) * 0.5;
    const cz = (this.boundsMin.z + this.boundsMax.z) * 0.5;
    const size = this.arenaSpan * 5 + 200;
    const y = this.arena.killPlaneY - 26;
    const tiles = Math.max(8, Math.round(size / 14));
    P.addOwned(
      this,
      'void',
      P.texturedFloorQuad(size, size, tiles, tiles),
      new THREE.Matrix4().makeTranslation(cx, y, cz),
    );
  }

  // -- Assembly ------------------------------------------------------------

  build(): THREE.Group {
    this.group.name = `arena:${this.arena.name}`;

    for (const b of this.arena.brushes) this.buildBrush(b);
    for (const r of this.arena.ramps) this.buildRamp(r);
    // Region names are chosen after every wall is known, so the biggest walls win and the
    // name stays rare enough to be a landmark.
    this.flushNameSites();
    this.buildLandmarks();
    this.buildTeamZone(0, this.arena.spawnsA, this.arena.gunSpawnA);
    this.buildTeamZone(1, this.arena.spawnsB, this.arena.gunSpawnB);
    this.buildCentre();
    this.buildOverhead();
    this.buildSkyline();
    this.buildVoid();
    this.buildAnchors();

    this.flush();

    const decalMat = makeDecalMaterial(this.atlas, this.gradient);
    const glowMat = makeDecalGlowMaterial(this.atlas);
    for (const mesh of this.decals.build(decalMat, glowMat)) this.group.add(mesh);
    this.disposables.push(decalMat, glowMat, this.atlas);

    const self = this;
    this.group.userData.dispose = (): void => self.dispose();
    this.group.userData.arenaName = this.arena.name;
    // Regions are storeys, so a callout resolves to a height, not a map half. Published
    // from the same source of truth the paint uses, so the HUD can never disagree with the
    // building about what a player should call the deck they are standing on.
    this.group.userData.regions = this.regions.map((r) => ({
      name: r.name,
      y: r.y,
      numeral: r.numeral,
      color: paintColor(r.paint),
    }));
    return this.group;
  }

  /** Merge every bucket into one mesh, plus one ink shell per outlined bucket. */
  private flush(): void {
    for (const [key, geos] of this.buckets) {
      if (geos.length === 0) continue;
      const merged = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
      if (!merged) {
        for (const g of geos) g.dispose();
        continue;
      }
      if (geos.length > 1) for (const g of geos) g.dispose();
      merged.computeBoundingSphere();

      const material = this.material(key);
      const mesh = new THREE.Mesh(merged, material);
      mesh.name = `arena-${key}`;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      const shadow = P.SHADOW_MATS.has(key);
      mesh.castShadow = shadow;
      mesh.receiveShadow = shadow;
      if (key === 'grate') {
        mesh.castShadow = false;
        mesh.receiveShadow = true;
      }
      if (
        key === 'skylineNear' ||
        key === 'skyline' ||
        key === 'skylineFar' ||
        key === 'skylineGlass' ||
        key === 'void'
      ) {
        mesh.frustumCulled = true;
        mesh.renderOrder = -2;
      }
      this.group.add(mesh);
      this.disposables.push(merged);

      if (P.OUTLINED_MATS.has(key)) {
        const shell = new THREE.Mesh(merged, this.outlineMat());
        shell.name = `arena-${key}-ink`;
        shell.matrixAutoUpdate = false;
        shell.updateMatrix();
        shell.castShadow = false;
        shell.receiveShadow = false;
        // Ahead of the shaded pass: same program for every shell, so they batch.
        shell.renderOrder = -1;
        // The material tracks the drawing buffer itself, so the line holds its CSS-pixel
        // weight through window resizes without needing a hook from the renderer.
        shell.onBeforeRender = (r): void => {
          r.getDrawingBufferSize(_bufferSize);
          const mat = this.outlineMat();
          const h = _bufferSize.y / r.getPixelRatio();
          if (mat.uniforms.uViewportHeight.value !== h) {
            mat.uniforms.uViewportHeight.value = h;
            mat.uniformsNeedUpdate = true;
          }
        };
        this.group.add(shell);
      }
    }
    this.buckets.clear();
    if (this.outlineMaterial) this.disposables.push(this.outlineMaterial);
  }

  private dispose(): void {
    for (const d of this.disposables) d.dispose();
    for (const m of this.materials.values()) m.dispose();
    for (const t of this.textures) t.dispose();
    if (this.anchorMaterial) this.anchorMaterial.dispose();
    if (this.haloMaterial) this.haloMaterial.dispose();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        // attachOutline mints a material per call; free those too.
        if (o.userData.__otIsOutline === true && !Array.isArray(o.material)) o.material.dispose();
      }
    });
    this.group.clear();
  }
}

// ---------------------------------------------------------------------------
// Grapple anchor geometry
// ---------------------------------------------------------------------------

/**
 * THE ANCHOR FIXTURE: a rooftop rigging gantry, built once and cloned per anchor.
 *
 * `uv.y` encodes the shader role: 0 = graphite frame, 1 = white paint, 2 = safety band.
 * `uv.x` is overwritten per anchor with a pulse phase.
 *
 * A **4.6 m beam** with a stay truss over it and a sheave-and-hook block hanging under it.
 * The player's grapple attaches at the hook, so the fixture is built about that point: the
 * beam runs across at `ANCHOR_HOOK_DROP` above the origin and the kingpost rises from it.
 *
 * Why this shape and not the old radial blade star:
 *
 *  - It is a **long horizontal with a triangle over it**. A roofscape is made of long
 *    horizontals too, but none of them is 4.6 m of isolated beam hanging in open air with a
 *    pendant under it, and the truss triangle is a shape the arena contains nowhere else.
 *  - The beam is **cross-braced rather than tubular**, so it holds a hard silhouette edge-on
 *    as well as face-on. A tube thins to nothing against bright sky at forty metres.
 *  - **The nose bands are geometry, not a texture.** Alternating 20 cm blocks of graphite
 *    and safety yellow at each end give a high-frequency pattern that survives distance,
 *    and being separate boxes they keep their hard edges through mip levels the way a
 *    painted stripe would not.
 *  - **White-painted end blocks and a white hook shell.** The bright half of the value
 *    pair, so the fixture is never dark-on-dark against a white parapet.
 *
 * Everything is a beam, a stay or a block: skeletal by construction, hanging in open air
 * well above any deck, so a fixture this size still cannot be mistaken for cover.
 */
function buildAnchorGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const push = (geo: THREE.BufferGeometry, role: number, xf: THREE.Matrix4): void => {
    const g = geo.index ? geo.toNonIndexed() : geo.clone();
    g.applyMatrix4(xf);
    const count = g.getAttribute('position').count;
    const uv = new Float32Array(count * 2);
    for (let i = 0; i < count; i++) uv[i * 2 + 1] = role;
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    g.deleteAttribute('tangent');
    g.clearGroups();
    parts.push(g);
  };

  const id = new THREE.Matrix4();
  const xf = (
    x: number,
    y: number,
    z: number,
    sx: number,
    sy: number,
    sz: number,
    rx = 0,
    ry = 0,
    rz = 0,
  ): THREE.Matrix4 => P.localXf(id, x, y, z, sx, sy, sz, rx, ry, rz);

  const span = ANCHOR_JIB_REACH;
  const beamY = ANCHOR_HOOK_DROP;
  const postH = Math.max(1.4, ANCHOR_MAST_HEIGHT - ANCHOR_HOOK_DROP - 0.4);
  const half = span * 0.5;

  // The beam: top and bottom flanges with a web between them, and a second beam crossed
  // through it so the fixture presents the same outline from any horizontal direction.
  for (const rot of [0, Math.PI * 0.5]) {
    push(P.UNIT_BOX, 0, xf(0, beamY + 0.15, 0, span, 0.11, 0.34, 0, rot, 0));
    push(P.UNIT_BOX, 0, xf(0, beamY - 0.15, 0, span, 0.11, 0.34, 0, rot, 0));
    push(P.UNIT_BOX, 0, xf(0, beamY, 0, span, 0.22, 0.1, 0, rot, 0));
  }

  // The kingpost and its stays: the triangle that makes this readable at a glance.
  push(P.UNIT_CYL, 0, xf(0, beamY + postH * 0.5, 0, 0.14, postH, 0.14));
  push(P.UNIT_BOX, 1, xf(0, beamY + postH + 0.12, 0, 0.46, 0.24, 0.46));
  const peak = new THREE.Vector3(0, beamY + postH, 0);
  for (const [dx, dz] of [
    [half - 0.5, 0],
    [-(half - 0.5), 0],
    [0, half - 0.5],
    [0, -(half - 0.5)],
  ] as [number, number][]) {
    const end = new THREE.Vector3(dx, beamY + 0.15, dz);
    const dir = end.clone().sub(peak);
    const len = dir.length();
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
    const m = new THREE.Matrix4().compose(
      peak.clone().addScaledVector(dir, len * 0.5),
      q,
      new THREE.Vector3(0.07, len, 0.07),
    );
    push(P.UNIT_CYL_LOW, 0, m);
  }

  // Beam noses: a white-painted end block, then four alternating safety blocks running
  // inboard of it. High-frequency contrast is what survives sixty metres.
  const blocks = 4;
  const blockW = 0.2;
  for (const [ux, uz] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as [number, number][]) {
    push(P.UNIT_BOX, 1, xf(ux * (half - 0.16), beamY, uz * (half - 0.16), 0.34, 0.46, 0.34));
    for (let i = 0; i < blocks; i++) {
      const d = half - 0.36 - blockW * (i + 0.5);
      push(
        P.UNIT_BOX,
        i % 2 === 0 ? 2 : 0,
        xf(ux * d, beamY, uz * d, ux !== 0 ? blockW : 0.38, 0.38, uz !== 0 ? blockW : 0.38),
      );
    }
  }

  // The hook block hanging at the anchor point itself: a graphite sheave housing, a white
  // painted cheek plate so the pendant has a bright note, and the hook.
  push(P.UNIT_CYL, 0, xf(0, beamY - 0.42, 0, 0.06, 0.84, 0.06));
  push(P.UNIT_BOX, 0, xf(0, 0.34, 0, 0.5, 0.62, 0.36));
  push(P.UNIT_BOX, 1, xf(0, 0.34, 0, 0.54, 0.34, 0.4));
  push(P.UNIT_TORUS, 0, xf(0, -0.1, 0, 0.86, 0.86, 0.86, Math.PI * 0.5, 0, 0));
  push(P.UNIT_SPHERE, 1, xf(0, 0.02, 0, 0.3, 0.3, 0.3));

  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) throw new Error('arenaMesh: failed to merge grapple anchor geometry');
  merged.computeBoundingSphere();
  return merged;
}

/**
 * The halo quad: a flat square in the XY plane, billboarded and shaped into a ring by the
 * fragment shader. One quad, six vertices, per anchor.
 */
function buildAnchorHaloGeometry(): THREE.BufferGeometry {
  const r = ANCHOR_HALO_RADIUS;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([-r, -r, 0, r, -r, 0, r, r, 0, -r, -r, 0, r, r, 0, -r, r, 0], 3),
  );
  geo.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1], 3),
  );
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(12), 2));
  return geo;
}

/** Stamp a per-anchor pulse phase into every vertex's uv.x. */
function setUvX(geo: THREE.BufferGeometry, phase: number): void {
  const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
  for (let v = 0; v < uv.count; v++) uv.setX(v, phase);
  uv.needsUpdate = true;
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

function centroid(points: Vec3[], fallback: Vec3): THREE.Vector3 {
  if (!points || points.length === 0) return vec(fallback);
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
    z += p.z;
  }
  return new THREE.Vector3(x / points.length, y / points.length, z / points.length);
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Build the renderable arena from the shared collision data.
 *
 * The returned group carries `userData.dispose()` for teardown and
 * `userData.regions` (name/position/colour) so the HUD can render callout names
 * from the same source of truth the level art uses.
 */
export function buildArenaMesh(arena: ArenaData): THREE.Group {
  return new ArenaBuilder(arena).build();
}
